import { useState, useEffect, useCallback, useRef, useMemo } from "react";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const CV_BASE = "https://comicvine.gamespot.com/api";
const API_BASE = window.location.hostname === 'localhost' 
  ? 'http://localhost:3002' 
  : ''; // Empty string = same origin for production

function storageGet(key, fallback) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return fallback || "";
    var value = window.localStorage.getItem(key);
    return value == null ? (fallback || "") : value;
  } catch (e) {
    return fallback || "";
  }
}

function storageSet(key, value) {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {}
}

function storageRemove(key) {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch (e) {}
}

// ─── DEMO MODE ────────────────────────────────────────────────────────────────
// Sample data for visitors who don't have API credentials
const DEMO_MODE = false; // Using curated dataset
const DEMO_ISSUES = [
  {
    id: 1,
    name: "The Legend of the Batman - Who He is, and How he Came to Be",
    issue_number: "1",
    cover_date: "1940-04-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/0/4/4-1485-5-1-batman.jpg" },
    volume: { name: "Batman" },
    _arc: "Year One",
    _characters: ["Batman (Bruce Wayne)", "Commissioner Gordon"],
    description: "The first appearance of Batman in his own title."
  },
  {
    id: 2,
    name: "The Joker",
    issue_number: "1",
    cover_date: "1940-04-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/0/4/4-1486-4-1-batman.jpg" },
    volume: { name: "Batman" },
    _arc: null,
    _characters: ["Batman (Bruce Wayne)", "The Joker", "Robin"],
    description: "The first appearance of The Joker."
  },
  {
    id: 3,
    name: "The Cat",
    issue_number: "1",
    cover_date: "1940-04-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/0/4/4-1487-3-1-batman.jpg" },
    volume: { name: "Batman" },
    _arc: null,
    _characters: ["Batman (Bruce Wayne)", "Catwoman"],
    description: "The first appearance of Catwoman (as The Cat)."
  },
  {
    id: 4,
    name: "The Long Halloween - Chapter One: Crime",
    issue_number: "1",
    cover_date: "1996-12-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/6/67663/4055329-01.jpg" },
    volume: { name: "Batman: The Long Halloween" },
    _arc: "The Long Halloween",
    _characters: ["Batman (Bruce Wayne)", "Commissioner Gordon", "Harvey Dent", "Carmine Falcone"],
    description: "A mysterious killer strikes on holidays."
  },
  {
    id: 5,
    name: "The Long Halloween - Chapter Two: Thanksgiving",
    issue_number: "2",
    cover_date: "1997-01-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/6/67663/4055330-02.jpg" },
    volume: { name: "Batman: The Long Halloween" },
    _arc: "The Long Halloween",
    _characters: ["Batman (Bruce Wayne)", "Commissioner Gordon", "Harvey Dent", "Catwoman"],
    description: "The Holiday Killer claims another victim."
  },
  {
    id: 6,
    name: "Hush - Chapter One: The Ransom",
    issue_number: "608",
    cover_date: "2002-12-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/0/4/22-1494-21-1-batman.jpg" },
    volume: { name: "Batman" },
    _arc: "Hush",
    _characters: ["Batman (Bruce Wayne)", "Catwoman", "Poison Ivy", "Harley Quinn", "The Joker"],
    description: "A new villain named Hush emerges."
  },
  {
    id: 7,
    name: "The Court of Owls - Part 1: Knife Trick",
    issue_number: "1",
    cover_date: "2011-11-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/6/67663/2074489-01.jpg" },
    volume: { name: "Batman" },
    _arc: "Court of Owls",
    _characters: ["Batman (Bruce Wayne)", "Dick Grayson", "Damian Wayne", "Alfred Pennyworth"],
    description: "Batman discovers a secret society controlling Gotham."
  },
  {
    id: 8,
    name: "The Court of Owls - Part 2: Trust Fall",
    issue_number: "2",
    cover_date: "2011-12-01",
    image: { small_url: "https://comicvine.gamespot.com/a/uploads/scale_small/6/67663/2108545-02.jpg" },
    volume: { name: "Batman" },
    _arc: "Court of Owls",
    _characters: ["Batman (Bruce Wayne)", "Nightwing", "Alfred Pennyworth"],
    description: "Batman is trapped in the Court's labyrinth."
  }
];

// Curated list of major Batman-universe characters with Comic Vine IDs.
// Format: { name, id } where id is the numeric part of their 4005-XXXX ID.
const POPULAR_FILTER_CHARS = [
  "Batman",
  "The Joker",
  "Catwoman",
  "Robin",
  "Nightwing",
  "Commissioner Gordon",
  "Alfred Pennyworth",
  "Two-Face",
  "The Riddler",
  "Bane",
  "Ra's Al Ghul",
  "Harley Quinn",
  "Poison Ivy",
  "Scarecrow",
  "Mr. Freeze",
  "The Penguin",
  "Batgirl",
  "Red Hood",
  "Oracle",
  "Damian Wayne",
  "Dick Grayson",
  "Tim Drake",
  "Jason Todd",
  "Barbara Gordon",
  "Talia Al Ghul",
  "Clayface",
  "Killer Croc",
  "Deathstroke",
];

const BATMAN_CHARACTERS = [
  { name: "Batman (Bruce Wayne)", image: "/characters/grid/batman-bruce-wayne.webp", heroImage: "/characters/batman-bruce-wayne.jpg", imagePosition: "center 18%", id: "1699", deck: "The Dark Knight of Gotham. Billionaire by day, vigilante by night. No superpowers, just iron will and the best toys money can buy." },
  { name: "Nightwing (Dick Grayson)", image: "/characters/grid/nightwing-dick-grayson.webp", heroImage: "/characters/nightwing-dick-grayson.jpg", imagePosition: "center 18%", id: "1369", deck: "The first Robin, all grown up. Acrobat, detective, leader. The best of Batman without the darkness." },
  { name: "Red Hood (Jason Todd)", image: "/characters/grid/red-hood-jason-todd.webp", heroImage: "/characters/red-hood-jason-todd.jpg", id: "11953", deck: "The Robin who died and came back angry. Now walks the line Batman won't — guns, killing, no remorse." },
  { name: "Robin (Tim Drake)", image: "/characters/grid/robin-tim-drake.webp", heroImage: "/characters/robin-tim-drake.jpg", id: "1443", deck: "The third Robin, the detective. Figured out Batman's identity on his own. Always thinking three moves ahead." },
  { name: "Robin (Damian Wayne)", image: "/characters/grid/robin-damian-wayne.webp", heroImage: "/characters/robin-damian-wayne.jpg", imagePosition: "center 18%", id: "64130", deck: "Bruce Wayne's son with Talia al Ghul. Raised by assassins. Arrogant, deadly, desperate to prove himself." },
  { name: "Batgirl (Barbara Gordon)", image: "/characters/grid/batgirl-barbara-gordon.webp", heroImage: "/characters/batgirl-barbara-gordon.jpg", id: "1418", deck: "The first and greatest Batgirl. The Joker took her legs but not her spirit — she became Oracle, then Batgirl again." },
  { name: "Alfred Pennyworth", image: "/characters/grid/alfred-pennyworth.webp", heroImage: "/characters/alfred-pennyworth.jpg", id: "1477", deck: "The man who raised Batman. Ex-SAS, current butler, permanent father figure. The only one who can tell Bruce when he's being an idiot." },
  { name: "Commissioner Gordon (James Gordon)", image: "/characters/grid/commissioner-gordon-james-gordon.webp", heroImage: "/characters/commissioner-gordon-james-gordon.jpg", id: "1444", deck: "The one good cop in a corrupt city. Keeps the GCPD running and the Bat-Signal lit. Never lost his moral compass." },
  { name: "The Joker", image: "/characters/grid/the-joker.webp", heroImage: "/characters/the-joker.jpg", id: "1807", deck: "The Clown Prince of Crime. Gotham's chaos agent. No one knows his real story, and that's the point." },
  { name: "Catwoman (Selina Kyle)", image: "/characters/grid/catwoman-selina-kyle.webp", heroImage: "/characters/catwoman-selina-kyle.jpg", id: "1599", deck: "The world's greatest thief with a heart of... well, sometimes gold. Walks the line between hero and villain." },
  { name: "Two-Face (Harvey Dent)", image: "/characters/grid/two-face-harvey-dent.webp", heroImage: "/characters/two-face-harvey-dent.jpg", id: "1659", deck: "Harvey Dent was Gotham's white knight DA until acid and a bad day turned him into something else. Now the coin decides." },
  { name: "The Riddler (Edward Nygma)", image: "/characters/grid/the-riddler-edward-nygma.webp", heroImage: "/characters/the-riddler-edward-nygma.jpg", imagePosition: "center top", id: "1441", deck: "Edward Nygma can't help himself — he has to leave clues. Obsessed with proving he's smarter than everyone else." },
  { name: "The Penguin (Oswald Cobblepot)", image: "/characters/grid/the-penguin-oswald-cobblepot.webp", heroImage: "/characters/the-penguin-oswald-cobblepot.jpg", id: "1442", deck: "Oswald Cobblepot runs the Iceberg Lounge and half the city's underworld. Never underestimate a man with an umbrella gun." },
  { name: "Bane", image: "/characters/grid/bane.webp", heroImage: "/characters/bane.jpg", id: "11104", deck: "The man who broke the Bat. Venom-enhanced strength and absolutely no mercy. He wants to destroy everything Batman stands for." },
  { name: "Ra's al Ghul", image: "/characters/grid/ra-s-al-ghul.webp", heroImage: "/characters/hero/ra-s-al-ghul.svg", heroPosition: "center center", id: "1484", deck: "The Demon's Head. Leader of the League of Assassins. Centuries old and wants Batman to be his heir." },
  { name: "Talia al Ghul", image: "/characters/grid/talia-al-ghul.webp", heroImage: "/characters/talia-al-ghul.jpg", id: "25441", deck: "Daughter of the Demon. Trained to lead, to kill, to seduce. Caught between her love for Batman and loyalty to her father." },
  { name: "Scarecrow (Jonathan Crane)", image: "/characters/grid/scarecrow-jonathan-crane.webp", heroImage: "/characters/scarecrow-jonathan-crane.jpg", imagePosition: "center top", id: "1477", deck: "Dr. Jonathan Crane weaponizes fear itself with his trademark toxin. Your worst nightmare, bottled and sold." },
  { name: "Harley Quinn (Harleen Quinzel)", image: "/characters/grid/harley-quinn-harleen-quinzel.webp", heroImage: "/characters/harley-quinn-harleen-quinzel.jpg", id: "1573", deck: "Dr. Harleen Quinzel was a promising psychiatrist until the Joker twisted her mind. Now finally free of him." },
  { name: "Poison Ivy (Pamela Isley)", image: "/characters/grid/poison-ivy-pamela-isley.webp", heroImage: "/characters/poison-ivy-pamela-isley.jpg", id: "1446", deck: "Pamela Isley doesn't care about your laws — she cares about the green. Will absolutely kill you for littering." },
  { name: "Mr. Freeze (Victor Fries)", image: "/characters/grid/mr-freeze-victor-fries.webp", heroImage: "/characters/mr-freeze-victor-fries.jpg", id: "11681", deck: "Victor Fries just wanted to save his wife. Now trapped in a subzero suit, unable to feel human touch. Gotham's most tragic monster." },
  { name: "Deathstroke (Slade Wilson)", image: "/characters/grid/deathstroke-slade-wilson.webp", heroImage: "/characters/deathstroke-slade-wilson.jpg", imagePosition: "center 18%", id: "1559", deck: "Slade Wilson. The world's greatest assassin. Enhanced strength, tactical genius, and absolutely no conscience." },
  { name: "Azrael (Jean-Paul Valley)", image: "/characters/grid/azrael-jean-paul-valley.webp", heroImage: "/characters/hero/azrael-jean-paul-valley.svg", heroPosition: "center center", id: "5766", deck: "Jean-Paul Valley was programmed by the Order of St. Dumas to be their angel of death. Briefly became a terrifying Batman." },
  { name: "Huntress (Helena Bertinelli)", image: "/characters/grid/huntress-helena-bertinelli.webp", heroImage: "/characters/huntress-helena-bertinelli.jpg", id: "1408", deck: "Helena Bertinelli. Daughter of a slain crime boss who became a vigilante more violent than Batman approves of." },
  { name: "Black Mask (Roman Sionis)", image: "/characters/grid/black-mask-roman-sionis.webp", heroImage: "/characters/black-mask-roman-sionis.jpg", id: "10172", deck: "Roman Sionis. Crime boss who wears a black skull mask carved from his father's coffin. Brutal, sadistic, obsessed with masks." },
  { name: "Clayface (Basil Karlo)", image: "/characters/grid/clayface-basil-karlo.webp", heroImage: "/characters/clayface-basil-karlo.jpg", id: "1570", deck: "Basil Karlo was an actor who wanted to be remembered. Now a shapeshifting monster who can be anyone." },
  { name: "Man-Bat (Kirk Langstrom)", image: "/characters/grid/man-bat-kirk-langstrom.webp", heroImage: "/characters/man-bat-kirk-langstrom.webp", id: "9913", deck: "Dr. Kirk Langstrom was trying to cure deafness. Instead he turned himself into a giant bat." },
  { name: "Hugo Strange", image: "/characters/grid/hugo-strange.webp", heroImage: "/characters/hugo-strange.webp", id: "12485", deck: "One of the first to deduce Batman's identity. Obsessed with understanding what drives a man to dress as a bat." },
  { name: "Killer Croc (Waylon Jones)", image: "/characters/grid/killer-croc-waylon-jones.webp", heroImage: "/characters/killer-croc-waylon-jones.jpg", id: "11203", deck: "Waylon Jones was born with a skin condition that turned him into something reptilian. Just wants to be left alone." },
  { name: "Deadshot (Floyd Lawton)", image: "/characters/grid/deadshot-floyd-lawton.webp", heroImage: "/characters/deadshot-floyd-lawton.jpg", id: "1253", deck: "Floyd Lawton never misses. Mercenary with a death wish and wrist-mounted arsenal. Somehow the shot goes wide when it counts." },
  { name: "Lucius Fox", image: "/characters/grid/lucius-fox.webp", heroImage: "/characters/lucius-fox.jpg", imagePosition: "center top", id: "23826", deck: "The man who builds Batman's toys. CEO of Wayne Enterprises, brilliant engineer, knows exactly what those R&D budgets are for." },
  { name: "Leslie Thompkins", image: "/characters/grid/leslie-thompkins.webp", heroImage: "/characters/leslie-thompkins.webp", id: "13588", deck: "Doctor and pacifist who runs a free clinic in Crime Alley. One of Bruce Wayne's oldest friends and moral compass." },
  { name: "Harvey Bullock", image: "/characters/grid/harvey-bullock.webp", heroImage: "/characters/harvey-bullock.jpg", id: "12187", deck: "Gotham detective who looks like a slob but gets results. Loyal to Gordon, suspicious of Batman, heart of gold underneath." },
  { name: "Renee Montoya", image: "/characters/grid/renee-montoya.webp", heroImage: "/characters/renee-montoya.jpg", id: "13591", deck: "One of Gotham's finest, former detective, current Question. Honest in a dishonest city. The badge means something to her." },
  { name: "Cassandra Cain", image: "/characters/grid/cassandra-cain.webp", heroImage: "/characters/cassandra-cain.jpg", imagePosition: "center 18%", id: "20498", deck: "The second Batgirl, raised by assassins to be the perfect killer. Never taught to speak, only to read body language." },
  { name: "Stephanie Brown", image: "/characters/grid/stephanie-brown.webp", heroImage: "/characters/stephanie-brown.jpg", id: "19656", deck: "The Spoiler, the fourth Robin, the third Batgirl. Keeps picking herself up no matter how many times the world knocks her down." },
  { name: "Duke Thomas", image: "/characters/grid/duke-thomas.webp", heroImage: "/characters/duke-thomas.jpg", id: "372716", deck: "The Signal. Metahuman with the ability to manipulate light. One of the newest members of the Bat-family." },
  { name: "Luke Fox (Batwing)", image: "/characters/grid/luke-fox-batwing.webp", heroImage: "/characters/luke-fox-batwing.webp", id: "311816", deck: "Lucius Fox's son who became Batwing. High-tech armor, military training, and his father's genius." },
  { name: "Batman Beyond (Terry McGinnis)", image: "/characters/grid/batman-beyond-terry-mcginnis.webp", heroImage: "/characters/batman-beyond-terry-mcginnis.webp", id: "64204", deck: "Terry McGinnis. The Batman of the future, mentored by an aging Bruce Wayne. Younger, snarkier, just as dedicated." },
];

// Comprehensive hardcoded list of Batman DC Comics volumes.
// Using a static list means zero wait time — issues start loading instantly.
// Format: [id, approximate_issue_count]
const BATMAN_VOLUME_LIST = [
  // All IDs verified via comicvine.gamespot.com URLs
  // ── Core Batman titles ───────────────────────────────────────────────────
  ["796", 713], // Batman (1940)                              /batman/4050-796/
  ["1655", 52], // Batman (2011) New 52                       /batman/4050-1655/
  ["91273", 157], // Batman (2016) Rebirth - King/Tynion/Zdarsky /batman/4050-91273/
  ["3491", 881], // Detective Comics (1937)                    /detective-comics/4050-3491/
  ["42721", 52], // Detective Comics (2011) New 52             /detective-comics/4050-42721/
  ["98510", 65], // Batman: Detective Comics (2016)            /batman-detective-comics/4050-98510/
  // ── Batman family ongoing titles ────────────────────────────────────────
  ["4720", 214], // Batman: Legends of the Dark Knight (1989)  /4050-4720/
  ["4724", 166], // Batman: Shadow of the Bat (1992)           /4050-4724/
  ["4716", 36], // The Batman Adventures (1992)               /4050-4716/
  ["5492", 37], // The Batman and Robin Adventures (1995)     /4050-5492/
  ["6598", 24], // The Batman Chronicles (1995)               /4050-6598/
  ["7207", 74], // Batman: Gotham Knights (2000)              /4050-7207/
  ["26584", 26], // Batman and Robin (2009) - Grant Morrison   /4050-26584/
  ["42719", 29], // Batman: The Dark Knight (2011)             /4050-42719/
  ["42692", 40], // Batman and Robin (2011) - Tomasi           /4050-42692/
  ["36705", 16], // Batman Incorporated (2010/2011)            /4050-36705/
  ["49112", 13], // Batman, Incorporated (2012)                /4050-49112/
  ["7608", 18], // All-Star Batman (2016)                     /4050-7608/
  ["95201", 50], // Batman Beyond (2016)                       /4050-95201/
  ["3109", 30], // Batman Annual (1961)                       /4050-3109/
  ["72915", 52], // Batman Eternal (2014)                      /4050-72915/
  ["85080", 26], // Batman & Robin Eternal (2015)              /4050-85080/
  ["55011", 24], // Batman '66 (2013)                          /4050-55011/
  ["44835", 25], // Batwing (2011)                             /4050-44835/
  ["43551", 40], // Batwoman (2011)                            /4050-43551/
  ["43548", 52], // Batgirl (2011)                             /4050-43548/
  ["43553", 30], // Nightwing (2011)                           /4050-43553/
  ["43549", 40], // Red Hood and the Outlaws (2011)            /4050-43549/
  // ── Verified miniseries & specials ──────────────────────────────────────
  ["6822", 13], // Batman: The Long Halloween (1996)          /4050-6822/
  ["6312", 14], // Batman: Dark Victory (1999)                /4050-6312/
  ["9223", 4], // Batman: Sword of Azrael (1992)             /4050-9223/
  ["6599", 6], // Batman/Huntress: Cry for Blood (2000)      /4050-6599/
  ["22099", 1], // Batman: The Man Who Laughs (2005)          /4050-22099/
  ["40210", 5], // Batman: Gates of Gotham (2011)             /4050-40210/
  ["104775", 8], // Batman: White Knight (2017)                /4050-104775/
  ["113690", 3], // Batman: Damned (2018)                      /4050-113690/
  ["115869", 7], // The Batman Who Laughs (2018)               /4050-115869/
  ["106109", 1], // Dark Nights: The Batman Who Laughs (2017)  /4050-106109/
  // ── Battle for the Cowl (2009) ──────────────────────────────────────────
  ["25959", 3], // Batman: Battle for the Cowl               /4050-25959/
  ["26256", 1], // Batman: Battle for the Cowl: Arkham Asylum /4050-26256/
  ["26294", 1], // Batman: Battle for the Cowl: The Underground /4050-26294/
  ["26323", 1], // Batman: Battle for the Cowl: The Network   /4050-26323/
  ["29454", 1], // Batman: Battle for the Cowl Companion      /4050-29454/
  // ── Additional verified volumes ─────────────────────────────────────────
  ["43964", 1], // Batman: The Black Mirror (2012 TPB)        /4050-43964/
  // ── Arc collected volumes (verified via Comic Vine URLs) ────────────────
  ["18960", 1], // Batman: Year One (TPB 1988)                /4050-18960/
  ["20592", 1], // Batman: Ego (2000)                         /4050-20592/
  ["19967", 1], // Batman: The Killing Joke (TPB 1988)        /4050-19967/
  ["3593", 4], // Batman: The Dark Knight Returns (1986)      /4050-3593/
  ["35076", 2], // Batman: Hush (TPB 2003)                    /4050-35076/
  ["50979", 3], // Batman: Knightfall (TPB remaster)          /4050-50979/
  ["33523", 3], // Batman: Knightfall trades (1998)           /4050-33523/
  ["19706", 5], // Batman: No Man's Land (TPB 1999)           /4050-19706/
  ["37468", 1], // Batman: Cataclysm (TPB 1999)               /4050-37468/
  ["135698", 1], // Batman: Contagion (TPB 2016)               /4050-135698/
  ["100686", 1], // Batman: Legacy (TPB 2017)                  /4050-100686/
  ["45155", 1], // Batman: R.I.P. (TPB 2010)                  /4050-45155/
  ["42875", 1], // Batman: Under the Red Hood (TPB 2011)      /4050-42875/
  ["50152", 3], // Batman: Earth One (2012)                   /4050-50152/
  ["25187", 1], // Batman: A Death in the Family (TPB)        /4050-25187/
  ["48729", 1], // Batman (2012 New 52 Vol 1 Court of Owls)   /4050-48729/
  // ── Arkham City series (all verified) ───────────────────────────────────
  ["40090", 5], // Batman: Arkham City prequel miniseries     /4050-40090/
  ["43514", 1], // Batman: Arkham City (TPB)                  /4050-43514/
  ["40238", 40], // Batman: Arkham City Digital Chapters       /4050-40238/
  ["44204", 1], // Batman: Arkham City Special Issue          /4050-44204/
  ["49145", 4], // Batman: Arkham City End Game               /4050-49145/
  ["80292", 12], // Batman: Arkham Knight (2015)               /4050-80292/
  // ── Official movie adaptations ──────────────────────────────────────────
  ["26469", 1], // Batman: Official Comic Adaptation (1989)   /4050-26469/
  ["26467", 1], // Batman Returns official adaptation (1992)  /4050-26467/
  ["20393", 1], // Batman Forever official adaptation (1995)  /4050-20393/
  ["36266", 1], // Batman & Robin official adaptation (1997)  /4050-36266/
  ["44587", 1], // Batman Begins official adaptation (2005)   /4050-44587/
  ["122957", 1], // Batman: 1989 Movie Adaptation Deluxe      /4050-122957/
  ["155689", 1], // The Riddler: Year One (The Batman tie-in) /4050-155689/
  // ── Batman-domain spinoffs (Batman universe, may not feature Batman) ────
  ["9958", 40], // Gotham Central (2003) - GCPD procedural    /4050-9958/
  ["77219", 18], // Gotham Academy (2014)                      /4050-77219/
  ["26891", 26], // Gotham City Sirens (2009)                  /4050-26891/
  ["52400", 18], // Talon (2012) - Court of Owls spinoff       /4050-52400/
  ["82863", 12], // We Are Robin (2015)                        /4050-82863/
  ["75536", 20], // Grayson (2014) - Dick Grayson as spy       /4050-75536/
  ["26651", 26], // Red Robin (2009) - Tim Drake               /4050-26651/
  ["4975", 183], // Robin (1993) - Tim Drake ongoing           /4050-4975/
  ["4955", 150], // Catwoman (1993) - Selina Kyle ongoing      /4050-4955/
  ["6498", 127], // Birds of Prey (1999) - Oracle/Black Canary /4050-6498/
  ["5490", 63], // Azrael (1995) - Jean-Paul Valley           /4050-5490/
  ["19721", 100], // Azrael: Agent of the Bat (1998)            /4050-19721/
  ["26754", 21], // Batman: Streets of Gotham (2009)           /4050-26754/
  ["26000", 3], // Azrael: Death's Dark Knight (2009)         /4050-26000/
  // ── Jeph Loeb / Tim Sale Batman series ──────────────────────────────────
  ["31743", 1], // Batman: Haunted Knight (TPB)               /4050-31743/
  // Note: Dark Victory (6312) and Long Halloween (6822) already in list
  // ── Batman: One Bad Day (2022-2023) — each villain is its own volume ────
  ["144574", 1], // Batman - One Bad Day: The Riddler          /4050-144574/
  ["145157", 1], // Batman - One Bad Day: Two-Face             /4050-145157/
  ["145624", 1], // Batman - One Bad Day: Penguin              /4050-145624/
  ["147492", 1], // Batman - One Bad Day: Bane                 /4050-147492/
  ["147597", 1], // Batman - One Bad Day: Catwoman             /4050-147597/
  ["148478", 1], // Batman - One Bad Day: Clayface             /4050-148478/
  ["148930", 1], // Batman - One Bad Day: Ra's al Ghul         /4050-148930/
  // ── Recent ongoing series (2022–2025) ───────────────────────────────────
  ["153462", 30], // Batman and Robin (2023) - Williamson, Bruce & Damian /4050-153462/
  ["160394", 6], // Batman and Robin: Year One (2024) - Waid/Samnee      /4050-160394/
  ["145473", 13], // Batman Incorporated (2022) - Ed Brisson               /4050-145473/
  ["153024", 12], // The Penguin (2023) - Tom King                         /4050-153024/
  ["92569", 130], // Nightwing (2016) Rebirth - Seeley/Taylor/Watters      /4050-92569/
  ["161607", 12], // Batman: Dark Patterns (2025) - Watters, Black Label   /4050-161607/
  ["160861", 20], // Batgirl (2024) - Cassandra Cain, Brombal/Miyazawa     /4050-160861/
  ["166619", 10], // Batman (2025) Vol.4 - Fraction/Jiménez                /4050-166619/
  // ── Absolute Universe (2024–) ────────────────────────────────────────────
  ["160294", 20], // Absolute Batman (2024) - Snyder/Dragotta              /4050-160294/
];

// Calls your Cloudflare Worker, which proxies Comic Vine server-side
// and adds the CORS headers the browser needs.
// Set WORKER_URL to the URL Cloudflare gives you after deploying the worker.
// WORKER_URL is stored in a ref inside the App component (see workerUrlRef)

// New proxy-based fetch - no API key needed client-side
function cvFetchProxy(endpoint, params) {
  var qs = new URLSearchParams(params).toString();
  var url = "/api/comicvine/" + endpoint + "/?" + qs;

  return fetch(url)
    .then(function (res) {
      if (!res.ok) throw new Error("Proxy error: " + res.status);
      return res.json();
    })
    .then(function (data) {
      if (data.error !== "OK")
        throw new Error(data.error || "Comic Vine API error");
      return data;
    })
    .catch(function (e) {
      throw new Error(
        e.message || "Failed to fetch. Please try again later."
      );
    });
}

// Legacy worker-based fetch (kept for compatibility)
function inferIssueCharacters(issue) {
  var text = [
    (issue.volume && issue.volume.name) || "",
    issue.name || "",
    issue.description || "",
  ]
    .join(" ")
    .replace(/<[^>]+>/g, " ")
    .toLowerCase();
  var matches = [];
  function add(name, keywords) {
    for (var i = 0; i < keywords.length; i++) {
      if (text.indexOf(keywords[i]) !== -1) {
        if (matches.indexOf(name) === -1) matches.push(name);
        return;
      }
    }
  }
  add("Batman (Bruce Wayne)", ["batman", "bruce wayne", "dark knight", "caped crusader"]);
  add("The Joker", ["joker", "jack napier", "clown prince"]);
  add("Catwoman (Selina Kyle)", ["catwoman", "selina kyle", "the cat"]);
  add("Robin (Dick Grayson)", ["dick grayson", "boy wonder", "robin"]);
  add("Nightwing (Dick Grayson)", ["nightwing"]);
  add("Commissioner Gordon (James Gordon)", ["commissioner gordon", "james gordon", "jim gordon"]);
  add("Alfred Pennyworth", ["alfred"]);
  add("Two-Face (Harvey Dent)", ["two-face", "two face", "harvey dent"]);
  add("The Riddler (Edward Nygma)", ["riddler", "edward nygma"]);
  add("The Penguin (Oswald Cobblepot)", ["penguin", "oswald cobblepot"]);
  add("Bane", ["bane"]);
  add("Ra's al Ghul", ["ra's al ghul", "ras al ghul"]);
  add("Talia al Ghul", ["talia"]);
  add("Scarecrow (Jonathan Crane)", ["scarecrow", "jonathan crane"]);
  add("Harley Quinn (Harleen Quinzel)", ["harley quinn", "harleen quinzel"]);
  add("Poison Ivy (Pamela Isley)", ["poison ivy", "pamela isley"]);
  add("Mr. Freeze (Victor Fries)", ["mr. freeze", "mr freeze", "victor fries"]);
  add("Batgirl (Barbara Gordon)", ["batgirl", "barbara gordon"]);
  add("Hugo Strange", ["hugo strange"]);
  add("Killer Croc (Waylon Jones)", ["killer croc", "waylon jones"]);
  add("Black Mask (Roman Sionis)", ["black mask", "roman sionis"]);
  add("Clayface (Basil Karlo)", ["clayface", "basil karlo"]);
  return matches;
}

function cvFetch(workerUrl, apiKey, endpoint, params) {
  if (!workerUrl) {
    return Promise.reject(
      new Error(
        "Worker URL not set. Please reconnect and enter your Cloudflare Worker URL."
      )
    );
  }
  var qs = new URLSearchParams(
    Object.assign({ api_key: apiKey, format: "json" }, params)
  ).toString();
  var url = workerUrl.replace(/\/$/, "") + "/" + endpoint + "/?" + qs;

  return fetch(url)
    .then(function (res) {
      if (!res.ok) throw new Error("Worker error: " + res.status);
      return res.json();
    })
    .then(function (data) {
      if (data.error !== "OK")
        throw new Error(data.error || "Comic Vine API error");
      return data;
    })
    .catch(function (e) {
      throw new Error(
        e.message || "Failed to fetch. Check your Worker URL and API key."
      );
    });
}

// ─── STORY ARC DETECTION ──────────────────────────────────────────────────────
var ARCS = [
  { name: "Batman: Year One", kw: ["batman: year one"] },
  { name: "The Joker: Year One", kw: ["joker: year one", "the joker: year one"] },
  { name: "The Batman (2022)", kw: ["riddler: year one", "the riddler: year one"] },
  { name: "The Long Halloween", kw: ["long halloween"] },
  { name: "Dark Victory", kw: ["dark victory"] },
  { name: "Batman: Ego", kw: ["batman: ego"] },
  { name: "Hush", kw: ["hush"] },
  { name: "Knightfall", kw: ["knightfall", "knightquest", "knightsend"] },
  { name: "No Man's Land", kw: ["no man's land", "no mans land"] },
  { name: "The Dark Knight Returns", kw: ["dark knight returns", "hunt the dark knight", "the dark knight falls", "the dark knight triumphant"] },
  { name: "Court of Owls", kw: ["court of owls", "night of owls", "city of owls"] },
  { name: "Death of the Family", kw: ["death of the family"] },
  { name: "Zero Year", kw: ["zero year"] },
  { name: "Endgame", kw: ["endgame"] },
  { name: "The Killing Joke", kw: ["killing joke"] },
  { name: "Movie Adaptations", kw: ["official comic adaptation", "official movie adaptation", "movie adaptation", "warner bros. motion picture"] },
  { name: "Contagion", kw: ["contagion"] },
  { name: "Legacy", kw: ["batman: legacy", "legacy:"] },
  { name: "Cataclysm", kw: ["cataclysm"] },
  { name: "Batman R.I.P.", kw: ["batman r.i.p", "r.i.p. the batman"] },
  { name: "The Black Mirror", kw: ["black mirror"] },
  { name: "Batman: Eternal", kw: ["batman eternal"] },
  { name: "I Am Gotham", kw: ["i am gotham"] },
  { name: "I Am Suicide", kw: ["i am suicide"] },
  { name: "War of Jokes and Riddles", kw: ["war of jokes", "jokes and riddles", "jokes & riddles"] },
  { name: "City of Bane", kw: ["city of bane"] },
  { name: "The Batman Who Laughs", kw: ["batman who laughs"] },
  { name: "Fear State", kw: ["fear state"] },
  { name: "A Lonely Place of Dying", kw: ["lonely place of dying"] },
  { name: "The Button", kw: ["the button"] },
  { name: "Night of the Monster Men", kw: ["monster men"] },
  { name: "Robin War", kw: ["robin war"] },
  { name: "Batman Incorporated", kw: ["batman incorporated", "batman inc"] },
  { name: "Flashpoint", kw: ["flashpoint"] },
  { name: "Year One", kw: ["year one"] },
];

var ARC_DESCRIPTIONS = {
  "A Lonely Place of Dying": "In the aftermath of Jason Todd's death, Tim Drake deduces Batman and Nightwing's identities and argues that Batman needs Robin to keep from losing himself.",
  "Batman: Ego": "A psychological one-shot that turns Batman's mission inward, staging Bruce Wayne against the monstrous voice of Batman itself.",
  "Batman: Eternal": "A citywide weekly mystery that pushes Gotham, Batman, and the extended Bat-family through corruption, conspiracy, and escalating street-level chaos.",
  "Batman Incorporated": "Bruce Wayne globalizes the Batman mission, recruiting international allies while old enemies strike at the idea of Batman as a worldwide symbol.",
  "Batman R.I.P.": "Grant Morrison's psychological trap for Bruce Wayne breaks down Batman's identity and tests whether the symbol can survive when Bruce is stripped away.",
  "Batman: Year One": "Frank Miller and David Mazzucchelli's origin classic follows Bruce Wayne and Jim Gordon as they separately learn how corrupt Gotham really is.",
  "Cataclysm": "A massive earthquake tears Gotham apart and sets the stage for the city's descent into No Man's Land.",
  "City of Bane": "Bane takes Gotham by force, turning Batman's city and allies against him while Bruce Wayne has to rebuild himself for the final return.",
  "Contagion": "A deadly plague spreads through Gotham, forcing Batman and his allies into a race against time as the city starts to panic.",
  "Court of Owls": "Batman discovers that Gotham's oldest families have hidden a secret society in the walls of the city he thought he knew completely.",
  "Dark Victory": "The Long Halloween's sequel follows another holiday-themed mystery while Gotham transitions further from mob rule to costumed crime and the rise of Robin.",
  "Death of the Family": "The Joker returns with a brutal attack on Batman's allies, trying to prove that the Bat-family makes Batman weak.",
  "Endgame": "Joker escalates his war with Batman into an apocalyptic battle for Gotham, turning the city itself into the final stage.",
  "Fear State": "Scarecrow and the Magistrate push Gotham into paranoia and authoritarian control, forcing Batman and his allies to fight fear as a system.",
  "Flashpoint": "An altered timeline remakes the DC Universe, with Thomas Wayne as Batman and Gotham changed by a world where Bruce died instead.",
  "Hush": "A mystery manipulator targets Bruce Wayne from every angle, pulling Batman through allies, enemies, romance, and betrayal across Gotham.",
  "I Am Gotham": "Batman meets Gotham and Gotham Girl, two super-powered protectors whose arrival raises the question of what Gotham actually needs from its heroes.",
  "I Am Suicide": "Batman assembles an unstable team for a mission into Santa Prisca, testing his will, guilt, and relationship with Catwoman.",
  "Knightfall": "Bane exhausts Batman by unleashing Arkham's inmates, breaks Bruce Wayne, and forces Gotham to confront what happens when someone else wears the cowl.",
  "Legacy": "A sequel to Contagion in which Batman races to stop a weaponized plague tied to Ra's al Ghul and Gotham's earlier outbreak.",
  "Movie Adaptations": "Comic adaptations and tie-ins for Batman films, preserving screen versions of Gotham in sequential-art form.",
  "Night of the Monster Men": "A crossover in which Hugo Strange unleashes kaiju-scale monsters on Gotham during a storm, forcing the Bat-family into disaster response.",
  "No Man's Land": "After an earthquake and government abandonment, Gotham becomes a sealed-off war zone where Batman, Gordon, and the city's survivors fight to reclaim civilization.",
  "Robin War": "Gotham's youth Robin movement collides with official crackdowns and the Court of Owls, testing what the Robin symbol means beyond one person.",
  "The Batman (2022)": "Tie-in material connected to Matt Reeves' Batman universe, including Riddler-focused prelude stories that deepen the film's Gotham.",
  "The Batman Who Laughs": "A nightmare Batman infected by Joker's worldview brings multiversal horror into Gotham and turns Batman's own mind into the threat.",
  "The Black Mirror": "Dick Grayson's Batman faces a cold, vicious Gotham mystery that proves the city can still corrupt and surprise even a different Dark Knight.",
  "The Button": "Batman and the Flash investigate the Watchmen button, connecting Gotham's mysteries to larger DC timeline fractures.",
  "The Dark Knight Returns": "An older Bruce Wayne comes out of retirement in a dystopian Gotham, redefining Batman as myth, rebellion, and obsession.",
  "The Joker: Year One": "A modern look at the early formation of the Joker legend and the trauma he spreads through Batman's world.",
  "The Killing Joke": "Alan Moore and Brian Bolland's infamous Joker story frames one bad day as a test of whether Batman and Joker are separated by anything at all.",
  "The Long Halloween": "A year-long murder mystery tracks the fall of Gotham's mob era and the rise of the costumed villains who will define Batman's world.",
  "War of Jokes and Riddles": "Joker and Riddler divide Gotham's villains into a citywide gang war while Batman revisits one of his darkest early failures.",
  "Year One": "Early-career origin stories that show heroes and villains becoming the figures Gotham will eventually fear, follow, or depend on.",
  "Zero Year": "Scott Snyder and Greg Capullo's New 52 origin epic throws a young Batman into a flooded, Riddler-controlled Gotham before the legend is fully formed."
};

var ARC_ISSUE_OVERRIDES = {
  "A Lonely Place of Dying": [32093, 32095, 32096],
  "Batman: Ego": [123427],
  "Batman: Year One": [112492],
  "Cataclysm": [44894, 44980],
  "City of Bane": [713771, 715211, 716807, 718639, 719300, 721033, 722980, 725997, 728313, 729492, 731301],
  "Contagion": [42232, 108469, 42245, 42249, 108467, 42265, 42336, 108470, 75530, 42334, 108468, 42276],
  "Court of Owls": [335060, 395028],
  "Dark Victory": [106658, 106655, 46207, 46254, 106656, 106659, 106660, 106661, 106662, 106663, 106664, 106665, 106666, 106667],
  "Death of the Family": [360790, 367690, 372334, 380426, 386088, 431430],
  "Endgame": [98689, 500263],
  "Fear State": [883726, 885308, 888319, 890015, 891946, 893976],
  "Hush": [124939, 124980, 125046, 125134, 125222, 231299, 121730, 121729, 125291, 91385, 542921, 91386, 91387, 91388, 311220],
  "Knightfall": [39315, 39454],
  "Legacy": [42666],
  "Movie Adaptations": [157985, 157978, 121974, 239258, 306949, 728304],
  "Night of the Monster Men": [550321, 552124],
  "No Man's Land": [45729, 45952],
  "The Batman (2022)": [1038281],
  "The Button": [592566, 594081],
  "The Dark Knight Returns": [26264, 26276, 26282, 26290],
  "The Joker: Year One": [1044130, 1044724, 1045407],
  "The Killing Joke": [119383],
  "The Long Halloween": [48827, 48828, 48829, 48830, 48831, 48832, 48833, 48834, 48835, 48836, 48837, 48838, 48839],
  "War of Jokes and Riddles": [603083, 606567, 609275, 611967, 614968, 619564, 622870, 626247],
  "Zero Year": [410267, 416902, 421652, 428250, 433107, 436145, 442869, 447467, 452296, 450507, 454042, 457575, 460259, 468049]
};

function getArcDescription(name) {
  return ARC_DESCRIPTIONS[name] || (name ? "A Batman story arc from Gotham Vault's curated issue archive." : "");
}

function detectArc(name, arcCredits) {
  var text = (name + " " + arcCredits).toLowerCase();
  for (var i = 0; i < ARCS.length; i++) {
    var arc = ARCS[i];
    for (var j = 0; j < arc.kw.length; j++) {
      if (text.indexOf(arc.kw[j]) !== -1) return arc.name;
    }
  }
  return null;
}

function detectIssueArc(issue) {
  var issueId = Number(issue && issue.id);
  for (var arcName in ARC_ISSUE_OVERRIDES) {
    if (ARC_ISSUE_OVERRIDES[arcName].indexOf(issueId) !== -1) return arcName;
  }
  var volumeName = issue && issue.volume && issue.volume.name ? issue.volume.name : "";
  var arcCredits = ((issue && issue.story_arc_credits) || [])
    .map(function (a) { return a && a.name ? a.name : String(a || ""); })
    .join(" ");
  return detectArc(((issue && issue.name) || "") + " " + volumeName, arcCredits) || (issue && issue._arc) || null;
}

// ─── THEME ────────────────────────────────────────────────────────────────────
var C = {
  bg: "#080810",
  surface: "#0f0f1e",
  card: "#12122a",
  border: "rgba(255,255,255,0.08)",
  borderGold: "rgba(255,214,50,0.3)",
  gold: "#FFD632",
  goldFaint: "rgba(255,214,50,0.07)",
  purple: "#a78bfa",
  purpleFaint: "rgba(124,58,237,0.1)",
  text: "#e8e8f0",
  muted: "#7a7a9a",
  dim: "#3a3a5a",
};

// ─── ICONS ────────────────────────────────────────────────────────────────────
function BatLogo({ size }) {
  size = size || 28;
  return (
    <svg
      viewBox="0 0 256 256"
      role="img"
      aria-label="Gotham Vault"
      style={{ width: size, height: size, display: "block" }}
    >
      <defs>
          <radialGradient id="door" cx="50%" cy="42%" r="58%">
            <stop offset="0" stopColor="#191936"/>
            <stop offset="0.62" stopColor="#0d0d1c"/>
            <stop offset="1" stopColor="#06060c"/>
          </radialGradient>
          <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fff1a8"/>
            <stop offset="0.5" stopColor="#ffd632"/>
            <stop offset="1" stopColor="#986200"/>
          </linearGradient>
          <linearGradient id="steel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#33335a"/>
            <stop offset="1" stopColor="#111124"/>
          </linearGradient>
        </defs>
      
        <rect width="256" height="256" rx="44" fill="#080810"/>
      
        {/* Vault door body */}
        <circle cx="128" cy="128" r="105" fill="url(#door)" stroke="url(#gold)" strokeWidth="8"/>
        <circle cx="128" cy="128" r="82" fill="none" stroke="#ffd632" strokeOpacity="0.28" strokeWidth="4"/>
        <circle cx="128" cy="128" r="58" fill="#090914" stroke="url(#steel)" strokeWidth="7"/>
      
        {/* Minimal vault bolts */}
        <g fill="url(#gold)">
          <circle cx="128" cy="38" r="5"/>
          <circle cx="128" cy="218" r="5"/>
          <circle cx="38" cy="128" r="5"/>
          <circle cx="218" cy="128" r="5"/>
          <circle cx="64" cy="64" r="4" opacity="0.75"/>
          <circle cx="192" cy="64" r="4" opacity="0.75"/>
          <circle cx="64" cy="192" r="4" opacity="0.75"/>
          <circle cx="192" cy="192" r="4" opacity="0.75"/>
        </g>
      
        {/* Door handle spokes, kept subtle */}
        <g stroke="#ffd632" strokeOpacity="0.24" strokeWidth="7" strokeLinecap="round">
          <path d="M128 128V72"/>
          <path d="M128 128l48-28"/>
          <path d="M128 128l48 28"/>
          <path d="M128 128v56"/>
          <path d="M128 128l-48 28"/>
          <path d="M128 128l-48-28"/>
        </g>
      
        {/* Option D bat symbol as focal mark */}
        <path fill="url(#gold)" d="M128 83c7 10 11 21 13 32 17-11 34-16 52-18-12 15-19 31-20 48 10 2 19 7 28 13-26 2-47 12-64 32l-9-31-9 31c-17-20-38-30-64-32 9-6 18-11 28-13-1-17-8-33-20-48 18 2 35 7 52 18 2-11 6-22 13-32Z"/>
      
        <circle cx="128" cy="128" r="13" fill="#080810" stroke="#ffd632" strokeWidth="5"/>
    </svg>
  );
}

function Ico({ d, size }) {
  size = size || 16;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      <path d={d} />
    </svg>
  );
}

function SearchIcon() {
  return (
    <Ico
      size={15}
      d="M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0-16 0M21 21l-4.35-4.35"
    />
  );
}
function XIcon() {
  return <Ico size={13} d="M18 6L6 18M6 6l12 12" />;
}
function ChevDown() {
  return <Ico size={13} d="M6 9l6 6 6-6" />;
}
function BackIcon() {
  return <Ico size={18} d="M19 12H5M12 5l-7 7 7 7" />;
}
function AlertIcon() {
  return (
    <Ico
      size={15}
      d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0M12 9v4M12 17h.01"
    />
  );
}
function LayersIcon() {
  return (
    <Ico size={13} d="M12 2L2 7l10 5 10-5-10-5M2 17l10 5 10-5M2 12l10 5 10-5" />
  );
}
function ExtIcon() {
  return (
    <Ico
      size={13}
      d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"
    />
  );
}

function StarIcon({ filled }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? C.gold : "none"}
      stroke={C.gold}
      strokeWidth="2"
      style={{ width: 15, height: 15 }}
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

// ─── AUTH FORM ────────────────────────────────────────────────────────────────
function AuthForm({ mode, onSuccess, onSwitchMode }) {
  var sEmail = useState('');
  var email = sEmail[0];
  var setEmail = sEmail[1];
  var sPassword = useState('');
  var password = sPassword[0];
  var setPassword = sPassword[1];
  var sUsername = useState('');
  var username = sUsername[0];
  var setUsername = sUsername[1];
  var sError = useState('');
  var error = sError[0];
  var setError = sError[1];
  var sLoading = useState(false);
  var loading = sLoading[0];
  var setLoading = sLoading[1];
  var emailRef = useRef(null);
  var passwordRef = useRef(null);
  var usernameRef = useRef(null);

  function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Some browsers/password managers visually autofill controlled React inputs
    // without firing onChange. Read the actual DOM values on submit so autofill works.
    var submittedEmail = ((emailRef.current && emailRef.current.value) || email || '').trim();
    var submittedPassword = (passwordRef.current && passwordRef.current.value) || password || '';
    var submittedUsername = ((usernameRef.current && usernameRef.current.value) || username || '').trim();
    setEmail(submittedEmail);
    setPassword(submittedPassword);
    setUsername(submittedUsername);

    if (!submittedEmail || !submittedPassword) {
      setError(mode === 'login' ? 'Email/username and password required' : 'Email and password required');
      return;
    }

    if (mode !== 'login' && !submittedUsername) {
      setError('Username required');
      return;
    }

    setLoading(true);
    
    var endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
    var payload = mode === 'login'
      ? { identifier: submittedEmail, password: submittedPassword }
      : { email: submittedEmail, password: submittedPassword, name: submittedUsername };
    fetch(API_BASE + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      setLoading(false);
      if (data.error) {
        setError(data.error);
      } else if (data.token) {
        storageSet('gv_auth_token', data.token);
        onSuccess(data.token, data.user);
      }
    })
    .catch(function(err) {
      setLoading(false);
      setError('Network error. Please try again.');
    });
  }

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div style={{ 
          background: 'rgba(239,68,68,.1)', 
          border: '1px solid rgba(239,68,68,.3)', 
          borderRadius: 8, 
          padding: '10px 14px', 
          fontSize: 12, 
          color: '#f87171', 
          marginBottom: 16 
        }}>
          {error}
        </div>
      )}
      
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>{mode === 'login' ? 'Email or username' : 'Email'}</label>
        <input
          ref={emailRef}
          type={mode === 'login' ? 'text' : 'email'}
          name={mode === 'login' ? 'username' : 'email'}
          autoComplete={mode === 'login' ? 'username' : 'email'}
          inputMode={mode === 'login' ? 'text' : 'email'}
          value={email}
          onInput={function(e) { setEmail(e.target.value); }}
          onChange={function(e) { setEmail(e.target.value); }}
          required
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid ' + C.border,
            borderRadius: 10,
            color: C.text,
            fontSize: 14,
            outline: 'none',
          }}
        />
      </div>

      {mode !== 'login' && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>Username</label>
          <input
            ref={usernameRef}
            type="text"
            name="username"
            autoComplete="username"
            value={username}
            onInput={function(e) { setUsername(e.target.value); }}
            onChange={function(e) { setUsername(e.target.value); }}
            required
            minLength={2}
            maxLength={40}
            style={{
              width: '100%',
              padding: '12px 14px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid ' + C.border,
              borderRadius: 10,
              color: C.text,
              fontSize: 14,
              outline: 'none',
            }}
          />
        </div>
      )}
      
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 6 }}>Password</label>
        <input
          ref={passwordRef}
          type="password"
          name="password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          value={password}
          onInput={function(e) { setPassword(e.target.value); }}
          onChange={function(e) { setPassword(e.target.value); }}
          required
          minLength={8}
          style={{
            width: '100%',
            padding: '12px 14px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid ' + C.border,
            borderRadius: 10,
            color: C.text,
            fontSize: 14,
            outline: 'none',
          }}
        />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          padding: '14px',
          background: loading ? C.dim : 'linear-gradient(135deg,' + C.gold + ',#e8a000)',
          border: 'none',
          borderRadius: 10,
          color: '#000',
          fontSize: 14,
          fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer',
          marginBottom: 16,
        }}
      >
        {loading ? 'Please wait...' : (mode === 'login' ? 'Sign In' : 'Create Account')}
      </button>
      
      <div style={{ textAlign: 'center' }}>
        <button
          type="button"
          onClick={onSwitchMode}
          style={{
            background: 'none',
            border: 'none',
            color: C.gold,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {mode === 'login' ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
        </button>
      </div>
    </form>
  );
}

// ─── COVER IMAGE ──────────────────────────────────────────────────────────────
function Cover({ src, w, h, r, fallbackText, arc }) {
  w = w || 52;
  h = h || 72;
  r = r || 6;
  var st = useState(false);
  var err = st[0];
  var setErr = st[1];
  
  // Show the real Comic Vine cover when available; fall back to the styled
  // placeholder only if an issue does not have an image or the image fails.
  var showPlaceholder = false;
  
  // Color schemes for different story arcs
  var arcColors = {
    "Year One": { bg: "#1a3a1a", border: "#4ade80", text: "#4ade80" },
    "The Long Halloween": { bg: "#3a1a1a", border: "#f87171", text: "#f87171" },
    "Dark Victory": { bg: "#1a1a3a", border: "#60a5fa", text: "#60a5fa" },
    "Knightfall": { bg: "#3a3a1a", border: "#fbbf24", text: "#fbbf24" },
    "No Man's Land": { bg: "#2a1a2a", border: "#c084fc", text: "#c084fc" },
    "Hush": { bg: "#1a2a3a", border: "#38bdf8", text: "#38bdf8" },
    "Court of Owls": { bg: "#2a2a1a", border: "#a3e635", text: "#a3e635" },
    "Death of the Family": { bg: "#3a1a2a", border: "#fb7185", text: "#fb7185" },
    "Endgame": { bg: "#1a2a2a", border: "#2dd4bf", text: "#2dd4bf" },
    "Zero Year": { bg: "#2a3a1a", border: "#a3e635", text: "#a3e635" },
    "White Knight": { bg: "#3a2a1a", border: "#fbbf24", text: "#fbbf24" },
    "Morrison Run": { bg: "#1a1a2a", border: "#818cf8", text: "#818cf8" },
    "New 52": { bg: "#2a1a1a", border: "#f87171", text: "#f87171" },
    "Rebirth": { bg: "#1a2a1a", border: "#4ade80", text: "#4ade80" },
    "Infinite Frontier": { bg: "#1a1a3a", border: "#60a5fa", text: "#60a5fa" },
    "Batman Beyond": { bg: "#2a2a2a", border: "#a1a1aa", text: "#a1a1aa" },
    "Team-Ups": { bg: "#3a2a2a", border: "#fbbf24", text: "#fbbf24" },
    "Gotham City Sirens": { bg: "#2a1a3a", border: "#c084fc", text: "#c084fc" },
    "Outlaws": { bg: "#3a1a1a", border: "#f87171", text: "#f87171" },
    "Nightwing": { bg: "#1a3a3a", border: "#22d3ee", text: "#22d3ee" },
    "Batgirl": { bg: "#3a1a3a", border: "#e879f9", text: "#e879f9" },
  };
  
  var colors = arc && arcColors[arc] ? arcColors[arc] : { bg: "#1a1a2e", border: C.borderGold, text: C.gold };
  
  if (showPlaceholder || err || !src) {
    // Generate initials from fallback text
    var initials = "?";
    if (fallbackText) {
      var words = fallbackText.split(/[\s\-:]+/).filter(function(w) { return w.length > 2; });
      if (words.length >= 2) {
        initials = (words[0][0] + words[1][0]).toUpperCase();
      } else if (words.length === 1 && words[0].length >= 2) {
        initials = words[0].substring(0, 2).toUpperCase();
      } else {
        initials = fallbackText.substring(0, 2).toUpperCase();
      }
    }
    
    return (
      <div
        style={{
          width: w,
          height: h,
          borderRadius: r,
          flexShrink: 0,
          background: "linear-gradient(135deg," + colors.bg + " 0%,#0d0d1a 50%," + colors.bg + " 100%)",
          border: "1px solid " + colors.border,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.text,
          fontSize: typeof w === "number" ? w * 0.35 : 32,
          fontWeight: 700,
          fontFamily: "monospace",
          letterSpacing: "1px",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
        }}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      onError={function () {
        setErr(true);
      }}
      style={{
        width: w,
        height: h,
        borderRadius: r,
        objectFit: "cover",
        flexShrink: 0,
        border: "1px solid " + C.border,
      }}
    />
  );
}

// ─── TAG ──────────────────────────────────────────────────────────────────────
function Tag({ label, color }) {
  color = color || C.gold;
  return (
    <span
      style={{
        fontSize: 10,
        padding: "2px 8px",
        borderRadius: 20,
        whiteSpace: "nowrap",
        background: color + "12",
        border: "1px solid " + color + "30",
        color: color + "cc",
        fontFamily: "monospace",
      }}
    >
      {label}
    </span>
  );
}

// ─── DROPDOWN ─────────────────────────────────────────────────────────────────
function Dropdown({ id, label, value, options, onChange, openId, setOpenId }) {
  var open = openId === id;
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={function () {
          setOpenId(open ? null : id);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 10px",
          borderRadius: 8,
          border: "1px solid " + (value ? C.borderGold : C.border),
          background: value ? C.goldFaint : "rgba(255,255,255,0.04)",
          color: value ? C.gold : C.muted,
          fontSize: 12,
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          transition: "all .15s",
        }}
      >
        {value || label}
        {value ? (
          <span
            onClick={function (e) {
              e.stopPropagation();
              onChange("");
              setOpenId(null);
            }}
          >
            <XIcon />
          </span>
        ) : (
          <ChevDown />
        )}
      </button>
      {open && (
        <div
          onClick={function (e) {
            e.stopPropagation();
          }}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 100,
            background: "#1a1a2e",
            border: "1px solid " + C.border,
            borderRadius: 10,
            minWidth: 200,
            maxHeight: 260,
            overflowY: "auto",
            boxShadow: "0 8px 32px rgba(0,0,0,.7)",
          }}
        >
          {[""].concat(options).map(function (opt) {
            return (
              <div
                key={opt || "_all"}
                onClick={function () {
                  onChange(opt);
                  setOpenId(null);
                }}
                onMouseEnter={function (e) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background =
                    opt === value ? C.goldFaint : "transparent";
                }}
                style={{
                  padding: "9px 14px",
                  fontSize: 12,
                  fontFamily: "monospace",
                  cursor: "pointer",
                  color: opt === value ? C.gold : opt ? C.text : C.muted,
                  background: opt === value ? C.goldFaint : "transparent",
                }}
              >
                {opt || "All " + label + "s"}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner({ message }) {
  message = message || "LOADING…";
  return (
    <div style={{ textAlign: "center", padding: "32px 20px" }}>
      <style>
        {
          ".spin{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"
        }
      </style>
      <div
        className="spin"
        style={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          margin: "0 auto 12px",
          border: "2px solid " + C.dim,
          borderTopColor: C.gold,
        }}
      />
      <div style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
        {message}
      </div>
    </div>
  );
}

// ─── CHECK ICON ───────────────────────────────────────────────────────────────
function CheckIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={filled ? "#22c55e" : "#7a7a9a"} strokeWidth="2.5" style={{ width: 15, height: 15 }}>
      {filled ? (
        <>
          <circle cx="12" cy="12" r="10" fill="#22c55e" stroke="#22c55e" />
          <polyline points="8,12 11,15 16,9" stroke="#0a0a14" strokeWidth="2.5" fill="none" />
        </>
      ) : (
        <>
          <circle cx="12" cy="12" r="10" stroke="#7a7a9a" />
          <polyline points="8,12 11,15 16,9" stroke="#7a7a9a" strokeWidth="2" fill="none" />
        </>
      )}
    </svg>
  );
}

// ─── COMIC CARD ───────────────────────────────────────────────────────────────
function ComicCard({ comic, onClick, wishlisted, onWishlist, owned, onOwn, onArcClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        gap: 12,
        background: C.card,
        border: "1px solid " + (owned ? "rgba(34,197,94,0.4)" : C.border),
        borderRadius: 14,
        padding: 12,
        cursor: "pointer",
        transition: "all .15s",
      }}
      onMouseEnter={function (e) {
        e.currentTarget.style.borderColor = owned ? "rgba(34,197,94,0.6)" : C.borderGold;
        e.currentTarget.style.background = "#16162e";
      }}
      onMouseLeave={function (e) {
        e.currentTarget.style.borderColor = owned ? "rgba(34,197,94,0.4)" : C.border;
        e.currentTarget.style.background = C.card;
      }}
    >
      <Cover src={comic.image && comic.image.small_url} fallbackText={comic.name || comic.volume.name} arc={comic._arc} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: C.text,
                lineHeight: 1.25,
                marginBottom: 2,
              }}
            >
              {(comic.volume && comic.volume.name) || "Batman"}
            </div>
            <div
              style={{ fontSize: 11, color: C.gold, fontFamily: "monospace" }}
            >
              {"#" +
                comic.issue_number +
                " · " +
                ((comic.cover_date && comic.cover_date.slice(0, 4)) || "—")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              onClick={function (e) {
                e.stopPropagation();
                onOwn(comic.id);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                flexShrink: 0,
              }}
            >
              <CheckIcon filled={owned} />
            </button>
            <button
              onClick={function (e) {
                e.stopPropagation();
                onWishlist(comic.id);
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 2,
                flexShrink: 0,
              }}
            >
              <StarIcon filled={wishlisted} />
            </button>
          </div>
        </div>
        {comic.name && (
          <div
            style={{
              fontSize: 12,
              color: C.muted,
              marginTop: 4,
              fontStyle: "italic",
            }}
          >
            "{comic.name}"
          </div>
        )}
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}
        >
          {comic._arc && (
            <span
              onClick={
                onArcClick
                  ? function (e) {
                      e.stopPropagation();
                      onArcClick(comic._arc);
                    }
                  : undefined
              }
              title={onArcClick ? "View full arc →" : undefined}
              style={{
                cursor: onArcClick ? "pointer" : "default",
                transition: "opacity .15s",
              }}
              onMouseEnter={function (e) {
                if (onArcClick) e.currentTarget.style.opacity = "0.7";
              }}
              onMouseLeave={function (e) {
                e.currentTarget.style.opacity = "1";
              }}
            >
              <Tag
                label={onArcClick ? comic._arc + " →" : comic._arc}
                color={C.purple}
              />
            </span>
          )}
          {(comic._characters || []).slice(0, 2).map(function (ch) {
            return <Tag key={ch} label={ch} />;
          })}
          {(comic._characters || []).length > 2 && (
            <span style={{ fontSize: 10, color: C.dim, padding: "2px 4px" }}>
              +{comic._characters.length - 2}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ARC BUNDLE CARD ──────────────────────────────────────────────────────────
function ArcCard({ arcName, issues, onClick }) {
  var firstIssue = issues && issues[0] ? issues[0] : null;
  var lastIssue = issues && issues.length ? issues[issues.length - 1] : null;
  return (
    <div
      className="story-arc-card"
      onClick={onClick}
      style={{
        background: C.card,
        border: "1px solid " + C.border,
        borderRadius: 14,
        padding: 0,
        cursor: "pointer",
        transition: "all .15s",
        overflow: "hidden",
        position: "relative",
      }}
      onMouseEnter={function (e) {
        e.currentTarget.style.borderColor = "rgba(124,58,237,0.4)";
        e.currentTarget.style.background = "#16162e";
      }}
      onMouseLeave={function (e) {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.background = C.card;
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg,#7c3aed,transparent)",
        }}
      />
      <div style={{ padding: 12, paddingBottom: 0 }}>
        <div
          style={{ position: "relative", width: "100%", aspectRatio: "2 / 3", marginBottom: 12 }}
        >
          <Cover
            src={firstIssue && firstIssue.image && firstIssue.image.small_url}
            w="100%"
            h="100%"
            r={10}
            fallbackText={arcName}
            arc={arcName}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.text,
              marginBottom: 3,
              lineHeight: 1.25,
            }}
          >
            {arcName}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.purple,
              fontFamily: "monospace",
              marginBottom: 6,
            }}
          >
            {issues.length} ISSUE{issues.length !== 1 ? "S" : ""} · COMPLETE ARC
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>
            {firstIssue && firstIssue.cover_date && firstIssue.cover_date.slice(0, 4)}
            {" – "}
            {lastIssue && lastIssue.cover_date && lastIssue.cover_date.slice(0, 4)}
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45, marginTop: 8 }}>
            {getArcDescription(arcName).slice(0, 132)}
            {getArcDescription(arcName).length > 132 ? "…" : ""}
          </div>
          <div
            style={{
              marginTop: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: C.purpleFaint,
              border: "1px solid rgba(124,58,237,0.3)",
              borderRadius: 6,
              padding: "3px 8px",
              fontSize: 11,
              color: C.purple,
            }}
          >
            <LayersIcon /> View full arc
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── ARC LIST CARD (for the fetched CV arcs) ─────────────────────────────────
function ArcListCard({ arc, onClick }) {
  return (
    <div
      className="story-arc-card"
      onClick={onClick}
      style={{
        background: C.card,
        border: "1px solid " + C.border,
        borderRadius: 14,
        padding: 0,
        cursor: "pointer",
        transition: "all .15s",
        position: "relative",
        overflow: "hidden",
      }}
      onMouseEnter={function (e) {
        e.currentTarget.style.borderColor = "rgba(124,58,237,0.4)";
        e.currentTarget.style.background = "#16162e";
      }}
      onMouseLeave={function (e) {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.background = C.card;
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg,#7c3aed,transparent)",
        }}
      />
      <div style={{ padding: 12, paddingBottom: 0 }}>
        <div style={{ width: "100%", aspectRatio: "2 / 3" }}>
          <Cover src={arc.image && arc.image.small_url} w="100%" h="100%" r={10} fallbackText={arc.name} />
        </div>
      </div>
      <div style={{ minWidth: 0, padding: 12, paddingTop: 10 }}>
        <div
          className="site-header-inner"
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: C.text,
            lineHeight: 1.25,
            marginBottom: 3,
          }}
        >
          {arc.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: C.purple,
            fontFamily: "monospace",
            marginBottom: 5,
          }}
        >
          {arc.count_of_issues > 0 ? arc.count_of_issues : "?"} ISSUE
          {arc.count_of_issues !== 1 ? "S" : ""}
        </div>
        {arc.deck && (
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            {arc.deck.slice(0, 100)}
            {arc.deck.length > 100 ? "…" : ""}
          </div>
        )}
        <div
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: C.purpleFaint,
            border: "1px solid rgba(124,58,237,0.3)",
            borderRadius: 6,
            padding: "3px 8px",
            fontSize: 11,
            color: C.purple,
          }}
        >
          <LayersIcon /> View issues
        </div>
      </div>
    </div>
  );
}

function namesReferToSameCharacter(candidate, target) {
  var cLower = String(candidate || "").toLowerCase();
  var tLower = String(target || "").toLowerCase();
  if (!cLower || !tLower) return false;
  if (cLower === tLower) return true;
  if (cLower.startsWith(tLower + " ") || cLower.startsWith(tLower + "(")) return true;
  if ((tLower.startsWith(cLower + " ") || tLower.startsWith(cLower + "(")) && cLower.length >= tLower.length - 3) return true;
  if (cLower.startsWith(tLower + "-") || tLower.startsWith(cLower + "-")) return true;
  if (cLower.indexOf(tLower) !== -1) return true;
  if (tLower.indexOf(cLower) !== -1 && cLower.length >= 10) return true;
  return false;
}

// ─── CHARACTER CARD ───────────────────────────────────────────────────────────
function CharCard({ char, count, deck, image, imagePosition, onClick }) {
  var imageUrl = image && image.small_url ? image.small_url : image;
  return (
    <div
      className="character-card"
      onClick={onClick}
      style={{
        position: "relative",
        minHeight: 230,
        overflow: "hidden",
        background: imageUrl ? "#111122" : "linear-gradient(135deg,#1a1a38,#0d0d1a)",
        border: "1px solid " + C.border,
        borderRadius: 18,
        cursor: "pointer",
        transition: "transform .18s ease, border-color .18s ease, box-shadow .18s ease",
        boxShadow: "0 12px 30px rgba(0,0,0,.28)",
      }}
      onMouseEnter={function (e) {
        e.currentTarget.style.borderColor = C.borderGold;
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "0 18px 42px rgba(0,0,0,.42)";
      }}
      onMouseLeave={function (e) {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,.28)";
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={char}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            minHeight: 230,
            objectFit: "cover",
            objectPosition: imagePosition || "top center",
            display: "block",
            filter: "saturate(1.05) contrast(1.03)",
          }}
        />
      ) : (
        <div
          style={{
            minHeight: 230,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: C.gold,
            fontSize: 54,
            fontWeight: 900,
          }}
        >
          {char[0]}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg,rgba(5,5,12,.05) 20%,rgba(5,5,12,.55) 58%,rgba(5,5,12,.94) 100%)",
        }}
      />
      <div style={{ position: "absolute", left: 14, right: 14, bottom: 13 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, lineHeight: 1.12, textShadow: "0 2px 12px rgba(0,0,0,.75)" }}>{char}</div>
        {deck && (
          <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.35, marginTop: 5 }}>
            {deck.slice(0, 74)}{deck.length > 74 ? "…" : ""}
          </div>
        )}
        <div style={{ fontSize: 10, color: C.gold, fontFamily: "monospace", marginTop: 8, letterSpacing: .6 }}>
          {count !== null ? count + " APPEARANCE" + (count !== 1 ? "S" : "") : "VIEW APPEARANCES"} ›
        </div>
      </div>
    </div>
  );
}

// ─── BUY BUTTONS ─────────────────────────────────────────────────────────────
// Direct ASIN links for popular collected editions — these go straight to the product page
// so the affiliate tag is clean and the user lands on the exact right item.
// Matched case-insensitively against the query keyword.
var ASIN_MAP = [
  { key: "batman #529", asin: "B00IYSGDDS" }, // Batman (1940-2011) #529 Kindle & comiXology
  { key: "batman 529", asin: "B00IYSGDDS" }, // Batman (1940-2011) #529 Kindle & comiXology
  { key: "long halloween", asin: "1779512694" }, // Batman: The Long Halloween Deluxe Edition
  { key: "dark victory", asin: "1401244017" }, // Batman: Dark Victory (New Edition)
  { key: "hush", asin: "1401223176" }, // Batman: Hush
  { key: "court of owls", asin: "1401235417" }, // Batman Vol. 1: The Court of Owls
  { key: "death of the family", asin: "1401246028" }, // Batman Vol. 3: Death of the Family
  { key: "zero year dark city", asin: "1401253350" }, // Batman Vol. 5: Zero Year - Dark City
  { key: "zero year secret city", asin: "1401249337" }, // Batman Vol. 4: Zero Year - Secret City
  { key: "zero year", asin: "1401249337" }, // Batman Vol. 4: Zero Year - Secret City
  { key: "year one", asin: "1401272444" }, // Batman: Year One (New Edition)
  { key: "under the red hood", asin: "1401231454" }, // Batman: Under the Red Hood
  { key: "killing joke", asin: "1401294057" }, // Batman: The Killing Joke Deluxe (New Edition)
  { key: "haunted knight", asin: "1563892731" }, // Batman: Haunted Knight
  { key: "arkham asylum", asin: "1779513178" }, // Batman: Arkham Asylum The Deluxe Edition
  { key: "white knight", asin: "1401279597" }, // Batman: White Knight
  { key: "who laughs", asin: "1401294030" }, // The Batman Who Laughs
];

function findAsinForQuery(query) {
  var q = (query || "").toLowerCase();
  var match = ASIN_MAP.find(function (entry) {
    return q.indexOf(entry.key) !== -1;
  });
  return match ? match.asin : null;
}

function getAmazonHref(query, asin, source) {
  var params = new URLSearchParams({
    store: "amazon",
    q: query || "Batman comic",
    source: source || "buy-button",
  });
  var resolvedAsin = asin || findAsinForQuery(query);
  if (resolvedAsin) params.set("asin", resolvedAsin);
  return "/api/out/buy?" + params.toString();
}

function BuyButtons({ query, asin }) {
  var amazonHref = getAmazonHref(query, asin, "buy-buttons");
  var stores = [
    {
      label: "Buy on Amazon",
      bg: "linear-gradient(135deg,#FF9900,#e68a00)",
      color: "#000",
      border: false,
      href: amazonHref,
    },
  ];
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          color: C.dim,
          fontFamily: "monospace",
          letterSpacing: 1,
          marginBottom: 8,
        }}
      >
        BUY THIS
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {stores.map(function (s) {
          return (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                background: s.bg,
                border: s.border ? "1px solid " + C.border : "none",
                borderRadius: 10,
                padding: "12px",
                color: s.color,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {s.label} <ExtIcon />
            </a>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: C.dim, lineHeight: 1.5, marginTop: 8 }}>
        As an Amazon Associate, Gotham Vault may earn from qualifying purchases.
      </div>
    </div>
  );
}

// ─── DETAIL SHEET ─────────────────────────────────────────────────────────────
function DetailSheet({
  item,
  type,
  onClose,
  wishlist,
  onWishlist,
  collection,
  onOwn,
  onSelectComic,
  onArcClick,
}) {
  if (!item) return null;
  var issues =
    type === "arc" ? item.issues : type === "character" ? item.issues : null;
  var buyQuery =
    type === "comic"
      ? ((item.volume && item.volume.name) || "Batman") +
        " #" +
        item.issue_number
      : type === "arc"
      ? "Batman " + item.name
      : "Batman " + item.char;
  var buyAsin = type === "comic" ? item.amazon_asin || findAsinForQuery(buyQuery) : findAsinForQuery(buyQuery);

  return (
    <div
      className="detail-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.88)",
        zIndex: 200,
        display: "flex",
        alignItems: "flex-end",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="detail-sheet"
        onClick={function (e) {
          e.stopPropagation();
        }}
        style={{
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          background: "linear-gradient(180deg,#161630," + C.bg + ")",
          borderTop:
            "1px solid " +
            (type === "arc" ? "rgba(124,58,237,0.4)" : C.borderGold),
          borderRadius: "20px 20px 0 0",
          maxHeight: "88vh",
          overflowY: "auto",
          padding: "0 0 40px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "12px 0 0",
          }}
        >
          <div
            style={{
              width: 40,
              height: 4,
              background: "rgba(255,255,255,0.15)",
              borderRadius: 2,
            }}
          />
        </div>
        <button
          onClick={onClose}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            color: C.muted,
            fontSize: 13,
            cursor: "pointer",
            padding: "10px 20px",
          }}
        >
          <BackIcon /> Back
        </button>

        <div className="detail-body" style={{ padding: "0 20px" }}>
          {type === "comic" && (
            <div className="detail-comic">
              <div className="detail-comic-main" style={{ display: "flex", gap: 16, marginBottom: 16 }}>
                <Cover
                  src={
                    item.image &&
                    (item.image.medium_url || item.image.small_url)
                  }
                  w={80}
                  h={112}
                  r={8}
                  fallbackText={item.name}
                  arc={item._arc}
                />
                <div className="detail-comic-info" style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: C.text,
                      lineHeight: 1.2,
                    }}
                  >
                    {(item.volume && item.volume.name) || "Batman"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.gold,
                      fontFamily: "monospace",
                      marginTop: 3,
                    }}
                  >
                    {"#" +
                      item.issue_number +
                      " · " +
                      (item.cover_date || "Unknown")}
                  </div>
                  {item.name && (
                    <div
                      style={{
                        fontSize: 13,
                        color: C.muted,
                        marginTop: 4,
                        fontStyle: "italic",
                      }}
                    >
                      "{item.name}"
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button
                      onClick={function () {
                        onOwn(item.id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        background:
                          collection.indexOf(item.id) !== -1
                            ? "rgba(34,197,94,0.15)"
                            : "rgba(255,255,255,0.06)",
                        border:
                          "1px solid " +
                          (collection.indexOf(item.id) !== -1
                            ? "rgba(34,197,94,0.5)"
                            : C.border),
                        borderRadius: 8,
                        padding: "5px 10px",
                        color:
                          collection.indexOf(item.id) !== -1 ? "#22c55e" : C.muted,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      <CheckIcon filled={collection.indexOf(item.id) !== -1} />
                      {collection.indexOf(item.id) !== -1 ? "Owned" : "Own"}
                    </button>
                    <button
                      onClick={function () {
                        onWishlist(item.id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        background:
                          wishlist.indexOf(item.id) !== -1
                            ? "rgba(255,214,50,0.15)"
                            : "rgba(255,255,255,0.06)",
                        border:
                          "1px solid " +
                          (wishlist.indexOf(item.id) !== -1
                            ? C.borderGold
                            : C.border),
                        borderRadius: 8,
                        padding: "5px 10px",
                        color:
                          wishlist.indexOf(item.id) !== -1 ? C.gold : C.muted,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      <StarIcon filled={wishlist.indexOf(item.id) !== -1} />
                      {wishlist.indexOf(item.id) !== -1 ? "Saved" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
              {item.description && (
                <p
                  style={{
                    fontSize: 13,
                    color: C.muted,
                    lineHeight: 1.65,
                    marginBottom: 16,
                  }}
                >
                  {item.description.replace(/<[^>]+>/g, "").slice(0, 320) + "…"}
                </p>
              )}
              {item._arc && (
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 9,
                      color: C.dim,
                      fontFamily: "monospace",
                      letterSpacing: 1,
                      marginBottom: 6,
                    }}
                  >
                    STORY ARC
                  </div>
                  <span
                    onClick={
                      onArcClick
                        ? function () {
                            onArcClick(item._arc);
                          }
                        : undefined
                    }
                    title={onArcClick ? "View full arc →" : undefined}
                    style={{
                      cursor: onArcClick ? "pointer" : "default",
                      opacity: 1,
                      transition: "opacity .15s",
                    }}
                    onMouseEnter={function (e) {
                      e.currentTarget.style.opacity = "0.75";
                    }}
                    onMouseLeave={function (e) {
                      e.currentTarget.style.opacity = "1";
                    }}
                  >
                    <Tag label={item._arc + " →"} color={C.purple} />
                  </span>
                </div>
              )}
              {item._characters && item._characters.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 9,
                      color: C.dim,
                      fontFamily: "monospace",
                      letterSpacing: 1,
                      marginBottom: 6,
                    }}
                  >
                    CHARACTERS
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {item._characters.map(function (ch) {
                      return <Tag key={ch} label={ch} />;
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {(type === "arc" || type === "character") && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: C.text,
                    marginBottom: 4,
                  }}
                >
                  {type === "arc" ? item.name : item.char}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontFamily: "monospace",
                    color: type === "arc" ? C.purple : C.gold,
                  }}
                >
                  {issues.length} ISSUE{issues.length !== 1 ? "S" : ""}
                  {type === "arc"
                    ? " · COMPLETE ARC"
                    : " · WITH THIS CHARACTER"}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                {issues.map(function (iss) {
                  return (
                    <div
                      key={iss.id}
                      onClick={function () {
                        onSelectComic(iss);
                      }}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        background: "rgba(255,255,255,0.03)",
                        borderRadius: 10,
                        border: "1px solid " + C.border,
                        padding: "10px 12px",
                        cursor: "pointer",
                        transition: "all .15s",
                      }}
                      onMouseEnter={function (e) {
                        e.currentTarget.style.borderColor = C.borderGold;
                      }}
                      onMouseLeave={function (e) {
                        e.currentTarget.style.borderColor = C.border;
                      }}
                    >
                      <Cover
                        src={iss.image && iss.image.small_url}
                        w={36}
                        h={50}
                        r={4}
                        fallbackText={iss.name}
                        arc={iss._arc}
                      />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.text,
                          }}
                        >
                          {(iss.volume && iss.volume.name) || "Batman"}{" "}
                          {"#" + iss.issue_number}
                        </div>
                        <div style={{ fontSize: 11, color: C.muted }}>
                          {iss.name ? '"' + iss.name + '" · ' : ""}
                          {(iss.cover_date && iss.cover_date.slice(0, 4)) ||
                            "—"}
                        </div>
                      </div>
                      <button
                        onClick={function (e) {
                          e.stopPropagation();
                          onWishlist(iss.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <StarIcon filled={wishlist.indexOf(iss.id) !== -1} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <BuyButtons query={buyQuery} asin={buyAsin} />
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
var TABS = ["Home", "Browse", "Story Arcs", "Characters", "Guide", "Collection"];
var VALID_TABS = ["Home", "Browse", "Story Arcs", "Characters", "Guide", "Collection", "Blog", "About", "FAQ", "Account", "Admin"];

function AdminAnalyticsPage() {
  var sToken = useState(function () { return storageGet('gv_admin_token') || ''; });
  var token = sToken[0];
  var setToken = sToken[1];
  var sAnalytics = useState(null);
  var analytics = sAnalytics[0];
  var setAnalytics = sAnalytics[1];
  var sLoading = useState(false);
  var loading = sLoading[0];
  var setLoading = sLoading[1];
  var sError = useState('');
  var error = sError[0];
  var setError = sError[1];

  function loadAnalytics(nextToken) {
    var adminToken = String(nextToken || token || '').trim();
    if (!adminToken) {
      setError('Enter the admin token to view analytics.');
      return;
    }
    setLoading(true);
    setError('');
    fetch(API_BASE + '/api/admin/analytics', {
      headers: { 'x-admin-token': adminToken }
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.error || 'Unable to load analytics.');
          return data;
        });
      })
      .then(function (data) {
        storageSet('gv_admin_token', adminToken);
        setAnalytics(data);
      })
      .catch(function (err) { setError(err.message || 'Unable to load analytics.'); })
      .finally(function () { setLoading(false); });
  }

  useEffect(function () {
    if (token) loadAnalytics(token);
  }, []);

  function StatCard(props) {
    return (
      <div style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>{props.label}</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: C.gold }}>{props.value}</div>
        {props.note && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{props.note}</div>}
      </div>
    );
  }

  function CountList(props) {
    var rows = props.rows || [];
    return (
      <div style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 12 }}>{props.title}</div>
        {rows.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>No data yet.</div>
        ) : rows.map(function (row) {
          return (
            <div key={row.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderTop: '1px solid rgba(255,255,255,.06)' }}>
              <span style={{ fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
              <span style={{ fontSize: 13, color: C.gold, fontFamily: 'monospace', flexShrink: 0 }}>{Number(row.count || 0).toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    );
  }

  var content = analytics && analytics.content;
  var accounts = analytics && analytics.accounts;
  var affiliate = analytics && analytics.affiliate;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 900, color: C.text, marginBottom: 6 }}>Admin Analytics</div>
          <div style={{ fontSize: 13, color: C.muted }}>Private site metrics: content coverage, account aggregates, and affiliate clicks.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="password"
            value={token}
            onChange={function (e) { setToken(e.target.value); }}
            onKeyDown={function (e) { if (e.key === 'Enter') loadAnalytics(e.currentTarget.value); }}
            placeholder="Admin token"
            style={{ background: C.card, border: '1px solid ' + C.borderGold, borderRadius: 999, padding: '10px 14px', color: C.text, minWidth: 220 }}
          />
          <button
            onClick={function () { loadAnalytics(token); }}
            disabled={loading}
            style={{ background: C.gold, border: 'none', borderRadius: 999, padding: '10px 16px', color: '#080810', fontWeight: 900, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 12, padding: 12, color: '#f87171', marginBottom: 16 }}>{error}</div>}

      {analytics && (
        <>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: 'monospace', marginBottom: 12 }}>Generated {new Date(analytics.generatedAt).toLocaleString()}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
            <StatCard label="Issues" value={content.issues.toLocaleString()} note={(content.coverCoveragePct || 0) + '% cover coverage'} />
            <StatCard label="Volumes" value={content.volumes.toLocaleString()} />
            <StatCard label="Story Arcs" value={content.storyArcs.toLocaleString()} />
            <StatCard label="Characters" value={content.characters.toLocaleString()} />
            <StatCard label="Users" value={accounts.users.toLocaleString()} note={accounts.usersWithCollection + ' with collections'} />
            <StatCard label="Affiliate Clicks" value={affiliate.clicks.toLocaleString()} note={affiliate.clicksLast7d + ' in last 7 days'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 18 }}>
            <div style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 10 }}>Account Summary</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.9 }}>
                Collection items: <b style={{ color: C.gold }}>{accounts.collectionItems.toLocaleString()}</b><br />
                Wishlist items: <b style={{ color: C.gold }}>{accounts.wishlistItems.toLocaleString()}</b><br />
                Avg collection size: <b style={{ color: C.gold }}>{accounts.avgCollectionSize}</b><br />
                Avg wishlist size: <b style={{ color: C.gold }}>{accounts.avgWishlistSize}</b><br />
                Latest signup: <b style={{ color: C.gold }}>{accounts.latestSignupAt ? new Date(accounts.latestSignupAt).toLocaleDateString() : '—'}</b>
              </div>
            </div>
            <div style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, padding: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.text, marginBottom: 10 }}>Content Range</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.8 }}>
                Earliest: <b style={{ color: C.gold }}>{content.earliestIssue ? content.earliestIssue.coverDate + ' · ' + content.earliestIssue.name : '—'}</b><br />
                Latest: <b style={{ color: C.gold }}>{content.latestIssue ? content.latestIssue.coverDate + ' · ' + content.latestIssue.name : '—'}</b><br />
                Data updated: <b style={{ color: C.gold }}>{content.lastUpdated ? new Date(content.lastUpdated).toLocaleString() : '—'}</b><br />
                Affiliate 24h: <b style={{ color: C.gold }}>{affiliate.clicksLast24h}</b>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
            <CountList title="Top Volumes" rows={content.topVolumes} />
            <CountList title="Top Story Arcs" rows={content.topStoryArcs} />
            <CountList title="Top Characters" rows={content.topCharacters} />
            <CountList title="Issues by Decade" rows={content.byDecade} />
            <CountList title="Top Achievements" rows={accounts.topAchievements} />
            <CountList title="Affiliate Sources" rows={affiliate.topSources} />
            <CountList title="Affiliate Searches / ASINs" rows={affiliate.topQueries} />
          </div>
        </>
      )}
    </div>
  );
}

function slugifyArcName(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getArcSlugFromLocation() {
  if (typeof window === 'undefined') return "";
  var path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path.indexOf('/story-arcs/') !== 0) return "";
  return decodeURIComponent(path.slice('/story-arcs/'.length));
}

function getUrlForArc(arcName, arcId) {
  var url = "/story-arcs/" + encodeURIComponent(slugifyArcName(arcName));
  if (arcId) url += "?id=" + encodeURIComponent(arcId);
  return url;
}

function getTabFromLocation() {
  if (typeof window === 'undefined') return "Home";
  var path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin') return "Admin";
  if (path === '/story-arcs' || path.indexOf('/story-arcs/') === 0) return "Story Arcs";
  var hash = decodeURIComponent(window.location.hash.slice(1));
  if (hash.indexOf('/') !== -1) hash = hash.split('/')[0];
  return VALID_TABS.indexOf(hash) !== -1 ? hash : "Home";
}

function getUrlForTab(newTab) {
  if (newTab === "Admin") return "/admin";
  if (newTab === "Story Arcs") return "/story-arcs";
  return "/#" + encodeURIComponent(newTab);
}

export default function App() {
  var workerUrlRef = useRef(
    (storageGet("gv_worker_url")) ||
      ""
  );
  var s0 = useState(function () {
    // Demo mode: auto-connect with demo data
    if (DEMO_MODE) {
      return "DEMO_KEY";
    }
    // Curated dataset mode: auto-connect without API key
    return "CURATED_DATA";
  });
  var apiKey = s0[0];
  var setApiKey = s0[1];
  var s1 = useState("");
  var keyInput = s1[0];
  var setKeyInput = s1[1];
  var s1b = useState(function () {
    return (
      storageGet("gv_worker_url") ||
      "https://lucky-unit-93ea.maxwell-i-k4.workers.dev"
    );
  });
  var workerInput = s1b[0];
  var setWorkerInput = s1b[1];
  var s2 = useState([]);
  var issues = s2[0];
  var setIssues = s2[1];
  var s3 = useState(false);
  var loading = s3[0];
  var setLoading = s3[1];
  var s4 = useState("");
  var error = s4[0];
  var setError = s4[1];
  var s5 = useState({ volIdx: 0, offset: 0 });
  var page = s5[0];
  var setPage = s5[1];
  var s6 = useState(true);
  var hasMore = s6[0];
  var setHasMore = s6[1];
  var s7 = useState(0);
  var total = s7[0];
  var setTotal = s7[1];
  var s8 = useState(getTabFromLocation);
  var tab = s8[0];
  var setTabRaw = s8[1];
  var contentTopRef = useRef(null);
  var appShellRef = useRef(null);
  var suppressNextTabScrollRef = useRef(false);
  var pendingBackScrollRef = useRef(null);
  var lastListScrollRef = useRef(0);
  var initialArcPathHandledRef = useRef(false);
  function getScrollY() {
    if (typeof window === 'undefined') return 0;
    // The app uses a fixed-height shell as the real scroll container
    // (`html, body { overflow: hidden }`), so window.scrollY is usually 0.
    // Prefer the shell scrollTop or Back restoration will save/restore top.
    return (appShellRef.current && appShellRef.current.scrollTop) ||
      (document.scrollingElement && document.scrollingElement.scrollTop) ||
      (document.documentElement && document.documentElement.scrollTop) ||
      (document.body && document.body.scrollTop) ||
      window.scrollY ||
      0;
  }
  function saveCurrentScrollToHistory() {
    if (typeof window === 'undefined') return;
    try {
      var currentTab = getTabFromLocation();
      window.history.replaceState(
        Object.assign({}, window.history.state || {}, {
          tab: VALID_TABS.indexOf(currentTab) !== -1 ? currentTab : tab,
          scrollY: getScrollY(),
          listScrollY: lastListScrollRef.current,
        }),
        '',
        window.location.href
      );
    } catch (e) {}
  }
  function restoreScrollY(y) {
    if (typeof window === 'undefined') return;
    var target = Math.max(0, Number(y) || 0);
    function apply() {
      window.scrollTo({ top: target, left: 0, behavior: 'auto' });
      if (document.scrollingElement) document.scrollingElement.scrollTop = target;
      if (document.documentElement) document.documentElement.scrollTop = target;
      if (document.body) document.body.scrollTop = target;
      if (appShellRef.current) appShellRef.current.scrollTop = target;
    }
    pendingBackScrollRef.current = target;
    apply();
    setTimeout(apply, 0);
    setTimeout(apply, 80);
    setTimeout(apply, 250);
    setTimeout(function () {
      apply();
      if (pendingBackScrollRef.current === target) pendingBackScrollRef.current = null;
    }, 600);
  }
  function scrollToTabTop() {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    if (appShellRef.current) appShellRef.current.scrollTop = 0;
  }
  // Wrapper to update both state, URL, and page position using pushState
  var setTab = useCallback(function(newTab) {
    if (typeof window !== 'undefined') saveCurrentScrollToHistory();
    setTabRaw(function(currentTab) {
      if (currentTab !== newTab) scrollToTabTop();
      return newTab;
    });
    if (typeof window !== 'undefined') {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      var newUrl = getUrlForTab(newTab);
      if (window.location.pathname + window.location.hash !== newUrl) {
        window.history.pushState({ tab: newTab, scrollY: 0 }, '', newUrl);
      }
    }
  }, [setTabRaw, tab]);
  // Reset scroll after tab content has actually swapped. This catches mobile/PWA
  // webviews and hash navigation that restore scroll after the click handler.
  var prevTabRef = useRef(tab);
  useEffect(function() {
    if (prevTabRef.current !== tab) {
      if (suppressNextTabScrollRef.current) {
        suppressNextTabScrollRef.current = false;
      } else {
        scrollToTabTop();
        setTimeout(scrollToTabTop, 0);
        setTimeout(scrollToTabTop, 80);
      }
      prevTabRef.current = tab;
    }
  }, [tab]);

  // Disable browser-native restoration and keep the current history entry's
  // scrollY fresh, so browser Back can restore the exact list position.
  useEffect(function() {
    if (typeof window === 'undefined') return;
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    saveCurrentScrollToHistory();
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        if (!selected && !charIssuesRef.current) lastListScrollRef.current = getScrollY();
        saveCurrentScrollToHistory();
        ticking = false;
      });
    }
    var scrollNode = appShellRef.current || window;
    scrollNode.addEventListener('scroll', onScroll, { passive: true });
    if (scrollNode !== window) window.addEventListener('scroll', onScroll, { passive: true });
    return function () {
      scrollNode.removeEventListener('scroll', onScroll);
      if (scrollNode !== window) window.removeEventListener('scroll', onScroll);
    };
  }, [tab]);

  // Listen for popstate and hashchange (browser back/forward buttons)
  useEffect(function() {
    function updateTabFromLocation(opts) {
      opts = opts || {};
      var hash = getTabFromLocation();
      if (VALID_TABS.indexOf(hash) !== -1) {
        setTabRaw(function(currentTab) {
          if (currentTab !== hash && !opts.preserveScroll) scrollToTabTop();
          if (currentTab !== hash && opts.preserveScroll) suppressNextTabScrollRef.current = true;
          return hash;
        });
      }
    }
    function handlePopState(e) {
      var restoreY = e.state && typeof e.state.scrollY === 'number'
        ? e.state.scrollY
        : e.state && typeof e.state.listScrollY === 'number'
        ? e.state.listScrollY
        : lastListScrollRef.current;
      // Close character view on back button if open
      var currentCharIssues = charIssuesRef.current;
      if (currentCharIssues && (!e.state || !e.state.char)) {
        setCharIssues(null);
      }
      // Also close comic modal if open
      if (selected && (!e.state || e.state.modal !== 'comic')) {
        setSelected(null);
      }
      // Keep story-arc detail state in lockstep with the actual URL/history entry.
      // The bug was: the list could repaint while stale detail click targets stayed
      // mounted, then a follow-up effect reopened the old arc. Back to /story-arcs
      // must always mean "no selected arc".
      var arcSlug = getArcSlugFromLocation();
      if (!arcSlug) {
        setSelectedArc(null);
        setArcIssues([]);
        setArcIssuesLoading(false);
      } else if (e.state && e.state.arc) {
        var arcMatch = arcBundles.find(function (arc) {
          return arc.slug === arcSlug || arc.name === e.state.arc;
        });
        if (arcMatch) openLocalArcInCurrentTab(arcMatch, false);
      }
      // Always update tab from the URL after handling modals/details
      updateTabFromLocation({ preserveScroll: true });
      restoreScrollY(restoreY);
    }
    function handleHashChange() {
      // Handle hash changes that don't trigger popstate. During browser-back
      // modal closes, preserve the list position saved in history state.
      var stateY = window.history.state && typeof window.history.state.scrollY === 'number' ? window.history.state.scrollY : null;
      var pendingY = pendingBackScrollRef.current !== null ? pendingBackScrollRef.current : stateY;
      updateTabFromLocation({ preserveScroll: pendingY !== null });
      if (pendingY !== null) restoreScrollY(pendingY);
    }
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);
    // Check URL on mount
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    updateTabFromLocation();
    return function() {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [selected, selectedArc, arcBundles]);

  // If Back closes a detail sheet, React may unmount the fixed overlay after
  // the popstate restore. Restore again after the close commit so layout can't
  // snap the list back to the top.
  useEffect(function () {
    if (pendingBackScrollRef.current !== null && !selected && !charIssues) {
      restoreScrollY(pendingBackScrollRef.current);
    }
  }, [selected, charIssues, tab]);

  var s9 = useState("");
  var debouncedSearch = s9[0];
  var setDebouncedSearch = s9[1];
  var searchInputRef = useRef(null); // uncontrolled input - no re-render on keystroke
  var sMovieSearchTerms = useState([]);
  var movieSearchTerms = sMovieSearchTerms[0];
  var setMovieSearchTerms = sMovieSearchTerms[1];
  var s10 = useState("");
  var charSearch = s10[0];
  var setCharSearch = s10[1];
  var s11 = useState("");
  var filterArc = s11[0];
  var setFilterArc = s11[1];
  var s12 = useState("");
  var filterChar = s12[0];
  var setFilterChar = s12[1];
  var s13 = useState("");
  var filterYear = s13[0];
  var setFilterYear = s13[1];
  var s14 = useState(null);
  var openDrop = s14[0];
  var setOpenDrop = s14[1];
  var s15 = useState(null);
  var selected = s15[0];
  var setSelected = s15[1];
  var s16 = useState("comic");
  var selType = s16[0];
  var setSelType = s16[1];
  var s17 = useState(function () {
    try {
      var saved = storageGet("gv_wishlist");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  var wishlist = s17[0];
  var setWishlist = s17[1];
  var sColl = useState(function() {
    var saved = storageGet("gv_collection");
    return saved ? JSON.parse(saved) : [];
  });
  var collection = sColl[0];
  var setCollection = sColl[1];
  var sAccountDataLoaded = useState(false);
  var accountDataLoaded = sAccountDataLoaded[0];
  var setAccountDataLoaded = sAccountDataLoaded[1];
  var sEarnedAchievements = useState(function() {
    try {
      return JSON.parse(storageGet("gv_achievements") || "{}");
    } catch (e) {
      return {};
    }
  });
  var earnedAchievements = sEarnedAchievements[0];
  var setEarnedAchievements = sEarnedAchievements[1];
  var accountSaveTimerRef = useRef(null);
  var s18 = useState(null);
  var charIssues = s18[0];
  var setCharIssues = s18[1];
  var charIssuesRef = useRef(charIssues);
  useEffect(function() {
    charIssuesRef.current = charIssues;
  }, [charIssues]);
  
  // Load auth token from localStorage on startup
  useEffect(function() {
    var savedToken = storageGet('gv_auth_token');
    if (savedToken) {
      setAuthToken(savedToken);
      // Fetch current user info
      fetch(API_BASE + '/api/auth/me', {
        headers: { 'Authorization': 'Bearer ' + savedToken }
      })
      .then(function(res) {
        if (res.status === 401 || res.status === 403) {
          storageRemove('gv_auth_token');
          setAuthToken(null);
          setAccountDataLoaded(false);
          return null;
        }
        if (!res.ok) throw new Error('Auth check temporarily unavailable');
        return res.json();
      })
      .then(function(data) {
        if (!data) return;
        if (!data.error) {
          setCurrentUser(data);
          if (data.data) {
            var localWishlist = [];
            var localCollection = [];
            try {
              localWishlist = JSON.parse(storageGet("gv_wishlist") || "[]");
              localCollection = JSON.parse(storageGet("gv_collection") || "[]");
            } catch (e) {}
            var localAchievements = {};
            try {
              localAchievements = JSON.parse(storageGet("gv_achievements") || "{}");
            } catch (e) {}
            var mergedWishlist = Array.from(new Set((Array.isArray(data.data.wishlist) ? data.data.wishlist : []).concat(localWishlist)));
            var mergedCollection = Array.from(new Set((Array.isArray(data.data.collection) ? data.data.collection : []).concat(localCollection)));
            var mergedAchievements = Object.assign({}, data.data.achievements || {}, localAchievements || {});
            setWishlist(mergedWishlist);
            setCollection(mergedCollection);
            setEarnedAchievements(mergedAchievements);
            try {
              storageSet("gv_wishlist", JSON.stringify(mergedWishlist));
              storageSet("gv_collection", JSON.stringify(mergedCollection));
              storageSet("gv_achievements", JSON.stringify(mergedAchievements));
            } catch (e) {}
          }
          setAccountDataLoaded(true);
        } else {
          // Token invalid, clear it
          storageRemove('gv_auth_token');
          setAuthToken(null);
          setAccountDataLoaded(false);
        }
      })
      .catch(function() {
        // Keep the saved token on network/API hiccups. The user should stay
        // signed in until they explicitly log out, unless the server says the
        // token is invalid with a 401/403 above.
        setAccountDataLoaded(false);
      });
    }
  }, []);
  
  var s19 = useState(false);
  var charLoading = s19[0];
  var setCharLoading = s19[1];
  var s20 = useState("");
  var charError = s20[0];
  var setCharError = s20[1];
  var s21 = useState([]);
  var arcResults = s21[0];
  var setArcResults = s21[1];
  var s22 = useState(false);
  var arcSearching = s22[0];
  var setArcSearching = s22[1];
  var s23 = useState("");
  var arcSearched = s23[0];
  var setArcSearched = s23[1];
  var s24 = useState("");
  var arcSearch = s24[0];
  var setArcSearch = s24[1];
  var s25 = useState(null);
  var selectedArc = s25[0];
  var setSelectedArc = s25[1];
  var s29 = useState([]);
  var arcIssues = s29[0];
  var setArcIssues = s29[1];
  var s30 = useState(false);
  var arcIssuesLoading = s30[0];
  var setArcIssuesLoading = s30[1];
  var sAbout = useState(false);
  var showAbout = sAbout[0];
  var setShowAbout = sAbout[1];
  // Auth state
  var sAuth = useState(null);
  var authToken = sAuth[0];
  var setAuthToken = sAuth[1];
  var sUser = useState(null);
  var currentUser = sUser[0];
  var setCurrentUser = sUser[1];
  var sShowAuth = useState(false);
  var showAuthModal = sShowAuth[0];
  var setShowAuthModal = sShowAuth[1];
  var sAuthMode = useState('login');
  var authMode = sAuthMode[0];
  var setAuthMode = sAuthMode[1];
  var sAccountPrompt = useState(null);
  var accountPrompt = sAccountPrompt[0];
  var setAccountPrompt = sAccountPrompt[1];
  var sBugMessage = useState("");
  var bugMessage = sBugMessage[0];
  var setBugMessage = sBugMessage[1];
  var sBugEmail = useState("");
  var bugEmail = sBugEmail[0];
  var setBugEmail = sBugEmail[1];
  var sBugSending = useState(false);
  var bugSending = sBugSending[0];
  var setBugSending = sBugSending[1];
  var sBugStatus = useState(null);
  var bugStatus = sBugStatus[0];
  var setBugStatus = sBugStatus[1];
  var s26 = useState([]);
  var charResults = s26[0];
  var setCharResults = s26[1];
  var s27 = useState(false);
  var charSearching = s27[0];
  var setCharSearching = s27[1];
  var s28 = useState("");
  var charSearched = s28[0];
  var setCharSearched = s28[1];
  var loaderRef = useRef();
  var charDebounceRef = useRef(null);
  var arcDebounceRef = useRef(null);
  var searchDebounceRef = useRef(null);
  var searchActiveRef = useRef(false); // true when user has typed a search term

  // Load issues directly from the hardcoded volume list — no discovery wait.
  // Fetches 3 volumes in parallel on each scroll trigger for max speed.
  var fetchPage = useCallback(function (key, volIdx, offset) {
    if (!key) return;
    
    // Demo mode: load sample data immediately
    if (DEMO_MODE && key === "DEMO_KEY") {
      setLoading(true);
      setTimeout(function() {
        setIssues(DEMO_ISSUES);
        setTotal(DEMO_ISSUES.length);
        setHasMore(false);
        setLoading(false);
      }, 500); // Small delay to show loading state
      return;
    }
    
    // Curated dataset mode (no API key needed) - load all issues at once
    if (key === "CURATED_DATA" && volIdx === 0 && offset === 0) {
      setLoading(true);
      cvFetchProxy("issues", {
        limit: "1000",
        offset: "0",
        sort: "cover_date:asc",
      })
        .then(function (data) {
          var allIssues = (data.results || []).map(function (iss) {
            // Curated data already has _characters and _arc, only add if missing
            var enriched = Object.assign({}, iss);
            enriched._arc = detectIssueArc(enriched);
            if (!enriched._characters || enriched._characters.length === 0) {
              enriched._characters = (iss.character_credits || []).map(function (c) {
                return c.name;
              });
            }
            if (!enriched._characters || enriched._characters.length === 0) {
              enriched._characters = inferIssueCharacters(enriched);
            }
            return enriched;
          });
          setIssues(allIssues);
          setTotal(data.number_of_total_results || allIssues.length);
          setHasMore(false);
          setLoading(false);
        })
        .catch(function (e) {
          setError("Failed to load comics: " + e.message);
          setLoading(false);
        });
      return;
    }
    
    // For subsequent calls (shouldn't happen with curated data), just stop
    setHasMore(false);
    setLoading(false);
  }, []);

  useEffect(
    function () {
      if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
        return;
      }
      var obs = new window.IntersectionObserver(
        function (entries) {
          if (
            entries[0].isIntersecting &&
            hasMore &&
            !loading &&
            apiKey &&
            !searchActiveRef.current
          ) {
            setPage(function (p) {
              return Object.assign({}, p, { _trigger: Date.now() });
            });
          }
        },
        { threshold: 0.5 }
      );
      if (loaderRef.current) obs.observe(loaderRef.current);
      return function () {
        obs.disconnect();
      };
    },
    [hasMore, loading, apiKey]
  );

  // Fetch all issues in a story arc from curated local data
  function fetchArcIssues(arc) {
    if (arc && arc.name) trackViewedArc(arc.name);
    setArcIssues([]);
    setArcIssuesLoading(true);
    
    // Search locally in our curated dataset
    var result = issues.filter(function(issue) {
      return issue._arc === arc.name;
    }).sort(function (a, b) {
      return (a.cover_date || "").localeCompare(b.cover_date || "");
    });
    
    setArcIssues(result);
    setSelectedArc(function (prev) {
      if (!prev) return prev;
      return Object.assign({}, prev, {
        count_of_issues: result.length,
      });
    });
    setArcIssuesLoading(false);
  }

  function fetchCharIssues(char) {
    if (char && char.name) trackViewedCharacter(char.name);
    setCharIssues({
      char: char.name,
      issues: [],
      loading: true,
      image: char.image || null,
      heroImage: char.heroImage || char.image || null,
      heroPosition: char.heroPosition || char.imagePosition || "center center",
      deck: char.deck || null,
    });
    setCharLoading(true);
    setCharError("");
    // Push state so back button returns to character list
    if (typeof window !== 'undefined' && (!window.history.state || !window.history.state.char)) {
      var y = getScrollY();
      lastListScrollRef.current = y;
      window.history.replaceState(Object.assign({}, window.history.state || {}, { scrollY: y, listScrollY: y }), '', window.location.href);
      window.history.pushState({ char: char.name, scrollY: y, listScrollY: y }, '', window.location.href);
    }
    
    // If issues haven't loaded yet, show error
    if (!issues || issues.length === 0) {
      setCharIssues({
        char: char.name,
        issues: [],
        loading: false,
        image: char.image || null,
        heroImage: char.heroImage || char.image || null,
        heroPosition: char.heroPosition || char.imagePosition || "center center",
        deck: "Comics still loading. Please try again in a moment.",
      });
      setCharLoading(false);
      return;
    }
    
    // Search locally in our dataset for issues with this character
    var charName = char.name.toLowerCase();
    var result = issues.filter(function(issue) {
      return (issue._characters || []).some(function(c) {
        var cLower = c.toLowerCase();
        return namesReferToSameCharacter(cLower, charName);
      });
    }).sort(function (a, b) {
      return (a.cover_date || "").localeCompare(b.cover_date || "");
    });
    
    setCharIssues({
      char: char.name,
      issues: result,
      image: char.image || null,
      heroImage: char.heroImage || char.image || null,
      heroPosition: char.heroPosition || char.imagePosition || "center center",
      deck: char.deck || null,
    });
    setCharLoading(false);
  }

  // Fetch all Batman story arcs from Comic Vine's story_arcs endpoint
  // Search Comic Vine for story arcs using the /search/ endpoint
  function searchStoryArcs(query) {
    if (!query || query.length < 2) return;
    recordSearchAchievement(query);
    setArcSearching(true);
    setArcSearched(query);
    setArcResults([]);

    // Use the proxy endpoint
    function fetchArcs(q) {
      return fetch("/api/story_arcs?" + new URLSearchParams({
        filter: "name:" + q,
        field_list: "id,name,deck,image,count_of_issues,publisher",
        limit: "20",
        offset: "0",
        sort: "count_of_issues:desc",
      }))
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          // Filter to DC Comics arcs only (publisher id 10)
          return (data.results || []).filter(function (arc) {
            if (!arc.publisher) return true; // keep if no publisher data
            var pub = (arc.publisher.name || "").toLowerCase();
            return (
              pub.indexOf("dc") !== -1 ||
              pub.indexOf("vertigo") !== -1 ||
              pub.indexOf("wildstorm") !== -1
            );
          });
        })
        .catch(function () {
          return [];
        });
    }

    // Search for both "Court of Owls" and "A Court of Owls" style titles
    var queries = [query];
    if (!query.toLowerCase().startsWith("a ")) queries.push("A " + query);

    Promise.all(queries.map(fetchArcs))
      .then(function (resultSets) {
        var seen = {};
        var merged = [];
        resultSets.forEach(function (set) {
          set.forEach(function (r) {
            if (r.id && !seen[r.id]) {
              seen[r.id] = true;
              merged.push(r);
            }
          });
        });
        merged.sort(function (a, b) {
          return (b.count_of_issues || 0) - (a.count_of_issues || 0);
        });
        setArcResults(merged);
        setArcSearching(false);
        // Fetch real issue counts from detail endpoint — list endpoint often returns 0
        // Stagger requests to avoid hammering the API all at once
        merged.forEach(function (arc, idx) {
          if (!arc.id) return;
          if (arc.count_of_issues > 0) return; // already have a valid count
          setTimeout(function () {
            fetch("/api/story_arc/" + arc.id + "?" + new URLSearchParams({
              field_list: "count_of_issues,issues",
            }))
              .then(function (res) {
                return res.json();
              })
              .then(function (data) {
                var r = data.results || {};
                // Use issues array length as it's always accurate; fall back to count_of_issues
                var real =
                  r.issues && r.issues.length > 0
                    ? r.issues.length
                    : r.count_of_issues || 0;
                if (real > 0) {
                  setArcResults(function (prev) {
                    return prev.map(function (a) {
                      return a.id === arc.id
                        ? Object.assign({}, a, { count_of_issues: real })
                        : a;
                    });
                  });
                }
              })
              .catch(function () {});
          }, idx * 200); // stagger by 200ms per arc
        });
      })
      .catch(function () {
        setArcSearching(false);
      });
  }

  // Search Comic Vine for characters using the /search/ endpoint
  function searchCharacters(query) {
    if (!query || query.length < 2) return;
    recordSearchAchievement(query);
    setCharSearching(true);
    setCharSearched(query);
    setCharResults([]);
    fetch("/api/search?" + new URLSearchParams({
      query: query,
      resources: "character",
      field_list: "id,name,deck,image,count_of_issue_appearances,resource_type",
      limit: "20",
      offset: "0",
    }))
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var results = data.results || [];
        if (results.length === 0) {
          var q = query.toLowerCase();
          results = allChars
            .filter(function (entry) {
              return entry[0].toLowerCase().indexOf(q) !== -1;
            })
            .slice(0, 20)
            .map(function (entry, idx) {
              var known = BATMAN_CHARACTERS.find(function (c) {
                return c.name === entry[0];
              });
              return {
                id: known ? known.id : "local-" + idx,
                name: entry[0],
                count_of_issue_appearances: entry[1],
                resource_type: "character",
                deck: known ? known.deck : "Gotham City character",
              };
            });
        }
        setCharResults(results);
        setCharSearching(false);
      })
      .catch(function () {
        setCharSearching(false);
      });
  }

  // On first mount with a saved API key (auto-login), kick off the initial fetch
  useEffect(
    function () {
      if (apiKey) {
        fetchPage(apiKey, 0, 0);
      }
    },
    [apiKey, fetchPage]
  );

  // Trigger issue loading when page advances past the first page
  useEffect(
    function () {
      if (apiKey && (page.volIdx > 0 || page.offset > 0)) {
        fetchPage(apiKey, page.volIdx, page.offset);
      }
    },
    [apiKey, page.volIdx, page.offset, fetchPage]
  );

  function handleConnect() {
    var k = keyInput.trim();
    var w = workerInput.trim();
    if (!k || !w) return;
    storageSet("gv_api_key", k);
    storageSet("gv_worker_url", w);
    workerUrlRef.current = w;
    setIssues([]);
    setTotal(0);
    setHasMore(true);
    setPage({ volIdx: 0, offset: 0 });
    setApiKey(k);
    // Directly kick off first fetch — useEffect won't fire if page was already {0,0}
    fetchPage(k, 0, 0);
  }

  // Derived
  var arcCounts = {};
  issues.forEach(function (i) {
    if (i._arc) arcCounts[i._arc] = (arcCounts[i._arc] || 0) + 1;
  });
  var allArcs = Array.from(
    new Set(
      issues
        .map(function (i) {
          return i._arc;
        })
        .filter(function (arc) {
          return arc && arcCounts[arc] > 1;
        })
    )
  ).sort();
  var allYears = Array.from(
    new Set(
      issues
        .map(function (i) {
          return i.cover_date && i.cover_date.slice(0, 4);
        })
        .filter(Boolean)
    )
  ).sort();
  var charMap = {};
  issues.forEach(function (i) {
    (i._characters || []).forEach(function (c) {
      charMap[c] = (charMap[c] || 0) + 1;
    });
  });
  BATMAN_CHARACTERS.forEach(function (c) {
    if (!charMap[c.name]) charMap[c.name] = 0;
  });
  var allChars = Object.entries(charMap).sort(function (a, b) {
    return b[1] - a[1];
  });
  var characterBadgeCount = Math.max(
    BATMAN_CHARACTERS.length,
    allChars.filter(function (c) {
      return c[1] > 0;
    }).length
  );

  var arcBundles = allArcs.map(function (arc) {
    return {
      name: arc,
      slug: slugifyArcName(arc),
      issues: issues
        .filter(function (i) {
          return i._arc === arc;
        })
        .sort(function (a, b) {
          return (a.cover_date || "").localeCompare(b.cover_date || "");
        }),
    };
  });

  function buildLocalArcDetail(arc) {
    return {
      id: null,
      name: arc.name,
      count_of_issues: arc.issues.length,
      deck: getArcDescription(arc.name),
      image:
        arc.issues[0] && arc.issues[0].image
          ? arc.issues[0].image
          : null,
    };
  }

  function openLocalArcInCurrentTab(arc, pushUrl) {
    if (arc && arc.name) trackViewedArc(arc.name);
    setSelectedArc(buildLocalArcDetail(arc));
    setArcIssues(arc.issues);
    setArcIssuesLoading(false);
    setArcSearch("");
    setArcResults([]);
    setArcSearched("");
    if (typeof window !== 'undefined' && pushUrl) {
      window.history.pushState({ tab: "Story Arcs", arc: arc.name, scrollY: 0 }, '', getUrlForArc(arc.name));
    }
  }

  function openLocalArcAsSiteTab(arc) {
    openLocalArcInCurrentTab(arc, true);
    scrollToTabTop();
  }

  useEffect(function () {
    if (typeof window === 'undefined') return;
    var slug = getArcSlugFromLocation();
    if (!slug) {
      if (window.location.pathname === '/story-arcs' && selectedArc) {
        setSelectedArc(null);
        setArcIssues([]);
        setArcIssuesLoading(false);
      }
      return;
    }
    // Auto-open slug URLs only for the initial/direct page load. User back/list
    // navigation is handled by popstate above; otherwise clearing selectedArc can
    // immediately reopen the old arc and leave stale clickable regions behind.
    if (initialArcPathHandledRef.current) return;
    if (selectedArc && slugifyArcName(selectedArc.name) === slug && arcIssues.length > 0) {
      initialArcPathHandledRef.current = true;
      return;
    }
    var match = arcBundles.find(function (arc) {
      return arc.slug === slug;
    });
    if (match) {
      initialArcPathHandledRef.current = true;
      setTabRaw("Story Arcs");
      openLocalArcInCurrentTab(match, false);
      return;
    }
    var arcId = new URLSearchParams(window.location.search).get("id");
    if (!arcId || selectedArc) return;
    initialArcPathHandledRef.current = true;
    setTabRaw("Story Arcs");
    setArcIssues([]);
    setArcIssuesLoading(true);
    fetch("/api/story_arc/" + encodeURIComponent(arcId) + "?" + new URLSearchParams({
      field_list: "id,name,deck,image,count_of_issues",
    }))
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var arc = data.results || null;
        if (!arc || !arc.name) return;
        setSelectedArc(arc);
        fetchArcIssues(arc);
      })
      .catch(function () {
        setArcIssuesLoading(false);
      });
  }, [arcBundles, selectedArc, arcIssues.length]);

  var filtered = useMemo(
    function () {
      var q = debouncedSearch.toLowerCase();
      var movieTerms = (movieSearchTerms || []).map(function (term) {
        return String(term || "").toLowerCase();
      }).filter(Boolean);
      var moviePickIds = new Set();
      movieTerms.forEach(function (term) {
        if (typeof term === "number" || /^\d+$/.test(String(term))) moviePickIds.add(Number(term));
      });
      function issueMatchesTerm(i, term) {
        return (
          ((i.volume && i.volume.name) || "").toLowerCase().indexOf(term) !== -1 ||
          (i.name || "").toLowerCase().indexOf(term) !== -1 ||
          (i._arc || "").toLowerCase().indexOf(term) !== -1 ||
          (i._characters || []).some(function (c) {
            return c.toLowerCase().indexOf(term) !== -1;
          })
        );
      }
      var result = issues.filter(function (i) {
        return (
          ((!q && movieTerms.length === 0) ||
            (moviePickIds.size > 0
              ? moviePickIds.has(Number(i.id))
              : movieTerms.length > 0
              ? movieTerms.some(function (term) { return issueMatchesTerm(i, term); })
              : issueMatchesTerm(i, q))) &&
          (!filterArc || i._arc === filterArc) &&
          (!filterChar || (i._characters || []).indexOf(filterChar) !== -1) &&
          (!filterYear || (i.cover_date || "").indexOf(filterYear) === 0)
        );
      });
      result.sort(function (a, b) {
        var da = a.cover_date || "9999";
        var db = b.cover_date || "9999";
        return da.localeCompare(db);
      });
      return result;
    },
    [issues, debouncedSearch, movieSearchTerms, filterArc, filterChar, filterYear]
  );

  function unlockAchievement(id) {
    setEarnedAchievements(function(current) {
      if (current && current[id]) return current;
      var next = Object.assign({}, current || {});
      next[id] = true;
      try {
        storageSet("gv_achievements", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }

  function readAchievementStats() {
    try {
      return JSON.parse(storageGet("gv_achievement_stats") || "{}");
    } catch (e) {
      return {};
    }
  }

  function writeAchievementStats(stats) {
    try {
      storageSet("gv_achievement_stats", JSON.stringify(stats || {}));
    } catch (e) {}
  }

  function achievementDateKey(date) {
    var d = date || new Date();
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  }

  function trackDailyVisitAchievement() {
    var stats = readAchievementStats();
    var today = achievementDateKey();
    var yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    var yesterday = achievementDateKey(yesterdayDate);
    var visits = stats.visits || {};
    if (visits.lastDate !== today) {
      visits.streak = visits.lastDate === yesterday ? (visits.streak || 0) + 1 : 1;
      visits.lastDate = today;
      stats.visits = visits;
      writeAchievementStats(stats);
    }
    if ((visits.streak || 0) >= 7) unlockAchievement("dedicated_fan");
  }

  function recordSearchAchievement(query) {
    var q = String(query || "").trim().toLowerCase();
    if (q.length < 2) return;
    var stats = readAchievementStats();
    var searches = stats.searches || { count: 0, last: "" };
    if (searches.last !== q) {
      searches.count = (searches.count || 0) + 1;
      searches.last = q;
      stats.searches = searches;
      writeAchievementStats(stats);
    }
    if ((searches.count || 0) >= 50) unlockAchievement("detective");
  }

  function trackViewedArc(arcName) {
    if (!arcName) return;
    var stats = readAchievementStats();
    var viewed = stats.viewedArcs || {};
    viewed[arcName] = true;
    stats.viewedArcs = viewed;
    writeAchievementStats(stats);
    var count = Object.keys(viewed).length;
    if (count >= 5) unlockAchievement("arc_explorer");
    if (count >= 20) unlockAchievement("arc_master");
  }

  function trackViewedCharacter(characterName) {
    if (!characterName) return;
    var stats = readAchievementStats();
    var viewed = stats.viewedCharacters || {};
    viewed[characterName] = true;
    stats.viewedCharacters = viewed;
    writeAchievementStats(stats);
    var count = Object.keys(viewed).length;
    if (count >= 10) unlockAchievement("character_buff");
    if (count >= 50) unlockAchievement("character_scholar");
  }

  function issueHasAnyCharacter(issue, names) {
    var haystack = ((issue._characters || []).join(" ") + " " + ((issue.volume && issue.volume.name) || "") + " " + (issue.name || "")).toLowerCase();
    return names.some(function (name) {
      return haystack.indexOf(name.toLowerCase()) !== -1;
    });
  }

  function saveAccountData(nextWishlist, nextCollection, nextAchievements) {
    if (!authToken || !accountDataLoaded) return;
    if (accountSaveTimerRef.current) clearTimeout(accountSaveTimerRef.current);
    accountSaveTimerRef.current = setTimeout(function() {
      fetch(API_BASE + '/api/account/data', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authToken
        },
        body: JSON.stringify({
          wishlist: nextWishlist,
          collection: nextCollection,
          achievements: nextAchievements || {}
        })
      }).catch(function(err) {
        console.warn('Failed to sync account data', err);
      });
    }, 300);
  }

  useEffect(function() {
    if (!authToken || !accountDataLoaded) return;
    saveAccountData(wishlist, collection, earnedAchievements);
  }, [wishlist, collection, earnedAchievements, authToken, accountDataLoaded]);

  useEffect(function() {
    if (authToken || currentUser) unlockAchievement("first_login");
    if (collection.length > 0) unlockAchievement("first_collection");
    if (wishlist.length > 0) unlockAchievement("first_wishlist");
    if (collection.length >= 10) unlockAchievement("ten_collection");
    if (collection.length >= 25) unlockAchievement("quarter_vault");
    if (collection.length >= 50) unlockAchievement("fifty_collection");
    if (collection.length >= 100) unlockAchievement("hundred_collection");
    if (collection.length >= 500) unlockAchievement("five_hundred_collection");
    var hour = new Date().getHours();
    if (hour >= 5 && hour < 9) unlockAchievement("early_bird");
    if (hour >= 22 || hour < 5) unlockAchievement("night_owl");
  }, [collection.length, wishlist.length, authToken, currentUser]);

  function promptForAccount(action) {
    if (authToken || currentUser) return;
    setAccountPrompt(action);
  }

  function toggleWishlist(id) {
    var adding = wishlist.indexOf(id) === -1;
    if (adding) promptForAccount("wishlist");
    setWishlist(function (w) {
      var next =
        w.indexOf(id) !== -1
          ? w.filter(function (x) {
              return x !== id;
            })
          : w.concat([id]);
      try {
        storageSet("gv_wishlist", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }
  function toggleCollection(id) {
    var addingToCollection = collection.indexOf(id) === -1;
    if (addingToCollection) promptForAccount("collection");
    setCollection(function (c) {
      var adding = c.indexOf(id) === -1;
      var next = adding
          ? c.concat([id])
          : c.filter(function (x) {
              return x !== id;
            });
      if (adding && wishlist.indexOf(id) !== -1) {
        unlockAchievement("wishlist_to_collection");
      }
      if (adding) {
        var stats = readAchievementStats();
        var today = achievementDateKey();
        var daily = stats.dailyAdds || { date: today, ids: [] };
        if (daily.date !== today) daily = { date: today, ids: [] };
        if (daily.ids.indexOf(id) === -1) daily.ids.push(id);
        stats.dailyAdds = daily;
        writeAchievementStats(stats);
        if (daily.ids.length >= 25) unlockAchievement("power_user");
      }
      try {
        storageSet("gv_collection", JSON.stringify(next));
      } catch (e) {}
      return next;
    });
  }
  var issueIdSet = new Set(issues.map(function (i) { return i.id; }));
  var wishlistIssues = issues.filter(function (i) {
    return wishlist.indexOf(i.id) !== -1;
  });
  var collectionIssues = issues.filter(function (i) {
    return collection.indexOf(i.id) !== -1;
  });
  var actualTrackedIds = new Set();
  wishlist.forEach(function (id) {
    if (issueIdSet.has(id)) actualTrackedIds.add(id);
  });
  collection.forEach(function (id) {
    if (issueIdSet.has(id)) actualTrackedIds.add(id);
  });
  var trackedCount = actualTrackedIds.size;
  useEffect(function() {
    if (!issues.length) return;
    trackDailyVisitAchievement();
    if (!collectionIssues.length) return;

    var batFamilyNames = ["Batman", "Nightwing", "Robin", "Batgirl", "Red Hood", "Oracle", "Damian Wayne", "Dick Grayson", "Tim Drake", "Jason Todd", "Barbara Gordon", "Cassandra Cain", "Stephanie Brown", "Duke Thomas", "Batwing"];
    var villainNames = ["Joker", "Catwoman", "Two-Face", "Riddler", "Penguin", "Bane", "Ra's", "Harley Quinn", "Poison Ivy", "Scarecrow", "Mr. Freeze", "Deathstroke", "Black Mask", "Clayface", "Killer Croc", "Hugo Strange", "Deadshot"];
    var batFamilySeen = {};
    var villainsSeen = {};

    collectionIssues.forEach(function (issue) {
      var year = Number((issue.cover_date || "").slice(0, 4));
      if (year >= 1940 && year < 1950) unlockAchievement("golden_age");
      if (year >= 2011) unlockAchievement("modern_era");
      if (issueHasAnyCharacter(issue, ["Joker"])) {
        // counted below by filtering all Joker issues
      }
      batFamilyNames.forEach(function (name) {
        if (issueHasAnyCharacter(issue, [name])) batFamilySeen[name] = true;
      });
      villainNames.forEach(function (name) {
        if (issueHasAnyCharacter(issue, [name])) villainsSeen[name] = true;
      });
    });

    if (Object.keys(batFamilySeen).length >= 5) unlockAchievement("bat_family");
    if (Object.keys(villainsSeen).length >= 10) unlockAchievement("rogues_gallery");
    if (collectionIssues.filter(function (issue) { return issueHasAnyCharacter(issue, ["Joker"]); }).length >= 5) unlockAchievement("joker_fav");
    if (collectionIssues.filter(function (issue) { return issueHasAnyCharacter(issue, ["Batman"]); }).length >= 20) unlockAchievement("dark_knight");
    if (collectionIssues.some(function (issue) {
      return String(issue._arc || "").toLowerCase().indexOf("year one") !== -1 ||
        ((((issue.volume && issue.volume.name) || "") + " " + (issue.name || "")).toLowerCase().indexOf("year one") !== -1);
    })) unlockAchievement("first_blood");

    var collectionSet = new Set(collection);
    if (arcBundles.some(function (arc) {
      return arc.issues.length > 1 && arc.issues.every(function (issue) { return collectionSet.has(issue.id); });
    })) unlockAchievement("completionist");
  }, [issues.length, collection.length, collectionIssues.length, arcBundles.length]);
  var homeFeaturedIssues = issues
    .filter(function (i) {
      var title = (((i.volume && i.volume.name) || "") + " " + (i.name || "")).toLowerCase();
      return (
        title.indexOf("year one") !== -1 ||
        title.indexOf("long halloween") !== -1 ||
        title.indexOf("killing joke") !== -1 ||
        title.indexOf("hush") !== -1 ||
        title.indexOf("court of owls") !== -1 ||
        title.indexOf("dark victory") !== -1
      );
    })
    .slice(0, 6);
  if (homeFeaturedIssues.length < 6) {
    homeFeaturedIssues = issues.slice(0, 6);
  }
  function openBrowseAll() {
    searchActiveRef.current = false;
    if (searchInputRef.current) searchInputRef.current.value = "";
    setDebouncedSearch("");
    setMovieSearchTerms([]);
    setFilterArc("");
    setFilterChar("");
    setFilterYear("");
    setTab("Browse");
  }
  var movieBranches = [
    {
      movie: "The Batman (2022)",
      vibe: "A focused Reeves lane: Riddler tie-in, detective noir, early-Batman DNA, and Ego.",
      comics: [
        { label: "The Riddler: Year One", search: 1038281, type: "Tie-in" },
        { label: "Batman: Year One", search: 112492, type: "Influence" },
        { label: "The Long Halloween", search: [48827, 48828, 48829, 48830, 48831, 48832, 48833, 48834, 48835, 48836, 48837, 48838, 48839], type: "Influence" },
        { label: "Batman: Ego", search: 123427, type: "Influence" },
      ],
    },
    {
      movie: "The Dark Knight Trilogy",
      vibe: "Grounded Nolan-era picks: the Begins adaptation and older-Bat DNA without repeating Reeves picks.",
      comics: [
        { label: "Batman Begins adaptation", search: 306949, type: "Tie-in" },
        { label: "The Dark Knight Returns", search: [26264, 26276, 26282, 26290], type: "Influence" },
      ],
    },
    {
      movie: "Batman '89",
      vibe: "Burton gothic Gotham, Joker, Vicki Vale, and theatrical menace.",
      comics: [
        { label: "1989 movie adaptation", search: 157985, type: "Tie-in" },
        { label: "1989 deluxe edition", search: 728304, type: "Tie-in" },
        { label: "The Killing Joke", search: 119383, type: "Influence" },
      ],
    },
    {
      movie: "Batman Returns",
      vibe: "A tight Returns lane: the adaptation plus selected Catwoman/Penguin-era flavor.",
      comics: [
        { label: "Batman Returns adaptation", search: 157978, type: "Tie-in" },
        { label: "Catwoman vs. Batman", search: 120795, type: "Character flavor" },
        { label: "The Penguin on Parole", search: 122557, type: "Character flavor" },
      ],
    },
    {
      movie: "Batman Forever / Batman & Robin",
      vibe: "Schumacher tie-ins with a small set of Riddler/Robin flavor picks.",
      comics: [
        { label: "Batman Forever adaptation", search: 121974, type: "Tie-in" },
        { label: "Batman & Robin adaptation", search: 239258, type: "Tie-in" },
        { label: "Remarkable Ruse of the Riddler", search: 7750, type: "Character flavor" },
      ],
    },
  ];
  function exploreMovieBranch(branchOrTerm) {
    var terms = [];
    var label = "Batman";
    if (Array.isArray(branchOrTerm)) {
      terms = branchOrTerm;
      label = "Selected movie picks";
    } else if (branchOrTerm && typeof branchOrTerm === "object") {
      terms = [];
      (branchOrTerm.comics || []).forEach(function (comic) {
        if (Array.isArray(comic.search)) terms = terms.concat(comic.search);
        else if (comic.search) terms.push(comic.search);
      });
      label = branchOrTerm.movie || terms[0] || "Batman";
    } else {
      label = branchOrTerm || "Batman";
      terms = [label];
    }
    setFilterArc("");
    setFilterChar("");
    setFilterYear("");
    setMovieSearchTerms(terms);
    setDebouncedSearch(label);
    setTab("Browse");
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }
  function openComic(c) {
    setSelected(c);
    setSelType("comic");
    // Push state for modal so back button closes it, while preserving the list scroll position.
    if (typeof window !== 'undefined' && (!window.history.state || window.history.state.modal !== 'comic')) {
      var y = getScrollY();
      lastListScrollRef.current = y;
      window.history.replaceState(Object.assign({}, window.history.state || {}, { scrollY: y, listScrollY: y }), '', window.location.href);
      window.history.pushState({ modal: 'comic', id: c.id, scrollY: y, listScrollY: y }, '', window.location.href);
    }
  }
  // ── API key screen ──
  if (!apiKey)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: C.bg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "'Georgia', serif",
          backgroundImage:
            "radial-gradient(ellipse at 50% 0%,rgba(30,20,80,.6) 0%,transparent 70%)",
        }}
      >
        <div style={{ color: C.gold, marginBottom: 16 }}>
          <BatLogo size={60} />
        </div>
        <div
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: C.text,
            letterSpacing: 2,
            marginBottom: 4,
          }}
        >
          GOTHAM VAULT
        </div>
        <div
          style={{
            fontSize: 11,
            color: C.gold,
            fontFamily: "monospace",
            letterSpacing: 4,
            marginBottom: 32,
          }}
        >
          BATMAN COMICS DATABASE
        </div>
        <div
          style={{
            width: "100%",
            maxWidth: 360,
            background: C.surface,
            border: "1px solid " + C.border,
            borderRadius: 16,
            padding: 24,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <AlertIcon />
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              Free Comic Vine API Key Needed
            </div>
          </div>
          <p
            style={{
              fontSize: 12,
              color: C.muted,
              lineHeight: 1.75,
              marginBottom: 16,
            }}
          >
            Gotham Vault pulls every Batman comic ever published — thousands of
            issues with covers, characters, and story arcs.
          </p>
          <div
            style={{
              background: C.goldFaint,
              border: "1px solid " + C.borderGold,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: C.gold,
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: 1,
                marginBottom: 6,
              }}
            >
              GET YOUR FREE KEY (30 SECONDS):
            </div>
            <ol
              style={{
                fontSize: 12,
                color: C.muted,
                paddingLeft: 18,
                margin: 0,
                lineHeight: 2.1,
              }}
            >
              <li>
                Visit{" "}
                <strong style={{ color: C.text }}>
                  comicvine.gamespot.com/api
                </strong>
              </li>
              <li>Create a free account</li>
              <li>Copy your API key from that page</li>
              <li>Paste it below and connect</li>
            </ol>
          </div>
          <div
            style={{
              fontSize: 10,
              color: C.gold,
              fontFamily: "monospace",
              letterSpacing: 1,
              marginBottom: 5,
            }}
          >
            STEP 1 — COMIC VINE API KEY
          </div>
          <input
            value={keyInput}
            onChange={function (e) {
              setKeyInput(e.target.value);
            }}
            onKeyDown={function (e) {
              if (e.key === "Enter") handleConnect();
            }}
            placeholder="Paste your Comic Vine API key…"
            style={{
              width: "100%",
              padding: "11px 14px",
              borderRadius: 10,
              boxSizing: "border-box",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid " + C.border,
              color: C.text,
              fontSize: 13,
              fontFamily: "monospace",
              outline: "none",
              marginBottom: 14,
            }}
          />
          <div
            style={{
              fontSize: 10,
              color: C.gold,
              fontFamily: "monospace",
              letterSpacing: 1,
              marginBottom: 5,
            }}
          >
            STEP 2 — CLOUDFLARE WORKER URL
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              marginBottom: 6,
              lineHeight: 1.6,
            }}
          >
            Deploy{" "}
            <strong style={{ color: C.text }}>
              comic-vine-proxy-worker.js
            </strong>{" "}
            to <strong style={{ color: C.text }}>workers.cloudflare.com</strong>
            , then paste the URL below.
          </div>
          <input
            value={workerInput}
            onChange={function (e) {
              setWorkerInput(e.target.value);
            }}
            onKeyDown={function (e) {
              if (e.key === "Enter") handleConnect();
            }}
            placeholder="https://your-worker.yourname.workers.dev"
            style={{
              width: "100%",
              padding: "11px 14px",
              borderRadius: 10,
              boxSizing: "border-box",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid " + C.border,
              color: C.text,
              fontSize: 13,
              fontFamily: "monospace",
              outline: "none",
              marginBottom: 10,
            }}
          />
          <button
            onClick={handleConnect}
            disabled={!keyInput.trim() || !workerInput.trim()}
            style={{
              width: "100%",
              padding: 13,
              borderRadius: 10,
              border: "none",
              background:
                keyInput.trim() && workerInput.trim()
                  ? "linear-gradient(135deg," + C.gold + ",#e8a000)"
                  : "rgba(255,255,255,0.08)",
              color: keyInput.trim() && workerInput.trim() ? "#000" : C.dim,
              fontSize: 14,
              fontWeight: 700,
              cursor:
                keyInput.trim() && workerInput.trim() ? "pointer" : "default",
            }}
          >
            Connect &amp; Load All Batman Comics →
          </button>
          <div
            style={{
              fontSize: 11,
              color: C.dim,
              marginTop: 10,
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Free for non-commercial use · Comic Vine API
          </div>
        </div>
      </div>
    );

  function submitBugReport(context) {
    var message = bugMessage.trim();
    if (message.length < 10) {
      setBugStatus({ type: "error", text: "Please describe the bug in at least 10 characters." });
      return;
    }

    setBugSending(true);
    setBugStatus(null);
    fetch(API_BASE + "/api/bug-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: message,
        contactEmail: bugEmail.trim(),
        accountEmail: currentUser && currentUser.email ? currentUser.email : "",
        page: context || tab,
        url: window.location.href,
        browser: window.navigator.userAgent,
      }),
    })
      .then(function(res) {
        return res.json().then(function(data) {
          if (!res.ok) throw new Error(data.error || "Could not send bug report.");
          return data;
        });
      })
      .then(function() {
        setBugMessage("");
        setBugEmail("");
        setBugStatus({ type: "success", text: "Bug report sent — thank you!" });
      })
      .catch(function(err) {
        setBugStatus({ type: "error", text: err.message || "Could not send bug report." });
      })
      .finally(function() {
        setBugSending(false);
      });
  }

  function renderBugReportCard(context) {
    return (
      <div style={{ background: "rgba(255,255,255,.04)", border: "1px solid " + C.border, borderRadius: 14, padding: 16, marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginBottom: 6 }}>Found a bug?</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 12 }}>
          Tell us what went wrong and this will send the report directly, with the page and browser details included.
        </div>
        <textarea
          value={bugMessage}
          onChange={function(e) { setBugMessage(e.target.value); }}
          placeholder="Describe the bug…"
          rows={4}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "rgba(255,255,255,.05)",
            border: "1px solid " + C.border,
            borderRadius: 10,
            padding: 12,
            color: C.text,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
            marginBottom: 10,
          }}
        />
        <input
          value={bugEmail}
          onChange={function(e) { setBugEmail(e.target.value); }}
          placeholder="Your email, optional"
          type="email"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "rgba(255,255,255,.05)",
            border: "1px solid " + C.border,
            borderRadius: 10,
            padding: "10px 12px",
            color: C.text,
            fontSize: 13,
            fontFamily: "inherit",
            marginBottom: 10,
          }}
        />
        {bugStatus && (
          <div style={{ fontSize: 12, color: bugStatus.type === "success" ? "#86efac" : "#f87171", marginBottom: 10 }}>
            {bugStatus.text}
          </div>
        )}
        <button
          onClick={function() { submitBugReport(context); }}
          disabled={bugSending}
          style={{
            width: "100%",
            background: bugSending ? "rgba(255,255,255,.12)" : "linear-gradient(135deg," + C.gold + ",#e8a000)",
            border: "none",
            borderRadius: 999,
            padding: "11px 16px",
            color: bugSending ? C.dim : "#090909",
            fontSize: 13,
            fontWeight: 800,
            cursor: bugSending ? "default" : "pointer",
          }}
        >
          {bugSending ? "Sending…" : "🐞 Send Bug Report"}
        </button>
      </div>
    );
  }

  // ── Main UI ──
  return (
    <div
      ref={appShellRef}
      onClick={function () {
        setOpenDrop(null);
      }}
      style={{
        height: "100vh",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: C.bg,
        fontFamily: "'Georgia', serif",
        color: C.text,
        backgroundImage:
          "radial-gradient(ellipse at 20% 0%,rgba(20,10,60,.7) 0%,transparent 60%)",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: C.surface,
          borderBottom: "1px solid " + C.borderGold,
          position: "sticky",
          top: 0,
          zIndex: 50,
          backdropFilter: "blur(12px)",
          width: "100%",
        }}
      >
        <div
          className="site-header-inner"
          style={{
            maxWidth: 1400,
            width: "100%",
            boxSizing: "border-box",
            margin: "0 auto",
            padding: "8px 20px",
            paddingTop: "calc(8px + env(safe-area-inset-top, 0px))",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            minWidth: 0,
          }}
        >
          <div 
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", minWidth: 0, flex: "1 1 auto" }}
            onClick={function() { setTab("Home"); }}
          >
            <div style={{ color: C.gold }}>
              <BatLogo size={42} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: "1.5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                GOTHAM VAULT
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: C.gold,
                  fontFamily: "monospace",
                  letterSpacing: "3px",
                  marginTop: -1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {DEMO_MODE && apiKey === "DEMO_KEY"
                  ? "DEMO MODE - SAMPLE ISSUES"
                  : issues.length === 0 && loading
                  ? "CONNECTING…"
                  : issues.length.toLocaleString() +
                    " LOADED" +
                    (hasMore
                      ? " · STILL LOADING…"
                      : " · " + total.toLocaleString() + " TOTAL")}
              </div>
            </div>
          </div>
          <nav className="header-nav" aria-label="Main navigation">
            {["Browse", "Story Arcs", "Characters", "Guide", "Collection"].map(function (t) {
              var active = tab === t;
              return (
                <button
                  key={t}
                  onClick={function () { if (t === "Browse") openBrowseAll(); else setTab(t); }}
                  style={{
                    background: active ? "rgba(212,160,23,.16)" : "rgba(255,255,255,.04)",
                    border: "1px solid " + (active ? C.borderGold : C.border),
                    borderRadius: 999,
                    padding: "7px 11px",
                    color: active ? C.gold : C.text,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "monospace",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all .15s",
                  }}
                >
                  {t}
                </button>
              );
            })}
          </nav>
          <select
            className="mobile-header-nav"
            aria-label="Main navigation"
            value={["Browse", "Story Arcs", "Characters", "Guide", "Collection"].indexOf(tab) >= 0 ? tab : ""}
            onChange={function (e) {
              var next = e.target.value;
              if (!next) return;
              if (next === "Browse") openBrowseAll(); else setTab(next);
            }}
            style={{
              background: "rgba(255,255,255,.04)",
              border: "1px solid " + C.borderGold,
              borderRadius: 999,
              padding: "8px 34px 8px 12px",
              color: C.gold,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "monospace",
              cursor: "pointer",
            }}
          >
            <option value="" disabled>Navigate</option>
            {["Browse", "Story Arcs", "Characters", "Guide", "Collection"].map(function (t) {
              return <option key={t} value={t}>{t}</option>;
            })}
          </select>
          <div className="header-account" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <button
              onClick={function () { setTab("Account"); }}
              style={{
                background: "none",
                border: "1px solid " + C.borderGold,
                borderRadius: 20,
                padding: "4px 12px",
                fontSize: 11,
                color: C.gold,
                fontFamily: "monospace",
                cursor: "pointer",
                transition: "all .15s",
              }}
            >
              {currentUser ? "👤 " + currentUser.name : "👤 Account"}
            </button>
          </div>
        </div>
      </div>

      <div ref={contentTopRef} className={tab === "Home" ? "home-content app-content-shell" : "app-content-shell"} style={{ maxWidth: (tab === "Blog" || tab === "About" || tab === "FAQ") ? 1040 : 1480, width: "100%", margin: "0 auto", padding: "16px 24px", boxSizing: "border-box" }}>
        {error && (
          <div
            style={{
              background: "rgba(239,68,68,.1)",
              border: "1px solid rgba(239,68,68,.3)",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 12,
              color: "#f87171",
              marginBottom: 14,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
            }}
          >
            <AlertIcon /> {error}
          </div>
        )}

        {tab === "Home" && (
          <div className="home-page">
            <div
              className="home-hero"
              style={{ 
                background: "url('/welcome-bg.jpg') center/cover no-repeat, linear-gradient(135deg,rgba(10,10,20,.7),rgba(10,10,20,.75))",
                backgroundBlendMode: "overlay",
                border: "1px solid " + C.borderGold,
                borderRadius: 18,
                padding: "60px 24px",
                marginBottom: 14,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    fontSize: 10,
                    color: C.gold,
                    fontFamily: "monospace",
                    letterSpacing: 2,
                    marginBottom: 8,
                  }}
                >
                  WELCOME TO THE VAULT
                </div>
                <div
                  style={{
                    fontSize: 28,
                    lineHeight: 1.05,
                    fontWeight: 800,
                    color: C.text,
                    marginBottom: 10,
                  }}
                >
                  Find your way through Batman comics.
                </div>
                <div
                  style={{
                    fontSize: 16,
                    color: "#ffffff",
                    lineHeight: 1.7,
                    marginBottom: 16,
                    maxWidth: 340,
                  }}
                >
                  Gotham Vault is a guided Batman comics database: browse issues, follow story arcs, look up characters, and track what you own or want next.
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={function () { setTab("Guide"); }}
                    style={{
                      border: "none",
                      borderRadius: 999,
                      padding: "10px 15px",
                      background: "linear-gradient(135deg," + C.gold + ",#e8a000)",
                      color: "#090909",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Start with the guide →
                  </button>
                  <button
                    onClick={openBrowseAll}
                    style={{
                      border: "1px solid " + C.border,
                      borderRadius: 999,
                      padding: "10px 15px",
                      background: "rgba(255,255,255,.05)",
                      color: C.text,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Browse issues
                  </button>
                </div>
              </div>
            </div>

            <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              {[ 
                {
                  id: "eraser-lenny-fiasco-gotham-crime-cleaner",
                  title: "The Eraser Explained — Lenny Fiasco Cleans Up Gotham's Crimes",
                  category: "Villain Spotlights",
                  date: "August 20, 2026",
                  excerpt: "Lenny Fiasco turned an old school humiliation into the Eraser, a pencil-headed professional who removes evidence for Gotham's criminals and makes every crime scene a battle of wits with Batman."
                },
                {
                  id: "signalman-phil-cobb-gotham-symbol-villain",
                  title: "Signalman Explained — Phil Cobb Turns Gotham's Symbols Against Batman",
                  category: "Villain Spotlights",
                  date: "August 19, 2026",
                  excerpt: "Phil Cobb became Signalman after Gotham's criminals refused to notice him, building crimes around signs, signals, and the belief that a memorable symbol could force the city to pay attention."
                },
                {
                  id: "film-freak-burt-weston-gotham-movie-villain",
                  title: "Film Freak Explained — Burt Weston Turns Gotham Crime Into a Movie Marathon",
                  category: "Villain Spotlights",
                  date: "August 17, 2026",
                  excerpt: "Burt Weston commits crimes through the borrowed identities of classic cinema, forcing Batman to solve a villain whose costumes, dialogue, and schemes change whenever the movie in his head cuts to a new scene."
                },
                {
                  id: "ratcatcher-otis-flannegan-gotham-rats",
                  title: "Ratcatcher Explained — Otis Flannegan Commands the Rats Beneath Gotham",
                  category: "Villain Spotlights",
                  date: "August 17, 2026",
                  excerpt: "Former Gotham rat-catcher Otis Flannegan turns the city's hidden tunnels and vermin into a living criminal network, making Gotham's neglect answer to him one swarm at a time."
                },
                {
                  id: "doctor-phosphorus-gotham-radioactive-villain",
                  title: "Doctor Phosphorus Explained — Gotham's Radioactive Nightmare That Never Stops Burning",
                  category: "Villain Spotlights",
                  date: "August 15, 2026",
                  excerpt: "Doctor Phosphorus transformed a corrupt Gotham power project into a body of living radiation, giving Batman one of his earliest modern villains and the city a walking reminder that civic greed leaves permanent scars."
                },
                {
                  id: "arnold-flass-gcpd-corruption-batman-year-one",
                  title: "Arnold Flass Explained — The Corrupt GCPD Detective Who Welcomed Jim Gordon to Gotham",
                  category: "GCPD & Gotham Corruption",
                  date: "August 15, 2026",
                  excerpt: "Arnold Flass is the brutal, well-connected detective who teaches Jim Gordon how deeply corruption runs through the GCPD, making him one of Batman: Year One's clearest symbols of a city that protects its own rot."
                },
                {
                  id: "lucius-fox-wayne-enterprises-batman-ally-gotham",
                  title: "Lucius Fox Explained — The Man Who Keeps Wayne Enterprises and Batman's Mission Alive",
                  category: "Gotham Allies & Wayne Enterprises",
                  date: "August 13, 2026",
                  excerpt: "Lucius Fox is more than Bruce Wayne's trusted executive: he protects the company that funds Gotham's future, turns ambitious ideas into working technology, and gives the Batman mission a vital connection to the city it claims to serve."
                },
                {
                  id: "batman-siege-colonel-brass-wayne-manor",
                  title: "Batman: Siege Explained — Colonel Brass Brings Gotham's Past to Wayne Manor",
                  category: "Gotham History & Wayne Legacy",
                  date: "August 10, 2026",
                  excerpt: "Batman: Siege turns Wayne Manor into a battleground when the vengeful Colonel Brass returns to reclaim a legacy buried beneath Gotham, forcing Bruce Wayne to defend both his city and the history attached to his family home."
                },
                {
                  id: "batman-idols-gotham-worships-the-bat",
                  title: "Batman: Idols Explained — When Gotham Worships the Bat",
                  category: "Gotham Myth & Early Batman",
                  date: "August 8, 2026",
                  excerpt: "Batman: Idols turns the Dark Knight's growing legend into evidence at a ritualistic crime scene, confronting Bruce with the frightening truth that Gotham can imitate, commercialize, and corrupt a symbol he never fully controlled."
                },
                {
                  id: "joker-killer-smile-psychological-horror-gotham",
                  title: "Joker: Killer Smile Explained — Gotham's Most Dangerous Patient",
                  category: "Joker & Psychological Horror",
                  date: "August 8, 2026",
                  excerpt: "Joker: Killer Smile traps psychiatrist Ben Arnell inside a therapeutic relationship the Joker has already turned into a weapon, asking whether anyone can study Gotham's most dangerous patient without carrying his chaos home."
                },
                {
                  id: "batman-the-last-crusade-jason-todd-final-mission",
                  title: "Batman: The Last Crusade Explained — Jason Todd's Final Mission Before the Dark Knight Returns",
                  category: "Elseworlds & Dark Knight Returns",
                  date: "August 5, 2026",
                  excerpt: "The Dark Knight Returns: The Last Crusade revisits an aging Bruce Wayne, a reckless Jason Todd, and the brutal case that explains why Gotham's Batman eventually disappeared."
                },
                {
                  id: "batman-false-faces-brian-k-vaughan-identity",
                  title: "Batman: False Faces Explained - Gotham's Masks Turn Identity Into a Weapon",
                  category: "Story Guide",
                  date: "July 22, 2026",
                  excerpt: "Batman: False Faces gathers Brian K. Vaughan's sharp stories about disguise, divided selves, and the dangerous identities Gotham creates, from Matches Malone to the city's most unstable rogues."
                },
                {
                  id: "batman-universe-bendis-derington-world-tour",
                  title: "Batman: Universe Explained — The Dark Knight Takes the Scenic Route",
                  category: "Story Arc Deep Dives",
                  date: "July 6, 2026",
                  excerpt: "Batman: Universe sends Bruce Wayne on a bright, fast-moving chase across the DC Universe, using Brian Michael Bendis and Nick Derington's globe-trotting adventure to show how Batman works when Gotham is no longer the whole map."
                },
                { label: "Issues", value: issues.length.toLocaleString(), tab: "Browse" },
                { label: "Story arcs", value: allArcs.length.toLocaleString(), tab: "Story Arcs" },
                { label: "Characters", value: characterBadgeCount.toLocaleString(), tab: "Characters" },
                { label: "Tracked", value: trackedCount.toLocaleString(), tab: "Collection" },
              ].filter(function (stat) { return stat && stat.label && stat.value != null && stat.tab; }).map(function (stat) {
                return (
                  <button
                    key={stat.label}
                    onClick={function () { if (stat.tab === "Browse") openBrowseAll(); else setTab(stat.tab); }}
                    style={{
                      textAlign: "left",
                      background: C.surface,
                      border: "1px solid " + C.border,
                      borderRadius: 14,
                      padding: 14,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: 22, color: C.gold, fontWeight: 800 }}>{stat.value}</div>
                    <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: 1 }}>{stat.label.toUpperCase()}</div>
                  </button>
                );
              })}
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Good places to begin</div>
                <button onClick={function () { setTab("Story Arcs"); }} style={{ background: "none", border: "none", color: C.gold, fontSize: 11, cursor: "pointer" }}>
                  View arcs →
                </button>
              </div>
              <div className="home-featured-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {homeFeaturedIssues.map(function (iss) {
                  return (
                    <button
                      key={iss.id}
                      onClick={function () { openComic(iss); }}
                      style={{
                        background: C.surface,
                        border: "1px solid " + C.border,
                        borderRadius: 12,
                        padding: 8,
                        cursor: "pointer",
                        textAlign: "left",
                        overflow: "hidden",
                      }}
                    >
                      <Cover src={iss.image && iss.image.small_url} w="100%" h="auto" r={8} fallbackText={iss.name} arc={iss._arc} />
                      <div style={{ fontSize: 10, color: C.text, fontWeight: 700, marginTop: 7, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(iss.volume && iss.volume.name) || "Batman"} #{iss.issue_number}
                      </div>
                      <div style={{ fontSize: 9, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {iss._arc || iss.name || "Issue"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>Start from a Batman movie</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>Pick the screen version you like — Gotham Vault will point you toward comics with the same DNA.</div>
                </div>
                <button onClick={function () { setTab("Guide"); }} style={{ background: "none", border: "none", color: C.gold, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
                  Full guide →
                </button>
              </div>
              <div className="home-movie-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                {movieBranches.map(function (branch) {
                  return (
                    <div
                      key={branch.movie}
                      onClick={function () { exploreMovieBranch(branch); }}
                      role="button"
                      tabIndex={0}
                      style={{
                        background: "linear-gradient(145deg,rgba(212,160,23,.12),rgba(255,255,255,.04))",
                        border: "1px solid " + C.border,
                        borderRadius: 14,
                        padding: 13,
                        cursor: "pointer",
                        textAlign: "left",
                        color: C.text,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: C.gold }}>{branch.movie}</div>
                        <div style={{ color: C.dim, fontSize: 16 }}>›</div>
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45, marginTop: 6 }}>{branch.vibe}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
                        {branch.comics.map(function (comic) {
                          return (
                            <button
                              key={comic.label}
                              onClick={function (e) {
                                e.stopPropagation();
                                exploreMovieBranch(comic.search);
                              }}
                              style={{
                                fontSize: 9,
                                color: C.text,
                                background: comic.type === "Tie-in" ? "rgba(212,160,23,.14)" : "rgba(0,0,0,.22)",
                                border: "1px solid " + (comic.type === "Tie-in" ? C.borderGold : "rgba(255,255,255,.08)"),
                                borderRadius: 999,
                                padding: "4px 7px",
                                cursor: "pointer",
                              }}
                              title={comic.type}
                            >
                              <span style={{ opacity: 0.72 }}>{comic.type}: </span>{comic.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>What can I do here?</div>
              {[
                ["Browse", "Search the full issue archive by title, character, year, or arc."],
                ["Story Arcs", "Follow curated runs like Year One, Hush, Knightfall, and No Man's Land."],
                ["Characters", "Pick a hero or villain and jump straight to their appearances."],
                ["Collection", "Save what you own and build a wishlist for the hunt."],
              ].map(function (item) {
                return (
                  <button key={item[0]} onClick={function () { setTab(item[0]); }} style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 12, background: "transparent", border: "none", borderTop: "1px solid " + C.border, padding: "11px 0", color: C.text, cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{item[0]}</span>
                    <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.45 }}>{item[1]}</span>
                  </button>
                );
              })}
            </div>

            <div className="home-footer-grid" style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {[
                ["Blog", "Deep dives"],
                ["About", "Project info"],
                ["FAQ", "Help & answers"],
              ].map(function(item) {
                return (
                  <button
                    key={item[0]}
                    onClick={function() { setTab(item[0]); }}
                    style={{
                      background: "rgba(255,255,255,.04)",
                      border: "1px solid " + C.border,
                      borderRadius: 12,
                      padding: "12px 10px",
                      color: C.text,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.gold, marginBottom: 4 }}>{item[0]}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{item[1]}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {tab === "Browse" && (
          <div style={{ width: "100%" }}>
            <div style={{ maxWidth: 480, margin: "0 auto" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid " + C.border,
                borderRadius: 12,
                padding: "9px 14px",
                marginBottom: 12,
              }}
            >
              <SearchIcon />
              <input
                ref={searchInputRef}
                onChange={function (e) {
                  var v = e.target.value;
                  searchActiveRef.current = v.length > 0;
                  setMovieSearchTerms([]);
                  clearTimeout(searchDebounceRef.current);
                  if (v.length === 0) {
                    setDebouncedSearch("");
                  } else {
                    searchDebounceRef.current = setTimeout(function () {
                      recordSearchAchievement(v);
                      setDebouncedSearch(v);
                      window.scrollTo(0, 0);
                    }, 200);
                  }
                }}
                placeholder="Search titles, characters…"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: C.text,
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              />
              {debouncedSearch && (
                <span
                  onClick={function () {
                    searchActiveRef.current = false;
                    if (searchInputRef.current)
                      searchInputRef.current.value = "";
                    setDebouncedSearch("");
                    setMovieSearchTerms([]);
                  }}
                  style={{ cursor: "pointer", color: C.dim }}
                >
                  <XIcon />
                </span>
              )}
            </div>
            <div
              onClick={function (e) {
                e.stopPropagation();
              }}
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 14,
              }}
            >
              <Dropdown
                id="arc"
                label="Arc"
                value={filterArc}
                options={allArcs}
                onChange={setFilterArc}
                openId={openDrop}
                setOpenId={setOpenDrop}
              />
              <Dropdown
                id="char"
                label="Character"
                value={filterChar}
                options={POPULAR_FILTER_CHARS.concat(
                  allChars
                    .slice(0, 80)
                    .map(function (x) {
                      return x[0];
                    })
                    .filter(function (c) {
                      return POPULAR_FILTER_CHARS.indexOf(c) === -1;
                    })
                )}
                onChange={setFilterChar}
                openId={openDrop}
                setOpenId={setOpenDrop}
              />
              <Dropdown
                id="year"
                label="Year"
                value={filterYear}
                options={allYears}
                onChange={setFilterYear}
                openId={openDrop}
                setOpenId={setOpenDrop}
              />
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.dim,
                fontFamily: "monospace",
                marginBottom: 12,
              }}
            >
              {movieSearchTerms.length > 1
                ? "MOVIE PICKS · " + filtered.length.toLocaleString() + " TIE-INS & INFLUENCES"
                : debouncedSearch || filterArc || filterChar || filterYear
                ? filtered.length > 200
                  ? "SHOWING 200 OF " +
                    filtered.length.toLocaleString() +
                    " MATCHES"
                  : filtered.length.toLocaleString() +
                    " MATCH" +
                    (filtered.length !== 1 ? "ES" : "")
                : filtered.length.toLocaleString() +
                  " ISSUES · " +
                  total.toLocaleString() +
                  " TOTAL"}
            </div>
            </div>
            {/* Centered issue grid - independent from the narrow controls/header column */}
            <div className="browse-grid" style={{ display: "grid", gap: 10, maxWidth: 1200, margin: "0 auto" }}>
              {(debouncedSearch || movieSearchTerms.length > 0 || filterArc || filterChar || filterYear
                ? filtered.slice(0, 200)
                : filtered
              ).map(function (c) {
                return (
                  <ComicCard
                    key={c.id}
                    comic={c}
                    wishlisted={wishlist.indexOf(c.id) !== -1}
                    onWishlist={toggleWishlist}
                    owned={collection.indexOf(c.id) !== -1}
                    onOwn={toggleCollection}
                    onClick={function () {
                      openComic(c);
                    }}
                    onArcClick={function (arcName) {
                      setSelected(null);
                      setSelectedArc(null);
                      setArcIssues([]);
                      setTab("Story Arcs");
                      setArcSearch(arcName);
                      clearTimeout(arcDebounceRef.current);
                      searchStoryArcs(arcName);
                    }}
                  />
                );
              })}
            </div>
            <div ref={loaderRef} style={{ padding: 24, textAlign: "center" }}>
              {loading && (
                <Spinner
                  message={
                    "LOADING… " +
                    issues.length.toLocaleString() +
                    " / " +
                    total.toLocaleString()
                  }
                />
              )}
              {!hasMore && issues.length > 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: C.dim,
                    fontFamily: "monospace",
                  }}
                >
                  ALL {total.toLocaleString()} ISSUES LOADED
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "Story Arcs" && (
          <div style={{ width: "100%" }}>
            <div style={{ maxWidth: 620, margin: "0 auto 18px" }}>
              <div style={{ fontSize: 10, color: C.purple, fontFamily: "monospace", letterSpacing: 2, marginBottom: 6 }}>STORY ARCS</div>
              <div style={{ fontSize: 26, color: C.text, fontWeight: 800, lineHeight: 1.1, marginBottom: 8 }}>Explore Batman by storyline.</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>Cards are arranged by arc, with cover art up front and issue counts below so the page works like a real browsing destination.</div>
            {/* Search — debounced, hits CV API on typing */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid " + C.border,
                borderRadius: 12,
                padding: "9px 14px",
                marginBottom: 6,
              }}
            >
              <SearchIcon />
              <input
                value={arcSearch}
                onChange={function (e) {
                  var q = e.target.value;
                  setArcSearch(q);
                  // Clear any open arc detail so search results can show
                  if (q.length >= 1) {
                    setSelectedArc(null);
                    setArcIssues([]);
                  }
                  clearTimeout(arcDebounceRef.current);
                  if (q.length >= 2) {
                    arcDebounceRef.current = setTimeout(function () {
                      searchStoryArcs(q);
                    }, 500);
                  } else {
                    setArcResults([]);
                    setArcSearched("");
                  }
                }}
                placeholder="Search all story arcs…"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: C.text,
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              />
              {arcSearch && (
                <span
                  onClick={function () {
                    setArcSearch("");
                    setArcResults([]);
                    setArcSearched("");
                  }}
                  style={{ cursor: "pointer", color: C.dim }}
                >
                  <XIcon />
                </span>
              )}
            </div>
            {arcSearch.length >= 2 && !arcSearching && arcSearched && (
              <div
                style={{
                  fontSize: 11,
                  color: C.dim,
                  fontFamily: "monospace",
                  marginBottom: 12,
                }}
              >
                {arcResults.length} RESULTS FOR "{arcSearch.toUpperCase()}"
              </div>
            )}
            {arcSearching && <Spinner message="SEARCHING COMIC VINE…" />}
            </div>

            {/* Arc detail view — only show when not actively searching */}
            {selectedArc ? (
              <div>
                <button
                  onClick={function () {
                    setSelectedArc(null);
                    setArcIssues([]);
                    setArcSearch("");
                    setArcResults([]);
                    setArcSearched("");
                    if (typeof window !== 'undefined' && window.location.pathname.indexOf('/story-arcs/') === 0) {
                      window.history.replaceState({ tab: "Story Arcs", scrollY: 0 }, '', '/story-arcs');
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "none",
                    border: "none",
                    color: C.muted,
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "0 0 14px",
                  }}
                >
                  <BackIcon /> All Story Arcs
                </button>
                <div style={{ display: "flex", gap: 14, marginBottom: 16 }}>
                  <Cover
                    src={selectedArc.image && selectedArc.image.medium_url}
                    w={72}
                    h={100}
                    r={8}
                    fallbackText={selectedArc.name}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: C.text,
                        marginBottom: 4,
                      }}
                    >
                      {selectedArc.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.purple,
                        fontFamily: "monospace",
                        marginBottom: 8,
                      }}
                    >
                      {arcIssuesLoading
                        ? "…"
                        : arcIssues.length > 0
                        ? arcIssues.length
                        : selectedArc.count_of_issues > 0
                        ? selectedArc.count_of_issues
                        : "?"}{" "}
                      ISSUES · STORY ARC
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: C.muted,
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedArc.deck || getArcDescription(selectedArc.name)}
                    </div>
                  </div>
                </div>
                <BuyButtons query={"Batman " + selectedArc.name + " comic"} asin={findAsinForQuery("Batman " + selectedArc.name + " comic")} />
                <div
                  style={{
                    marginTop: 20,
                    fontSize: 9,
                    color: C.dim,
                    fontFamily: "monospace",
                    letterSpacing: 1,
                    marginBottom: 10,
                  }}
                >
                  {arcIssuesLoading
                    ? "LOADING ISSUES…"
                    : arcIssues.length + " ISSUES IN THIS ARC"}
                </div>
                {arcIssuesLoading && (
                  <Spinner
                    message={
                      "FETCHING " +
                      (selectedArc.name || "").toUpperCase() +
                      " ISSUES…"
                    }
                  />
                )}
                {!arcIssuesLoading && arcIssues.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "24px 0",
                      color: C.dim,
                      fontSize: 13,
                    }}
                  >
                    No issues found. This arc may use a different ID format on
                    Comic Vine.
                  </div>
                )}
                {!arcIssuesLoading && arcIssues.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {arcIssues.map(function (c) {
                      return (
                        <ComicCard
                          key={c.id}
                          comic={c}
                          wishlisted={wishlist.indexOf(c.id) !== -1}
                          onWishlist={toggleWishlist}
                          owned={collection.indexOf(c.id) !== -1}
                          onOwn={toggleCollection}
                          onClick={function () {
                            openComic(c);
                          }}
                          onArcClick={function (arcName) {
                            setSelected(null);
                            setSelectedArc(null);
                            setArcIssues([]);
                            setTab("Story Arcs");
                            setArcSearch(arcName);
                            clearTimeout(arcDebounceRef.current);
                            searchStoryArcs(arcName);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Search results or curated arc bundles */}
                {arcSearch.length >= 2 ? (
                  <div>
                    {!arcSearching && arcResults.length > 0 && (
                      <div className="story-arc-grid">
                        {arcResults.map(function (arc) {
                          return (
                            <ArcListCard
                              key={arc.id}
                              arc={arc}
                              onClick={function () {
                                setSelectedArc(arc);
                                fetchArcIssues(arc);
                                setArcSearch("");
                                setArcResults([]);
                                setArcSearched("");
                                if (typeof window !== 'undefined') {
                                  window.history.pushState({ tab: "Story Arcs", arc: arc.name, scrollY: 0 }, '', getUrlForArc(arc.name, arc.id));
                                }
                                scrollToTabTop();
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {!arcSearching &&
                      arcSearched &&
                      arcResults.length === 0 && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "32px 20px",
                            color: C.dim,
                            fontSize: 13,
                          }}
                        >
                          No story arcs found for "{arcSearch}"
                        </div>
                      )}
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.dim,
                        fontFamily: "monospace",
                        marginBottom: 12,
                      }}
                    >
                      DETECTED FROM LOADED ISSUES · SEARCH TO FIND MORE
                    </div>
                    <div className="story-arc-grid">
                      {arcBundles.map(function (arc) {
                        return (
                          <ArcCard
                            key={arc.name}
                            arcName={arc.name}
                            issues={arc.issues}
                            onClick={function () {
                              openLocalArcAsSiteTab(arc);
                            }}
                          />
                        );
                      })}
                      {arcBundles.length === 0 && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "32px 20px",
                            color: C.dim,
                            fontSize: 13,
                          }}
                        >
                          Arcs appear here as issues load. Search above to find
                          any arc directly.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "Characters" && (
          <div>
            {/* Search — debounced, hits CV API on typing */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid " + C.border,
                borderRadius: 12,
                padding: "9px 14px",
                marginBottom: 6,
              }}
            >
              <SearchIcon />
              <input
                value={charSearch}
                onChange={function (e) {
                  var q = e.target.value;
                  setCharSearch(q);
                  // Clear character detail when user starts a new search
                  if (q.length > 0) setCharIssues(null);
                  clearTimeout(charDebounceRef.current);
                  if (q.length >= 2) {
                    charDebounceRef.current = setTimeout(function () {
                      searchCharacters(q);
                    }, 500);
                  } else {
                    setCharResults([]);
                    setCharSearched("");
                  }
                }}
                placeholder="Search all DC characters…"
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: C.text,
                  fontSize: 13,
                  fontFamily: "inherit",
                }}
              />
              {charSearch && (
                <span
                  onClick={function () {
                    setCharSearch("");
                    setCharResults([]);
                    setCharSearched("");
                  }}
                  style={{ cursor: "pointer", color: C.dim }}
                >
                  <XIcon />
                </span>
              )}
            </div>
            {charSearch.length >= 2 && !charSearching && charSearched && (
              <div
                style={{
                  fontSize: 11,
                  color: C.dim,
                  fontFamily: "monospace",
                  marginBottom: 12,
                }}
              >
                {charResults.length} RESULTS FOR "{charSearch.toUpperCase()}"
              </div>
            )}
            {charSearching && <Spinner message="SEARCHING COMIC VINE…" />}

            {charError && (
              <div
                style={{
                  background: "rgba(239,68,68,.1)",
                  border: "1px solid rgba(239,68,68,.3)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontSize: 12,
                  color: "#f87171",
                  marginBottom: 12,
                }}
              >
                {charError}
              </div>
            )}

            {/* Character appearances detail view */}
            {charIssues ? (
              <div>
                <button
                  onClick={function () {
                    setCharIssues(null);
                    if (typeof window !== 'undefined' && window.history.state && window.history.state.char) {
                      window.history.back();
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "none",
                    border: "none",
                    color: C.muted,
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "0 0 14px",
                  }}
                >
                  <BackIcon /> All Characters
                </button>
                {(() => {
                  var heroImage = charIssues.heroImage && charIssues.heroImage.small_url ? charIssues.heroImage.small_url : (charIssues.heroImage || (charIssues.image && charIssues.image.small_url ? charIssues.image.small_url : charIssues.image));
                  return (
                    <div
                      style={{
                        position: "relative",
                        minHeight: 390,
                        borderRadius: 24,
                        overflow: "hidden",
                        border: "1px solid " + C.borderGold,
                        background: heroImage
                          ? "linear-gradient(90deg,rgba(5,5,12,.94),rgba(5,5,12,.54)), url('" + heroImage + "') " + (charIssues.heroPosition || "center center") + " / cover no-repeat"
                          : "linear-gradient(135deg,#1a1a38,#0d0d1a)",
                        boxShadow: "0 28px 80px rgba(0,0,0,.5)",
                        marginBottom: 18,
                        display: "flex",
                        alignItems: "flex-end",
                        padding: 26,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "linear-gradient(90deg,rgba(5,5,12,.96) 0%,rgba(5,5,12,.82) 46%,rgba(5,5,12,.18) 100%), linear-gradient(180deg,rgba(5,5,12,.08) 20%,rgba(5,5,12,.78) 100%)",
                        }}
                      />
                      <div style={{ position: "relative", zIndex: 1, maxWidth: 640 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.gold,
                            fontFamily: "monospace",
                            letterSpacing: 1.7,
                            textTransform: "uppercase",
                            marginBottom: 8,
                          }}
                        >
                          Character File
                        </div>
                        <div
                          style={{
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: "clamp(36px, 7vw, 72px)",
                            lineHeight: .95,
                            fontWeight: 800,
                            color: C.text,
                            marginBottom: 12,
                            textShadow: "0 4px 24px rgba(0,0,0,.8)",
                          }}
                        >
                          {charIssues.char}
                        </div>
                        {charIssues.deck && (
                          <div
                            style={{
                              fontSize: 16,
                              color: C.muted,
                              lineHeight: 1.6,
                              maxWidth: 700,
                              textShadow: "0 2px 12px rgba(0,0,0,.75)",
                            }}
                          >
                            {charIssues.deck}
                          </div>
                        )}
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 14,
                            padding: "7px 11px",
                            borderRadius: 999,
                            border: "1px solid rgba(212,160,23,.45)",
                            background: "rgba(13,13,27,.72)",
                            color: C.gold,
                            fontSize: 11,
                            fontFamily: "monospace",
                            letterSpacing: .6,
                          }}
                        >
                          {charLoading
                            ? "LOADING APPEARANCES…"
                            : charIssues.issues.length + " APPEARANCES"}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div
                  style={{
                    background: C.card,
                    border: "1px solid " + C.border,
                    borderRadius: 18,
                    padding: 18,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: C.text,
                        marginBottom: 4,
                      }}
                    >
                      {charIssues.char}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.gold,
                        fontFamily: "monospace",
                        marginBottom: 4,
                      }}
                    >
                      {charLoading
                        ? "LOADING APPEARANCES…"
                        : charIssues.issues.length + " APPEARANCES"}
                    </div>
                    {charIssues.deck && (
                      <div
                        style={{
                          fontSize: 12,
                          color: C.muted,
                          lineHeight: 1.6,
                        }}
                      >
                        {charIssues.deck}
                      </div>
                    )}
                  </div>
                </div>
                {charLoading && (
                  <Spinner
                    message={
                      "FETCHING " +
                      charIssues.char.toUpperCase() +
                      " APPEARANCES…"
                    }
                  />
                )}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {(charIssues.issues || []).map(function (c) {
                    return (
                      <ComicCard
                        key={c.id}
                        comic={c}
                        wishlisted={wishlist.indexOf(c.id) !== -1}
                        onWishlist={toggleWishlist}
                        owned={collection.indexOf(c.id) !== -1}
                        onOwn={toggleCollection}
                        onClick={function () {
                          openComic(c);
                        }}
                        onArcClick={function (arcName) {
                          setSelected(null);
                          setSelectedArc(null);
                          setArcIssues([]);
                          setTab("Story Arcs");
                          setArcSearch(arcName);
                          clearTimeout(arcDebounceRef.current);
                          searchStoryArcs(arcName);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                {/* Show search results if searching, otherwise curated list */}
                {charSearch.length >= 2 ? (
                  <div>
                    {!charSearching && charResults.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {charResults.map(function (c) {
                          return (
                            <div
                              key={c.id}
                              onClick={function () {
                                fetchCharIssues(c);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                background: C.card,
                                border: "1px solid " + C.border,
                                borderRadius: 12,
                                padding: "10px 14px",
                                cursor: "pointer",
                                transition: "all .15s",
                              }}
                              onMouseEnter={function (e) {
                                e.currentTarget.style.borderColor =
                                  C.borderGold;
                              }}
                              onMouseLeave={function (e) {
                                e.currentTarget.style.borderColor = C.border;
                              }}
                            >
                              {c.image && c.image.small_url ? (
                                <Cover
                                  src={c.image.small_url}
                                  w={36}
                                  h={50}
                                  r={4}
                                  fallbackText={c.name}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: "50%",
                                    background:
                                      "linear-gradient(135deg,#1a1a38,#0d0d1a)",
                                    border: "1px solid " + C.borderGold,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 15,
                                    color: C.gold,
                                    fontWeight: 700,
                                  }}
                                >
                                  {c.name[0]}
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: C.text,
                                  }}
                                >
                                  {c.name}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: C.muted,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {(
                                    c.count_of_issue_appearances || 0
                                  ).toLocaleString()}{" "}
                                  APPEARANCES
                                </div>
                                {c.deck && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: C.dim,
                                      marginTop: 2,
                                    }}
                                  >
                                    {c.deck.slice(0, 70)}
                                    {c.deck.length > 70 ? "…" : ""}
                                  </div>
                                )}
                              </div>
                              <div style={{ color: C.dim, fontSize: 20 }}>
                                ›
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {!charSearching &&
                      charSearched &&
                      charResults.length === 0 && (
                        <div
                          style={{
                            textAlign: "center",
                            padding: "32px 20px",
                            color: C.dim,
                            fontSize: 13,
                          }}
                        >
                          No DC characters found for "{charSearch}"
                        </div>
                      )}
                  </div>
                ) : (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.dim,
                        fontFamily: "monospace",
                        marginBottom: 12,
                      }}
                    >
                      POPULAR CHARACTERS · SEARCH TO FIND MORE
                    </div>
                    <div className="character-grid">
                      {BATMAN_CHARACTERS.map(function (c) {
                        var matchingIssues = issues.filter(function (issue) {
                          return (issue._characters || []).some(function (name) {
                            return namesReferToSameCharacter(name, c.name);
                          });
                        });
                        var previewImage = c.image || null;
                        var count = matchingIssues.length;
                        return (
                          <CharCard
                            key={c.name}
                            char={c.name}
                            deck={c.deck}
                            count={count}
                            image={previewImage}
                            imagePosition={c.imagePosition}
                            onClick={function () {
                              fetchCharIssues(c);
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === "Guide" && (
          <div>
            <div
              style={{
                fontSize: 11,
                color: C.dim,
                fontFamily: "monospace",
                marginBottom: 16,
              }}
            >
              WHERE TO START WITH BATMAN COMICS
            </div>
            <div
              style={{
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              New to Batman? These are the stories that define the character —
              picked for quality, accessibility, and impact. Start anywhere.
            </div>

            {/* ── TIER 1: Start Here ── */}
            <div
              style={{
                fontSize: 10,
                color: C.gold,
                fontFamily: "monospace",
                letterSpacing: 1,
                marginBottom: 10,
              }}
            >
              ★ START HERE
            </div>
            {[
              {
                title: "Batman: Year One",
                author: "Frank Miller & David Mazzucchelli",
                desc: "The definitive origin. A gritty, grounded retelling of Bruce Wayne's first year as Batman alongside a young Jim Gordon. Short, perfect, essential.",
                issues: "4 issues",
                difficulty: "Beginner",
                asin: "1401272444",
                color: C.gold,
              },
              {
                title: "Batman: The Long Halloween",
                author: "Jeph Loeb & Tim Sale",
                desc: "A noir murder mystery across a full year of Gotham. Introduces Two-Face's origin and the full rogues gallery. Inspired the Nolan films.",
                issues: "13 issues",
                difficulty: "Beginner",
                asin: "1779512694",
                color: C.gold,
              },
              {
                title: "Batman: Hush",
                author: "Jeph Loeb & Jim Lee",
                desc: "The perfect gateway story — every major villain, gorgeous art by Jim Lee, and a page-turning mystery. The most accessible introduction to the wider Batman universe.",
                issues: "12 issues",
                difficulty: "Beginner",
                asin: "1401223176",
                color: C.gold,
              },
            ].map(function (book) {
              return (
                <div
                  key={book.title}
                  style={{
                    background: C.card,
                    border: "1px solid " + C.borderGold,
                    borderRadius: 14,
                    padding: "14px 16px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: C.text,
                          marginBottom: 2,
                        }}
                      >
                        {book.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.purple,
                          marginBottom: 6,
                        }}
                      >
                        {book.author}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 4,
                        marginLeft: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.gold,
                          fontFamily: "monospace",
                          background: C.goldFaint,
                          padding: "2px 7px",
                          borderRadius: 10,
                        }}
                      >
                        {book.difficulty}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.muted,
                          fontFamily: "monospace",
                        }}
                      >
                        {book.issues}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.6,
                      marginBottom: 12,
                    }}
                  >
                    {book.desc}
                  </div>
                  <a
                    href={getAmazonHref(book.title, book.asin, "guide-reading-list")}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: "linear-gradient(135deg,#FF9900,#e68a00)",
                      color: "#000",
                      borderRadius: 8,
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    Buy on Amazon <ExtIcon />
                  </a>
                </div>
              );
            })}

            {/* ── TIER 2: Next Steps ── */}
            <div
              style={{
                fontSize: 10,
                color: C.purple,
                fontFamily: "monospace",
                letterSpacing: 1,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              ◆ NEXT STEPS
            </div>
            {[
              {
                title: "Batman: The Killing Joke",
                author: "Alan Moore & Brian Bolland",
                desc: "The definitive Joker story. Dark, brief, and unforgettable — explores what separates Batman from his greatest enemy. Read after Year One.",
                issues: "1 issue (48 pages)",
                difficulty: "Beginner",
                asin: "1401294057",
                color: C.purple,
              },
              {
                title: "Batman: Dark Victory",
                author: "Jeph Loeb & Tim Sale",
                desc: "The direct sequel to Long Halloween. Follows Two-Face's killing spree and introduces Dick Grayson as Robin. Even better than the first.",
                issues: "14 issues",
                difficulty: "Beginner",
                asin: "1401244017",
                color: C.purple,
              },
              {
                title: "Batman: Arkham Asylum",
                author: "Grant Morrison & Dave McKean",
                desc: "Batman enters a nightmarish, fully painted Arkham Asylum overrun by his rogues. Psychological, surreal, and unlike anything else in comics.",
                issues: "1 OGN (120 pages)",
                difficulty: "Intermediate",
                asin: "1779513178",
                color: C.purple,
              },
              {
                title: "Batman: Court of Owls",
                author: "Scott Snyder & Greg Capullo",
                desc: "The New 52 masterpiece. A secret society has controlled Gotham for centuries — and they want Batman dead. One of the greatest modern Batman runs.",
                issues: "11 issues",
                difficulty: "Beginner",
                asin: "1401235417",
                color: C.purple,
              },
            ].map(function (book) {
              return (
                <div
                  key={book.title}
                  style={{
                    background: C.card,
                    border: "1px solid rgba(167,139,250,0.2)",
                    borderRadius: 14,
                    padding: "14px 16px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: C.text,
                          marginBottom: 2,
                        }}
                      >
                        {book.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.purple,
                          marginBottom: 6,
                        }}
                      >
                        {book.author}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 4,
                        marginLeft: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.purple,
                          fontFamily: "monospace",
                          background: "rgba(167,139,250,0.1)",
                          padding: "2px 7px",
                          borderRadius: 10,
                        }}
                      >
                        {book.difficulty}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.muted,
                          fontFamily: "monospace",
                        }}
                      >
                        {book.issues}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.6,
                      marginBottom: 12,
                    }}
                  >
                    {book.desc}
                  </div>
                  <a
                    href={getAmazonHref(book.title, book.asin, "guide-reading-list")}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: "linear-gradient(135deg,#FF9900,#e68a00)",
                      color: "#000",
                      borderRadius: 8,
                      padding: "7px 14px",
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    Buy on Amazon <ExtIcon />
                  </a>
                </div>
              );
            })}

            {/* ── TIER 3: Go Deeper ── */}
            <div
              style={{
                fontSize: 10,
                color: C.muted,
                fontFamily: "monospace",
                letterSpacing: 1,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              ▸ GO DEEPER
            </div>
            {[
              {
                title: "Batman: Under the Red Hood",
                author: "Judd Winick & Doug Mahnke",
                desc: "A figure from Batman's past returns as a violent vigilante. Jason Todd's story, masterfully told. Best read after knowing who Robin is.",
                issues: "12 issues",
                difficulty: "Intermediate",
                asin: "1401231454",
              },
              {
                title: "Batman: Haunted Knight",
                author: "Jeph Loeb & Tim Sale",
                desc: "Three Halloween one-shots set before Long Halloween. Scarecrow, Mad Hatter, and Penguin each get a spotlight. Perfect seasonal reading.",
                issues: "3 issues",
                difficulty: "Beginner",
                asin: "1563892731",
              },
              {
                title: "Batman: White Knight",
                author: "Sean Murphy",
                desc: "The Joker is cured of his insanity and runs for office — exposing Batman as the real problem. A sharp, political take on the mythology.",
                issues: "8 issues",
                difficulty: "Intermediate",
                asin: "1401279597",
              },

              {
                title: "Absolute Batman",
                author: "Scott Snyder & Nick Dragotta",
                desc: "A bold 2024 reinvention: no money, no mansion, no Alfred. A working-class Bruce Wayne fights crime with self-built gear. The best-selling comic of 2024.",
                issues: "Ongoing (2024–)",
                difficulty: "Beginner",
                asin: null,
              },
            ].map(function (book) {
              return (
                <div
                  key={book.title}
                  style={{
                    background: C.card,
                    border: "1px solid " + C.border,
                    borderRadius: 14,
                    padding: "14px 16px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: C.text,
                          marginBottom: 2,
                        }}
                      >
                        {book.title}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.purple,
                          marginBottom: 6,
                        }}
                      >
                        {book.author}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 4,
                        marginLeft: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.muted,
                          fontFamily: "monospace",
                          background: "rgba(255,255,255,0.05)",
                          padding: "2px 7px",
                          borderRadius: 10,
                        }}
                      >
                        {book.difficulty}
                      </div>
                      <div
                        style={{
                          fontSize: 9,
                          color: C.muted,
                          fontFamily: "monospace",
                        }}
                      >
                        {book.issues}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.6,
                      marginBottom: 12,
                    }}
                  >
                    {book.desc}
                  </div>
                  {book.asin ? (
                    <a
                      href={getAmazonHref(book.title, book.asin, "guide-movie-branch")}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        background: "linear-gradient(135deg,#FF9900,#e68a00)",
                        color: "#000",
                        borderRadius: 8,
                        padding: "7px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      Buy on Amazon <ExtIcon />
                    </a>
                  ) : (
                    <div
                      style={{
                        fontSize: 11,
                        color: C.gold,
                        fontFamily: "monospace",
                      }}
                    >
                      ★ FIND IN BROWSE TAB
                    </div>
                  )}
                </div>
              );
            })}

            <div
              style={{
                fontSize: 11,
                color: C.dim,
                textAlign: "center",
                padding: "20px 0 8px",
                fontFamily: "monospace",
              }}
            >
              ALL ISSUES AVAILABLE IN THE BROWSE TAB ↑
            </div>
          </div>
        )}

        {tab === "Collection" && (
          <div>
            {/* Owned Section */}
            <div style={{ marginBottom: 24 }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: "1px solid " + C.border
              }}>
                <span style={{ fontSize: 14, color: "#22c55e" }}>✓</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>OWNED</span>
                <span style={{ fontSize: 11, color: C.dim, fontFamily: "monospace" }}>
                  {collectionIssues.length} ISSUE{collectionIssues.length!==1?"S":""}
                </span>
              </div>
              {collectionIssues.length === 0 ? (
                <div style={{ textAlign:"center", padding:"30px 20px", color:C.dim }}>
                  <div style={{ fontSize:14 }}>Tap the checkmark on any comic to mark it as owned</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {collectionIssues.map(function(c) {
                    return <ComicCard key={c.id} comic={c} wishlisted={wishlist.indexOf(c.id)!==-1} onWishlist={toggleWishlist} owned={true} onOwn={toggleCollection} onClick={function() { openComic(c); }} />;
                  })}
                </div>
              )}
            </div>

            {/* Wishlist Section */}
            <div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: "1px solid " + C.border
              }}>
                <span style={{ fontSize: 14, color: C.gold }}>★</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>WISHLIST</span>
                <span style={{ fontSize: 11, color: C.dim, fontFamily: "monospace" }}>
                  {wishlistIssues.length} ISSUE{wishlistIssues.length!==1?"S":""}
                </span>
              </div>
              {wishlistIssues.length === 0 ? (
                <div style={{ textAlign:"center", padding:"30px 20px", color:C.dim }}>
                  <div style={{ fontSize:14 }}>Tap the star on any comic to add it to your wishlist</div>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {wishlistIssues.map(function(c) {
                    return <ComicCard key={c.id} comic={c} wishlisted={true} onWishlist={toggleWishlist} owned={collection.indexOf(c.id)!==-1} onOwn={toggleCollection} onClick={function() { openComic(c); }} />;
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "Wishlist_OLD" && (
          <div>
            <div
              style={{
                fontSize: 11,
                color: C.dim,
                fontFamily: "monospace",
                marginBottom: 14,
              }}
            >
              {wishlistIssues.length} SAVED ISSUE
              {wishlistIssues.length !== 1 ? "S" : ""}
            </div>
            {wishlistIssues.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 20px",
                  color: C.dim,
                }}
              >
                <div
                  style={{
                    fontSize: 36,
                    marginBottom: 10,
                    color: C.gold + "33",
                  }}
                >
                  ★
                </div>
                <div style={{ fontSize: 14 }}>
                  Tap the star on any comic to save it here
                </div>
              </div>
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {wishlistIssues.map(function (c) {
                  return (
                    <ComicCard
                      key={c.id}
                      comic={c}
                      wishlisted={true}
                      onWishlist={toggleWishlist}
                      owned={collection.indexOf(c.id) !== -1}
                      onOwn={toggleCollection}
                      onClick={function () {
                        openComic(c);
                      }}
                      onArcClick={function (arcName) {
                        setSelected(null);
                        setSelectedArc(null);
                        setArcIssues([]);
                        setTab("Story Arcs");
                        setArcSearch(arcName);
                        clearTimeout(arcDebounceRef.current);
                        searchStoryArcs(arcName);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

        {tab === "About" && (
          <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 8px", boxSizing: "border-box" }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                About Gotham Vault
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                An unofficial fan-built Batman comics guide for browsing issues, following story arcs, exploring characters, and tracking what you own or want next.
              </div>
            </div>
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: 18, marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginBottom: 8 }}>What it is</div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
                Gotham Vault is a curated comics database focused on Batman and Gotham-related stories. Comic details are based on real DC Comics publications, with curated metadata and reading paths to make the history easier to navigate.
              </div>
            </div>
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: 18, marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginBottom: 8 }}>Disclaimer</div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
                This is an unofficial fan project. Batman, related characters, stories, and imagery are property of DC Comics. Data may include curated summaries, estimated dates, and placeholder images.
              </div>
            </div>
            <div style={{ background: "rgba(255,214,50,0.05)", border: "1px dashed " + C.borderGold, borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginBottom: 8 }}>Install the app</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>
                iPhone: Share → Add to Home Screen<br />Android: Menu → Add to Home Screen
              </div>
            </div>
            {renderBugReportCard("About")}
          </div>
        )}

        {tab === "FAQ" && (
          <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 8px", boxSizing: "border-box" }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                FAQ
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                Quick answers for using Gotham Vault.
              </div>
            </div>
            {[
              ["Do I need an account?", "No. You can browse without one. An account lets your collection, wishlist, and achievements carry across devices."],
              ["What does the checkmark do?", "The checkmark marks an issue as owned and adds it to your collection."],
              ["What does the star do?", "The star saves an issue to your wishlist."],
              ["Why are some achievements locked?", "Some achievements track milestones that are live now; others are planned hooks for richer activity tracking."],
              ["Where does the comic data come from?", "The site uses curated Batman-related data and cached Comic Vine-style metadata."],
              ["Is this official DC?", "No. Gotham Vault is an unofficial fan project."],
            ].map(function(item) {
              return (
                <div key={item[0]} style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 14, padding: 16, marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.gold, marginBottom: 6 }}>{item[0]}</div>
                  <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>{item[1]}</div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "Blog" && (
          <div style={{ padding: "0 8px", boxSizing: "border-box" }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                Gotham Vault Blog
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                Deep dives into Batman comics, character spotlights, reading guides, and analysis of the Dark Knight's greatest stories.
              </div>
            </div>
            
            {/* Blog Posts */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {[
                {
                  id: "king-tut-william-mcelroy-gotham-pharaoh",
                  title: "King Tut Explained — William McElroy Makes Gotham His Ancient Kingdom",
                  category: "Villain Spotlights",
                  date: "August 26, 2026",
                  excerpt: "Professor William McElroy's royal delusion transforms a mild Egyptologist into King Tut, a theatrical rogue who attacks modern Gotham with pharaonic traps, riddles, and absolute ancient authority."
                },
                {
                  id: "polka-dot-man-abner-krill-gotham-villain",
                  title: "Polka-Dot Man Explained — Abner Krill Turns Gotham Into a Deadly Pattern",
                  category: "Villain Spotlights",
                  date: "August 26, 2026",
                  excerpt: "Abner Krill transformed a field of costume dots into a portable arsenal, becoming Polka-Dot Man: a gloriously strange Silver Age rogue whose visual gimmick concealed real tactical versatility."
                },
                {
                  id: "black-spider-eric-needham-gotham-vigilante",
                  title: "Black Spider Explained — Eric Needham's Deadly War on Gotham's Drug Trade",
                  category: "Villain Spotlights",
                  date: "August 25, 2026",
                  excerpt: "Eric Needham hunts Gotham's drug dealers with acrobatics, guns, and lethal certainty, making Black Spider a dark reflection of Batman whose crusade confuses vengeance with justice."
                },
                {
                  id: "doctor-death-karl-hellfern-batman-first-supervillain",
                  title: "Doctor Death Explained — Karl Hellfern, Batman's First Recurring Supervillain",
                  category: "Villain Spotlights",
                  date: "August 25, 2026",
                  excerpt: "Doctor Karl Hellfern brought lethal chemistry, a skeletal face, and serialized menace to Batman's earliest cases, becoming the Dark Knight's first villain to return for a rematch."
                },
                {
                  id: "the-spook-val-kaliban-gotham-escape-artist-villain",
                  title: "The Spook Explained — Val Kaliban, Gotham's Ghostly Escape Artist",
                  category: "Villain Spotlights",
                  date: "August 25, 2026",
                  excerpt: "Val Kaliban built the Spook into a seemingly supernatural criminal identity, using escape artistry, staged hauntings, and a hidden mansion to make Batman chase a ghost who always stayed one locked door ahead."
                },
                {
                  id: "ventriloquist-scarface-arnold-wesker-gotham-crime-boss",
                  title: "The Ventriloquist and Scarface Explained — Gotham's Puppet Crime Boss",
                  category: "Villain Spotlights",
                  date: "August 24, 2026",
                  excerpt: "Arnold Wesker looks like Gotham's meekest bystander, but through Scarface he becomes a ruthless underworld boss whose wooden face gives buried rage a voice, a gun, and a criminal empire."
                },
                {
                  id: "tweedledee-tweedledum-gotham-criminal-cousins",
                  title: "Tweedledee and Tweedledum Explained — Gotham's Mirror-Image Masterminds",
                  category: "Villain Spotlights",
                  date: "August 24, 2026",
                  excerpt: "Dumfree and Deever Tweed turned their uncanny resemblance, synchronized schemes, and Wonderland theatrics into a shared criminal identity that has confused Batman and divided Gotham's underworld for generations."
                },
                {
                  id: "bat-mite-fifth-dimensional-batman-fan",
                  title: "Bat-Mite Explained — Batman's Biggest Fan Is Gotham's Strangest Problem",
                  category: "Character Spotlights",
                  date: "August 24, 2026",
                  excerpt: "Bat-Mite is a reality-bending visitor from the Fifth Dimension whose admiration for Batman turns ordinary cases into impossible spectacles, making him equal parts superfan, nuisance, and commentary on the Dark Knight's changing eras."
                },
                {
                  id: "crime-doctor-matthew-thorne-gotham-underworld-surgeon",
                  title: "Crime Doctor Explained — Matthew Thorne Prescribes Silence for Gotham's Underworld",
                  category: "Villain Spotlights",
                  date: "August 23, 2026",
                  excerpt: "Dr. Matthew Thorne hides a ruthless criminal practice behind a physician's respectability, treating wounded mobsters, protecting their secrets, and turning medical trust into one of Gotham's most unsettling underworld weapons."
                },
                {
                  id: "electrocutioner-lester-buchinsky-gotham-villain",
                  title: "Electrocutioner Explained — Lester Buchinsky Turns Gotham's Justice Into a Live Wire",
                  category: "Villain Spotlights",
                  date: "August 23, 2026",
                  excerpt: "Lester Buchinsky began as a self-appointed executioner targeting criminals the courts could not hold, then became a voltage-armed mercenary whose warped idea of justice repeatedly put him in Batman's path."
                },
                {
                  id: "kite-man-charles-brown-gotham-villain",
                  title: "Kite Man Explained — Charles Brown Flies Above Gotham's Punchlines",
                  category: "Villain Spotlights",
                  date: "August 23, 2026",
                  excerpt: "Charles Brown turned kites, gliders, and an unshakable taste for theatrical crime into the Kite Man identity, evolving from a Silver Age punchline into one of Gotham's most unexpectedly human rogues."
                },
                {
                  id: "humpty-dumpty-humphry-dumpler-gotham-villain",
                  title: "Humpty Dumpty Explained — Humphry Dumpler Tries to Put Gotham Back Together",
                  category: "Villain Spotlights",
                  date: "August 22, 2026",
                  excerpt: "Humphry Dumpler is a gifted repairman whose compulsion to take broken things apart turns horrifying when he decides Gotham's machines, institutions, and even people must be dismantled before they can be fixed."
                },
                {
                  id: "orca-grace-balin-gotham-villain",
                  title: "Orca Explained — Grace Balin Brings Gotham's Rage From the Deep",
                  category: "Villain Spotlights",
                  date: "August 22, 2026",
                  excerpt: "Dr. Grace Balin used experimental spinal-cord research to walk again, only to transform into the superhuman Orca and wage a furious campaign against the wealthy people she blamed for Gotham's suffering."
                },
                {
                  id: "magpie-margaret-pye-gotham-jewel-thief",
                  title: "Magpie Explained — Margaret Pye Steals Gotham's Brightest Treasures",
                  category: "Villain Spotlights",
                  date: "August 22, 2026",
                  excerpt: "Margaret Pye turned her obsession with glittering objects into the Magpie identity, stealing priceless jewels and replacing them with deadly replicas that make every Gotham display case a potential trap."
                },
                {
                  id: "condiment-king-mitchell-mayo-gotham-villain",
                  title: "Condiment King Explained — Gotham's Joke Villain With a Surprisingly Sharp Bite",
                  category: "Villain Spotlights",
                  date: "August 21, 2026",
                  excerpt: "Condiment King began as an ordinary comedian brainwashed into a ridiculous crime spree, but later comics turned the sauce-slinging identity into a stubborn reminder that even Gotham's punchline villains can be dangerous."
                },
                {
                  id: "doctor-double-x-simon-ecks-energy-duplicate-gotham",
                  title: "Doctor Double X Explained — Simon Ecks Gives His Darkest Impulses a Body",
                  category: "Villain Spotlights",
                  date: "August 21, 2026",
                  excerpt: "Dr. Simon Ecks discovered how to project a living energy duplicate of himself, creating Doctor Double X — a superhuman second self that turns one Gotham scientist's ambition and buried cruelty into a physical threat."
                },
                {
                  id: "cavalier-mortimer-drake-gotham-swashbuckler-villain",
                  title: "The Cavalier Explained — Mortimer Drake Makes Crime a Gotham Spectacle",
                  category: "Villain Spotlights",
                  date: "August 21, 2026",
                  excerpt: "Mortimer Drake turned a fortune, a flair for swordplay, and an obsession with theatrical adventure into the Cavalier, a flamboyant rogue who treats every Gotham robbery like the climax of a swashbuckling film."
                },
                {
                  id: "crazy-quilt-paul-dekker-color-gotham-villain",
                  title: "Crazy Quilt Explained — Paul Dekker Turns Color Into a Weapon Against Batman",
                  category: "Villain Spotlights",
                  date: "August 20, 2026",
                  excerpt: "Paul Dekker was a criminal mastermind and painter whose damaged sight could perceive only searing color, inspiring the light-powered helmet and hypnotic weapons of one of Gotham's most visually distinctive villains."
                },
                {
                  id: "ten-eyed-man-philip-reardon-gotham-villain",
                  title: "Ten-Eyed Man Explained — Philip Reardon Sees Gotham Through His Fingertips",
                  category: "Villain Spotlights",
                  date: "August 20, 2026",
                  excerpt: "Philip Reardon lost his sight in a warehouse explosion, only for experimental surgery to connect his optic nerves to his fingertips and turn a traumatized veteran into one of Batman's strangest sensory foes."
                },
                {
                  id: "cluemaster-arthur-brown-gotham-puzzle-villain",
                  title: "Cluemaster Explained — Arthur Brown Leaves Gotham a Trail Batman Cannot Ignore",
                  category: "Villain Spotlights",
                  date: "August 19, 2026",
                  excerpt: "Arthur Brown built the Cluemaster identity around elaborate crimes and compulsory clues, becoming a dangerous Gotham schemer whose greatest legacy is also his daughter, Stephanie Brown."
                },
                {
                  id: "zebra-man-jacob-baker-magnetic-gotham-villain",
                  title: "Zebra-Man Explained — Jacob Baker's Magnetic Crime Wave in Gotham",
                  category: "Villain Spotlights",
                  date: "August 19, 2026",
                  excerpt: "Jacob Baker became Zebra-Man after experimental radiation gave him control over attraction and repulsion, turning a striped Silver Age oddity into a villain who can hurl Batman aside or make escape physically impossible."
                },
                {
                  id: "amygdala-aaron-helzinger-gotham-villain",
                  title: "Amygdala Explained — Aaron Helzinger's Rage and Tragedy in Gotham",
                  category: "Villain Spotlights",
                  date: "August 18, 2026",
                  excerpt: "Aaron Helzinger became Amygdala after experimental brain surgery left him unable to regulate his emotions, turning an immensely strong Arkham inmate into one of Gotham's most tragic and dangerously misunderstood figures."
                },
                {
                  id: "great-white-shark-warren-white-gotham-crime-boss",
                  title: "Great White Shark Explained — How Warren White Became Arkham's Ruthless Crime Boss",
                  category: "Villain Spotlights",
                  date: "August 18, 2026",
                  excerpt: "Warren White entered Arkham as a corrupt financier trying to escape prison and emerged as the Great White Shark, a frost-scarred strategist who learned to rule Gotham's underworld from inside the asylum."
                },
                {
                  id: "jane-doe-arkham-identity-thief-gotham",
                  title: "Jane Doe Explained — Arkham's Identity Thief Who Wears Her Victims' Lives",
                  category: "Villain Spotlights",
                  date: "August 18, 2026",
                  excerpt: "Jane Doe studies her victims until she can steal more than their appearance, turning identity itself into a Gotham crime scene and forcing Batman to find the person hiding beneath a perfectly rehearsed life."
                },
                {
                  id: "lock-up-lyle-bolton-gotham-vigilante-jailer",
                  title: "Lock-Up Explained — Lyle Bolton Thinks Gotham Needs a Crueler Jailer",
                  category: "Villain Spotlights",
                  date: "August 17, 2026",
                  excerpt: "Lyle Bolton turns his obsession with punishment into the identity of Lock-Up, imprisoning everyone he judges responsible for Gotham's disorder and forcing Batman to defend justice from a vigilante without mercy."
                },
                {
                  id: "cornelius-stirk-gotham-fear-vampire",
                  title: "Cornelius Stirk Explained — Gotham's Psychic Predator Who Feeds on Fear",
                  category: "Villain Spotlights",
                  date: "August 16, 2026",
                  excerpt: "Cornelius Stirk uses psychic illusions to disguise himself, terrify his victims, and hunt Gotham from the margins — a grotesque predator whose crimes turn fear itself into camouflage and sustenance."
                },
                {
                  id: "professor-pyg-lazlo-valentin-gotham-perfection",
                  title: "Professor Pyg Explained — Lazlo Valentin's Horrifying Pursuit of Perfection in Gotham",
                  category: "Villain Spotlights",
                  date: "August 16, 2026",
                  excerpt: "Professor Pyg turns Gotham into a grotesque operating theater, using Dollotrons, masks, and mind control to remake unwilling victims according to Lazlo Valentin's warped idea of human perfection."
                },
                {
                  id: "firefly-garfield-lynns-gotham-arsonist",
                  title: "Firefly Explained — Garfield Lynns Turns Gotham Into a Burning Stage",
                  category: "Villain Spotlights",
                  date: "August 16, 2026",
                  excerpt: "Garfield Lynns evolved from a theatrical criminal who weaponized light into Gotham's obsessive master of arson, using a winged battlesuit and flamethrowers to make every blaze feel like a performance staged for Batman."
                },
                {
                  id: "killer-moth-cameron-van-cleer-batman-villain",
                  title: "Killer Moth Explained — The Anti-Batman Who Built a Career Protecting Gotham's Criminals",
                  category: "Villain Spotlights",
                  date: "August 15, 2026",
                  excerpt: "Killer Moth began as Batman's deliberate opposite: a costumed professional who offered Gotham's criminals the same protection the Dark Knight gave ordinary citizens, complete with a Moth-Signal and a secret identity financed by crime."
                },
                {
                  id: "slam-bradley-gotham-private-detective",
                  title: "Slam Bradley Explained — The Private Detective Who Walked Gotham Before Batman",
                  category: "Gotham Detectives & History",
                  date: "August 14, 2026",
                  excerpt: "Slam Bradley brought hard-boiled detective fiction to the first issue of Detective Comics, then found a second life in Gotham as the stubborn private eye who investigates the cases lurking beyond Batman's shadow."
                },
                {
                  id: "hamilton-hill-gotham-mayor-political-corruption",
                  title: "Hamilton Hill Explained — The Mayor Who Turned Gotham City Hall Against Batman",
                  category: "Gotham Politics & Corruption",
                  date: "August 14, 2026",
                  excerpt: "Mayor Hamilton Hill shows how Gotham can threaten Batman without a supervillain: by placing public authority, police pressure, and political ambition in the service of men who want the Dark Knight erased."
                },
                {
                  id: "vicki-vale-gotham-journalist-batman-secret",
                  title: "Vicki Vale Explained — The Gotham Reporter Who Keeps Chasing Batman's Secret",
                  category: "Gotham Citizens & Journalism",
                  date: "August 14, 2026",
                  excerpt: "Vicki Vale turns Gotham journalism into detective work, pursuing the truth behind Batman while forcing Bruce Wayne to confront how much of his life can survive honest scrutiny."
                },
                {
                  id: "rupert-thorne-gotham-corruption-crime-boss",
                  title: "Rupert Thorne Explained — The Crime Boss Who Made Gotham City Hall His Weapon",
                  category: "Gotham Crime & Corruption",
                  date: "August 13, 2026",
                  excerpt: "Rupert Thorne rarely needs a costume to threaten Batman: his power comes from turning money, political influence, and Gotham's compromised institutions into protection for an old-fashioned criminal empire."
                },
                {
                  id: "maxie-zeus-gotham-villain-arkham",
                  title: "Maxie Zeus Explained — Gotham's Crime Lord Who Believes He Is a God",
                  category: "Villain Spotlights",
                  date: "August 13, 2026",
                  excerpt: "Maxie Zeus turns classical mythology into criminal doctrine, ruling Gotham's underworld from a private Olympus where delusion, theatrical power, and very real violence become impossible to separate."
                },
                {
                  id: "maggie-sawyer-gcpd-commissioner-gotham",
                  title: "Maggie Sawyer Explained — The Metropolis Cop Who Took Command in Gotham",
                  category: "GCPD & Gotham Allies",
                  date: "August 12, 2026",
                  excerpt: "Maggie Sawyer built her reputation policing super-crime in Metropolis before bringing that experience to Gotham, where her command of the GCPD tests what honest leadership looks like in a city already claimed by the Bat-Signal."
                },
                {
                  id: "sarah-essen-gordon-gcpd-commissioner-gotham",
                  title: "Sarah Essen Explained — The GCPD Commissioner Gotham Nearly Forgot",
                  category: "GCPD & Gotham Allies",
                  date: "August 12, 2026",
                  excerpt: "Sarah Essen is more than Jim Gordon's partner and second wife: she is a gifted detective, a wartime commissioner, and one of the clearest examples of the personal cost paid by the honest officers who try to hold Gotham together."
                },
                {
                  id: "crispus-allen-gcpd-detective-spectre-gotham",
                  title: "Crispus Allen Explained — From Gotham Detective to the Spectre",
                  category: "GCPD & Gotham's Supernatural",
                  date: "August 12, 2026",
                  excerpt: "Crispus Allen begins as one of Gotham Central's most principled detectives and ends as the host of divine vengeance, a transformation that turns one honest cop's murder into a reckoning with justice, judgment, and mercy."
                },
                {
                  id: "harvey-bullock-gotham-detective-gordon-ally",
                  title: "Harvey Bullock Explained — Gotham's Roughest Good Cop",
                  category: "GCPD & Gotham Allies",
                  date: "August 11, 2026",
                  excerpt: "Harvey Bullock is loud, abrasive, and willing to bend rules, yet beneath the rumpled coat is one of Jim Gordon's most dependable detectives — a cop whose flaws make his loyalty to Gotham feel earned rather than easy."
                },
                {
                  id: "aaron-cash-arkham-asylum-guard-gotham",
                  title: "Aaron Cash Explained — The Arkham Guard Who Keeps Going Back",
                  category: "Arkham Asylum & Gotham Survivors",
                  date: "August 11, 2026",
                  excerpt: "Aaron Cash stands between Arkham Asylum's staff and its most dangerous inmates with little more than experience, nerve, and a prosthetic hook — proving that survival in Gotham can be its own kind of heroism."
                },
                {
                  id: "leslie-thompkins-gotham-clinic-bruce-wayne",
                  title: "Leslie Thompkins Explained — The Doctor Who Heals Gotham Without a Mask",
                  category: "Gotham Allies & Moral Voices",
                  date: "August 11, 2026",
                  excerpt: "Leslie Thompkins has spent decades treating the people Gotham overlooks, challenging Bruce Wayne to measure his mission not only by criminals stopped, but by wounded lives restored without fear or violence."
                },
                {
                  id: "calendar-man-julian-day-gotham-dates",
                  title: "Calendar Man Explained — Julian Day Makes Every Date in Gotham a Threat",
                  category: "Villain Spotlights",
                  date: "August 10, 2026",
                  excerpt: "Julian Day turns holidays, anniversaries, and seasons into criminal signatures, proving that one of Gotham's strangest villains becomes most dangerous when the entire calendar feels like evidence only he can read."
                },
                {
                  id: "black-mask-roman-sionis-gotham-crime-boss",
                  title: "Black Mask Explained — Roman Sionis Turns Gotham's Obsession With Masks Into a Crime Empire",
                  category: "Villain Spotlights",
                  date: "August 10, 2026",
                  excerpt: "Roman Sionis makes Gotham's favorite symbol into something cruel: a black skull, a criminal identity, and an empire built on the belief that every respectable face in the city is only another disguise."
                },
                {
                  id: "batman-last-rites-bruce-wayne-final-crisis",
                  title: "Batman: Last Rites Explained — Bruce Wayne Faces the End Before Final Crisis",
                  category: "Story Arc Deep Dives",
                  date: "August 9, 2026",
                  excerpt: "Batman: Last Rites traps Bruce Wayne inside a weaponized tour of his own history, using memory, identity, and the Black Glove's final assault to bridge Batman R.I.P. and Final Crisis."
                },
                {
                  id: "batman-faces-two-face-island-matt-wagner",
                  title: "Batman: Faces Explained — Two-Face Builds a Kingdom for Gotham's Outcasts",
                  category: "Two-Face & Gotham Tragedies",
                  date: "August 9, 2026",
                  excerpt: "Batman: Faces pits the Dark Knight against Two-Face's plan to create an island nation for people society has rejected, turning Harvey Dent's scarred sense of justice into a seductive promise of belonging built on abduction, murder, and control."
                },
                {
                  id: "batman-jokers-last-laugh-gotham-pandemic",
                  title: "Batman: Joker's Last Laugh Explained — When Gotham's Chaos Goes Global",
                  category: "Joker & Gotham Crossovers",
                  date: "August 9, 2026",
                  excerpt: "Joker's Last Laugh turns a false terminal diagnosis into a worldwide crisis, as the Clown Prince infects supervillains with his signature venom and forces Batman's allies to confront what happens when Gotham's most dangerous idea escapes the city."
                },
                {
                  id: "joker-killer-smile-psychological-horror-gotham",
                  title: "Joker: Killer Smile Explained — Gotham's Most Dangerous Patient",
                  category: "Joker & Psychological Horror",
                  date: "August 8, 2026",
                  excerpt: "Joker: Killer Smile traps psychiatrist Ben Arnell inside a therapeutic relationship the Joker has already turned into a weapon, asking whether anyone can study Gotham's most dangerous patient without carrying his chaos home."
                },
                {
                  id: "batman-one-bad-day-rogues-gallery-spotlight",
                  title: "Batman: One Bad Day Explained — Gotham's Rogues at Their Most Human",
                  category: "Villain Spotlights",
                  date: "August 8, 2026",
                  excerpt: "Batman: One Bad Day is a 2022–2023 anthology of prestige one-shots testing the Joker's Killing Joke thesis against eight members of Gotham's rogues gallery — the Riddler, Two-Face, Penguin, Bane, Ra's al Ghul, Mr. Freeze, Catwoman, and more — each stripped to their core by a single catastrophic day."
                },
                {
                  id: "batman-catwoman-tom-king-love-gotham",
                  title: "Batman/Catwoman Explained — Tom King's Love Story at the End of Gotham",
                  category: "Romance, Tragedy & Gotham Myth",
                  date: "August 7, 2026",
                  excerpt: "Batman/Catwoman is Tom King and Clay Mann's twelve-issue maxi-series told across three timelines — past, present, and a haunting future — asking whether Bruce Wayne and Selina Kyle can build a life together when the mission, the Joker, and a lifetime of choices stand between them."
                },
                {
                  id: "batman-and-robin-must-die-morrison-gotham-finale",
                  title: "Batman and Robin: Must Die Explained — Grant Morrison's Dark Knight Comes Home",
                  category: "Story Arc Deep Dives",
                  date: "August 7, 2026",
                  excerpt: "Batman and Robin: Must Die brings Morrison's entire Batman project to a head as Dick Grayson's Gotham faces Doctor Hurt's final gambit, the Joker's chaos, and the imminent return of Bruce Wayne — testing whether the symbol is larger than any single person who carries it."
                },
                {
                  id: "batman-bane-of-the-demon-ras-al-ghul-partnership",
                  title: "Batman: Bane of the Demon Explained — Bane Fights Alongside Ra's al Ghul",
                  category: "Villain Spotlights",
                  date: "August 7, 2026",
                  excerpt: "Batman: Bane of the Demon tests a dangerous alliance between two of Gotham's most formidable enemies, sending Bane to serve Ra's al Ghul's global campaign — and asking whether a man defined by dominance can ever truly follow another's command."
                },
                {
                  id: "batman-gotham-knights-bat-family-legacy-gotham",
                  title: "Batman: Gotham Knights Explained — The Bat-Family Holds Gotham Together",
                  category: "Bat-Family & Gotham Legacy",
                  date: "August 6, 2026",
                  excerpt: "Batman: Gotham Knights ran for seventy-four issues as a companion title focused on the extended Bat-Family, giving Tim Drake, Alfred, Commissioner Gordon, and Bruce's wider network room to define themselves beyond the shadow of the cowl."
                },
                {
                  id: "batman-gordons-law-jim-gordon-undercover-gotham",
                  title: "Batman: Gordon's Law Explained — Jim Gordon Goes Undercover in Gotham",
                  category: "Jim Gordon & Gotham Law",
                  date: "August 6, 2026",
                  excerpt: "Batman: Gordon's Law strips Jim Gordon of his badge and sends him into Gotham's criminal underworld to expose the corruption that official channels refuse to touch — a rare spotlight on the Commissioner as the story's driving lead."
                },
                {
                  id: "batman-tao-gotham-martial-arts-mystery",
                  title: "Batman: Tao Explained — Gotham Tests the Philosophy Behind the Fist",
                  category: "Batman Martial Arts & Philosophy",
                  date: "August 6, 2026",
                  excerpt: "Batman: Tao sends Bruce Wayne into a murder mystery shaped by martial discipline, ancient philosophy, and the uncomfortable gap between mastering combat and understanding the purpose behind it."
                },
                {
                  id: "batman-rules-of-engagement-lex-luthor-gotham-arms",
                  title: "Batman: Rules of Engagement Explained — Lex Luthor Brings a Weapons War to Gotham",
                  category: "Batman, Gotham & Lex Luthor",
                  date: "August 5, 2026",
                  excerpt: "Batman: Rules of Engagement pits Bruce Wayne against Lex Luthor’s military technology, turning Gotham into a proving ground for weapons, corporate power, and the line Batman refuses to cross."
                },
                {
                  id: "batman-detective-death-and-the-city-paul-dini",
                  title: "Batman: Detective — Death and the City Explained — Paul Dini Returns Gotham to the Case File",
                  category: "Detective Case Files",
                  date: "August 5, 2026",
                  excerpt: "Batman: Detective — Death and the City collects Paul Dini's early Detective Comics mysteries, where the Riddler, Poison Ivy, the Penguin, and Gotham's quieter crimes put Bruce Wayne's observation and judgment ahead of crossover spectacle."
                },
                {
                  id: "batman-prey-hugo-strange-gotham-manhunt",
                  title: "Batman: Prey Explained — Hugo Strange Turns Gotham Against the Dark Knight",
                  category: "Hugo Strange & Early Batman",
                  date: "August 4, 2026",
                  excerpt: "Batman: Prey pits an early-career Dark Knight against Hugo Strange, whose psychological campaign, police influence, and manufactured vigilante turn Gotham itself into a trap for the man behind the mask."
                },
                {
                  id: "batman-the-destroyer-gotham-architecture",
                  title: "Batman: The Destroyer Explained — The Secret Architect Behind Gotham City",
                  category: "Gotham History & Architecture",
                  date: "August 4, 2026",
                  excerpt: "Batman: The Destroyer uncovers the buried work of architect Cyrus Pinkney, turning Gotham's famous skyline into the evidence at the center of a conflict over history, ownership, and urban memory."
                },
                {
                  id: "batman-bruce-wayne-fugitive-identity-on-trial",
                  title: "Batman: Bruce Wayne — Fugitive Explained — When the Dark Knight Abandons His Own Name",
                  category: "Story Arc Deep Dives",
                  date: "August 4, 2026",
                  excerpt: "Bruce Wayne: Fugitive sends Gotham's most famous citizen on the run and pushes Batman toward a dangerous conclusion: that Bruce Wayne was only ever a disguise he can finally discard."
                },
                {
                  id: "batman-ring-arrow-bat-ras-al-ghul-history",
                  title: "Batman: The Ring, the Arrow and the Bat Explained — Bruce Wayne and Oliver Queen Face Ra’s al Ghul",
                  category: "Batman Team-Ups & Hidden History",
                  date: "August 3, 2026",
                  excerpt: "The Ring, the Arrow and the Bat reaches into Bruce Wayne and Oliver Queen’s past, revealing how an early encounter with Ra’s al Ghul helped shape two very different heroes before Batman and Green Arrow fully claimed their legends."
                },
                {
                  id: "batman-the-black-glove-club-of-heroes-mystery",
                  title: "Batman: The Black Glove Explained — The Club of Heroes Enters a Murder Trap",
                  category: "Story Arc Deep Dives",
                  date: "August 3, 2026",
                  excerpt: "Batman: The Black Glove reunites the international Club of Heroes on a storm-bound island, turning a nostalgic gathering into a locked-room mystery designed to expose the fears beneath every heroic identity."
                },
                {
                  id: "batman-orpheus-rising-gavin-king-gotham-undercover",
                  title: "Batman: Orpheus Rising Explained — Gavin King Goes Undercover in Gotham",
                  category: "Character & Legacy Spotlights",
                  date: "August 3, 2026",
                  excerpt: "Batman: Orpheus Rising introduces Gavin King, an idealistic young operative who builds the identity of Orpheus to infiltrate Gotham's criminal elite—and discovers how much a convincing mask can cost its wearer."
                },
                {
                  id: "batman-cold-days-mr-freeze-jury-trial",
                  title: "Batman: Cold Days Explained — Bruce Wayne Puts the Dark Knight on Trial",
                  category: "Detective & Moral Dilemmas",
                  date: "August 2, 2026",
                  excerpt: "Batman: Cold Days places Bruce Wayne on the jury in Mr. Freeze's murder trial, forcing him to examine whether Batman's certainty—and the violence used to enforce it—helped convict the wrong man."
                },
                {
                  id: "batman-the-hill-gotham-neighborhood",
                  title: "Batman: The Hill Explained — Gotham Beyond the Shadow of the Bat",
                  category: "Gotham Street-Level Stories",
                  date: "August 2, 2026",
                  excerpt: "Batman: The Hill shifts the spotlight to a neglected Gotham neighborhood, where ordinary residents confront street violence, civic abandonment, and the limits of waiting for one masked hero to save their community."
                },
                {
                  id: "batman-book-of-the-dead-egyptian-mystery",
                  title: "Batman: Book of the Dead Explained — An Egyptian Mystery Beneath Gotham",
                  category: "Graphic Novel Deep Dives",
                  date: "August 2, 2026",
                  excerpt: "Batman: Book of the Dead sends the Dark Knight through an archaeological conspiracy linking ancient Egypt to the foundations of Gotham, where buried history proves as dangerous as any costumed criminal."
                },
                {
                  id: "batman-i-joker-dystopian-gotham-elseworld",
                  title: "Batman: I, Joker Explained — When Gotham Turns the Dark Knight into a Religion",
                  category: "Elseworlds Deep Dives",
                  date: "August 1, 2026",
                  excerpt: "Batman: I, Joker imagines a future Gotham where the legend of the Dark Knight has hardened into a brutal state religion—and one condemned man must wear the Joker's face to expose the tyranny hiding beneath the cowl."
                },
                {
                  id: "batman-masque-phantom-of-the-opera-elseworld",
                  title: "Batman: Masque Explained — The Dark Knight Enters the Phantom’s Opera House",
                  category: "Elseworlds Deep Dives",
                  date: "August 1, 2026",
                  excerpt: "Batman: Masque transforms Bruce Wayne’s world into an operatic gothic romance, recasting the Dark Knight myth through the shadows, obsession, and tragic spectacle of The Phantom of the Opera."
                },
                {
                  id: "batman-scar-of-the-bat-prohibition-gotham-elseworld",
                  title: "Batman: Scar of the Bat Explained — The Dark Knight Meets Prohibition-Era Gangland",
                  category: "Elseworlds Deep Dives",
                  date: "August 1, 2026",
                  excerpt: "Batman: Scar of the Bat recasts the legend as a hard-boiled 1920s crime story, placing a masked vigilante beside Eliot Ness in a war against bootleggers, corruption, and the machinery that lets organized crime own a city."
                },
                {
                  id: "batman-brotherhood-of-the-bat-tallant-future-gotham",
                  title: "Batman: Brotherhood of the Bat Explained — Ra’s al Ghul Rebuilds the Dark Knight in His Own Image",
                  category: "Elseworlds Deep Dives",
                  date: "July 31, 2026",
                  excerpt: "Batman: Brotherhood of the Bat imagines a future where Bruce Wayne is gone, Ra’s al Ghul controls Gotham, and an army of assassins wears discarded Batsuits—until Tallant challenges the corrupted legacy he was raised to serve."
                },
                {
                  id: "batman-the-abduction-alien-conspiracy-gotham",
                  title: "Batman: The Abduction Explained — An Alien Conspiracy Tests Gotham’s Greatest Skeptic",
                  category: "Graphic Novel Deep Dives",
                  date: "July 31, 2026",
                  excerpt: "Batman: The Abduction drops the Dark Knight into a mystery of missing time, UFO testimony, and apparent extraterrestrial contact, forcing a detective who trusts evidence above all else to investigate a case designed to make certainty impossible."
                },
                {
                  id: "batman-harvest-breed-george-pratt-gotham-war-trauma",
                  title: "Batman: Harvest Breed Explained — Gotham’s Violence Comes Home From War",
                  category: "Graphic Novel Deep Dives",
                  date: "July 31, 2026",
                  excerpt: "George Pratt’s Batman: Harvest Breed follows a brutal Gotham murder case into memories of the Vietnam War, using painted, expressionistic art to connect the city’s present violence with trauma that was never truly left behind."
                },
                {
                  id: "batman-the-last-angel-gotham-faith-mystery",
                  title: "Batman: The Last Angel Explained — Faith, Fraud, and a Gotham Miracle",
                  category: "Graphic Novel Deep Dives",
                  date: "July 30, 2026",
                  excerpt: "Batman: The Last Angel sends the Dark Knight after an ancient religious mystery, using Gotham’s appetite for miracles to test the line between evidence, belief, and the dangerous stories people need to be true."
                },
                {
                  id: "batman-snow-mr-freeze-origin-gotham-winter",
                  title: "Batman: Snow Explained — Mr. Freeze Turns Gotham’s Winter Into a Tragedy",
                  category: "Villain Spotlights",
                  date: "July 30, 2026",
                  excerpt: "Batman: Snow retells Victor Fries’s origin as a collision between grief, scientific obsession, and Bruce Wayne’s first attempt to build a team, giving Mr. Freeze a wintry Gotham tragedy where every effort to preserve life leaves someone colder."
                },
                {
                  id: "batman-run-riddler-run-gotham-corruption",
                  title: "Batman: Run, Riddler, Run Explained — A Puzzle Hidden Inside Gotham’s Power Structure",
                  category: "Riddler & Villains",
                  date: "July 30, 2026",
                  excerpt: "Batman: Run, Riddler, Run pulls the Dark Knight and the Riddler into the same conspiracy, turning a redevelopment scheme into a tense question of whether Gotham’s cleverest criminal can expose corruption without becoming its next victim."
                },
                {
                  id: "batman-death-by-design-gotham-architecture",
                  title: "Batman: Death by Design Explained — When Gotham’s Architecture Becomes the Crime Scene",
                  category: "Graphic Novel Deep Dives",
                  date: "July 29, 2026",
                  excerpt: "Batman: Death by Design turns Gotham’s collapsing landmarks and ambitious redevelopment into a murder mystery, using Dave Taylor’s sweeping black-and-white art to ask whether Bruce Wayne can save a city without erasing the history written into its stone."
                },
                {
                  id: "batman-the-gauntlet-dick-grayson-final-test",
                  title: "Batman: The Gauntlet Explained — Dick Grayson’s Final Test Before Robin",
                  category: "Batman Family & Robin",
                  date: "July 29, 2026",
                  excerpt: "Batman: The Gauntlet sends Dick Grayson into Gotham for one last proving run before he can become Robin — a compact coming-of-age mystery that shows why Bruce’s first partner was never simply a smaller Batman."
                },
                {
                  id: "batman-gotham-underground-criminal-power-vacuum",
                  title: "Batman: Gotham Underground Explained — The Criminal War Beneath the Cowl",
                  category: "Gotham Underworld",
                  date: "July 29, 2026",
                  excerpt: "Gotham Underground follows Batman into the city's criminal power vacuum as the Penguin, Tobias Whale, the Suicide Squad, and a crowd of costumed operators fight over territory — a street-level bridge between major events that maps who really profits when Gotham's old order breaks."
                },
                {
                  id: "batman-noel-christmas-carol-gotham",
                  title: "Batman: Noël Explained — A Christmas Carol in Gotham's Shadows",
                  category: "Graphic Novel Deep Dives",
                  date: "July 28, 2026",
                  excerpt: "Batman: Noël is Lee Bermejo's painted graphic novel retelling of A Christmas Carol with Bruce Wayne as Scrooge — a self-contained story about a Dark Knight who has grown so cold in his mission that mercy starts to look like weakness, told through a Gotham buried under dirty snow."
                },
                {
                  id: "gotham-city-sirens-catwoman-harley-ivy-team",
                  title: "Gotham City Sirens Explained - Catwoman, Harley Quinn, and Poison Ivy as Gotham's Most Dangerous Household",
                  category: "Joker & Villains",
                  date: "July 28, 2026",
                  excerpt: "Gotham City Sirens follows Catwoman, Harley Quinn, and Poison Ivy sharing a Gotham townhouse in uneasy alliance — Paul Dini's 2009 series is the best sustained treatment of all three characters together, built around a friendship that is more complicated and more durable than the setup suggests."
                },
                {
                  id: "gotham-academy-olive-silverlock-gotham-school-mysteries",
                  title: "Gotham Academy Explained - Olive Silverlock and the Secrets Hidden in Gotham's Elite School",
                  category: "Batman Family & Gotham Street Level",
                  date: "July 27, 2026",
                  excerpt: "Gotham Academy follows Olive Silverlock and Maps Mizoguchi through the mysteries buried inside Gotham City's most prestigious boarding school — a YA series by Becky Cloonan and Brenden Fletcher where the architecture has secrets, the student body carries Gotham's legacy, and Batman exists at the edges of a story that belongs entirely to the next generation."
                },
                {
                  id: "gotham-by-midnight-fawkes-corrigan-gcpd-supernatural",
                  title: "Gotham by Midnight Explained - The GCPD's Midnight Shift and the Supernatural City Beneath",
                  category: "Gotham Central & Street Level",
                  date: "July 27, 2026",
                  excerpt: "Gotham by Midnight follows Jim Corrigan and a GCPD unit that investigates cases nobody else will touch — the ones where the crime scene doesn't make sense and the witness list includes things that shouldn't exist. Ray Fawkes builds a Gotham where the supernatural isn't separate from the city's horror; it's the city's horror, made visible."
                },
                {
                  id: "joker-azzarello-bermejo-crime-noir-gotham",
                  title: "Joker by Azzarello & Bermejo Explained - Gotham's Worst Night Told From the Wrong Side",
                  category: "Joker & Villains",
                  date: "July 27, 2026",
                  excerpt: "The 2008 Joker OGN by Brian Azzarello and Lee Bermejo is a crime noir told through the eyes of a low-level thug caught in the Clown Prince's orbit as he retakes Gotham's underworld — a story where Batman barely appears and the city pays the full price anyway."
                },
                {
                  id: "batwoman-elegy-kate-kane-gotham-identity",
                  title: "Batwoman: Elegy Explained - Kate Kane and the Question of Who Gets to Fight for Gotham",
                  category: "Batman Family",
                  date: "July 26, 2026",
                  excerpt: "Batwoman: Elegy follows Kate Kane facing a cult leader who speaks in Wonderland quotes while her own origin unfolds in flashback — a story about military discharge, religious terror, and what's left when the institutions that were supposed to protect you don't."
                },
                {
                  id: "cassandra-cain-batgirl-silent-weapon-gotham",
                  title: "Cassandra Cain: Batgirl Explained - The Silent Weapon Gotham Didn't See Coming",
                  category: "Batman Family",
                  date: "July 26, 2026",
                  excerpt: "Cassandra Cain's Batgirl series follows a child raised without language — trained to read bodies instead of words — as she inherits the cowl and builds a identity in Gotham without the vocabulary most heroes take for granted."
                },
                {
                  id: "nightwing-year-one-dick-grayson-becomes-nightwing",
                  title: "Nightwing: Year One Explained - How Dick Grayson Stopped Being Robin",
                  category: "Robin & Batman Family",
                  date: "July 25, 2026",
                  excerpt: "Nightwing: Year One traces the night Bruce Wayne fired Dick Grayson as Robin — and the long, painful journey from that rupture to the birth of Nightwing in Bludhaven's shadow."
                },
                {
                  id: "batman-beyond-return-of-the-joker-future-gotham-horror",
                  title: "Batman Beyond: Return of the Joker Explained - Gotham's Darkest Punchline",
                  category: "Animated Gotham",
                  date: "July 25, 2026",
                  excerpt: "Batman Beyond: Return of the Joker uses a future Gotham to close the Joker's story with a trauma-soaked mystery — revealing what he did to Tim Drake that was bad enough to make Bruce Wayne bury it for decades."
                },
                {
                  id: "batman-mask-of-the-phantasm-animated-film-gotham-tragedy",
                  title: "Batman: Mask of the Phantasm Explained - Gotham's Finest Tragedy",
                  category: "Animated Gotham",
                  date: "July 25, 2026",
                  excerpt: "Batman: Mask of the Phantasm turns Bruce Wayne's origin into a love story, following the return of Andrea Beaumont and the memories she unlocks of the years before the cowl — when Batman was still a choice Bruce Wayne hadn't fully made."
                },
                {
                  id: "batman-faith-vigilantes-gotham-belief",
                  title: "Batman: Faith Explained - When Gotham Turns the Dark Knight Into a Religion",
                  category: "Early Career Stories",
                  date: "July 24, 2026",
                  excerpt: "Batman: Faith follows an early victory that inspires a rescued man to build his own army of believers, forcing Bruce to confront the dangerous gap between becoming Gotham's symbol and controlling what that symbol means."
                },
                {
                  id: "batman-many-deaths-silent-detective-mystery",
                  title: "The Many Deaths of the Batman Explained - A Silent Mystery of Masks and Murder",
                  category: "Detective Stories",
                  date: "July 24, 2026",
                  excerpt: "The Many Deaths of the Batman opens with a nearly wordless hunt for a killer targeting Batman's former teachers, turning Bruce Wayne's hard-earned skills and secret history into the clues of a chilling Gotham mystery."
                },
                {
                  id: "batman-unseen-invisible-man-gotham-horror",
                  title: "Batman: Unseen Explained - The Invisible Man Haunts Gotham",
                  category: "Gothic Horror",
                  date: "July 24, 2026",
                  excerpt: "Batman: Unseen reunites Doug Moench and Kelley Jones for a gothic mystery about an invisible killer, forcing the Dark Knight to hunt an enemy who turns Gotham's shadows into the perfect hiding place."
                },
                {
                  id: "batman-engines-ted-mckeever-gotham-machine",
                  title: "Batman: Engines Explained - Gotham's Machines Feed a Killer's Obsession",
                  category: "Graphic Novel Deep Dives",
                  date: "July 23, 2026",
                  excerpt: "Batman: Engines sends the Dark Knight after a ritualistic killer whose fixation on machinery turns Gotham's industrial landscape into a feverish argument about control, identity, and the systems people build to contain their fear."
                },
                {
                  id: "batman-war-drums-gotham-countdown",
                  title: "Batman: War Drums Explained - Gotham Counts Down to a Gang War",
                  category: "Gotham Events",
                  date: "July 23, 2026",
                  excerpt: "Batman: War Drums gathers the pressure building before War Games, following the Bat-family, Gotham's gangs, and a city whose fragile criminal order is already beginning to crack."
                },
                {
                  id: "batman-made-of-wood-green-lantern-gotham-mystery",
                  title: "Batman: Made of Wood Explained - Green Lantern Returns to Gotham's Oldest Unsolved Case",
                  category: "Detective Stories",
                  date: "July 23, 2026",
                  excerpt: "Batman: Made of Wood pairs the Dark Knight with Alan Scott for a murder mystery rooted in Gotham's past, asking what happens when an old hero returns to a case that never stopped haunting him."
                },
                {
                  id: "batman-jokers-five-way-revenge-lethal-return",
                  title: "Batman: The Joker's Five-Way Revenge Explained - The Clown Prince Gets His Teeth Back",
                  category: "Classic Stories",
                  date: "July 22, 2026",
                  excerpt: "The Joker's Five-Way Revenge restores Batman's greatest enemy as a calculating killer, sending the Dark Knight through Gotham to save five former henchmen from a punchline with no survivors."
                },
                {
                  id: "batman-night-of-the-owls-gotham-siege",
                  title: "Batman: Night of the Owls Explained - Gotham Fights Back Against the Court",
                  category: "Gotham Events",
                  date: "July 22, 2026",
                  excerpt: "Batman: Night of the Owls expands the Court of Owls conspiracy into a citywide siege, forcing the Bat-family to defend Gotham while Bruce Wayne confronts an enemy woven into its oldest foundations."
                },
                {
                  id: "batman-man-bat-kirk-langstrom-science-horror",
                  title: "Batman: Man-Bat Explained - Kirk Langstrom Turns Science Into Gotham Horror",
                  category: "Villain Spotlights",
                  date: "July 21, 2026",
                  excerpt: "Batman: Man-Bat turns Kirk Langstrom's failed cure into one of Gotham's purest science-horror tragedies, showing how a good intention can grow wings, teeth, and a shadow Batman has to chase through the city."
                },
                {
                  id: "batman-secrets-joker-media-gotham",
                  title: "Batman: Secrets Explained - Joker Turns Gotham's Media Machine Against the Mask",
                  category: "Story Guide",
                  date: "July 21, 2026",
                  excerpt: "Batman: Secrets puts the Dark Knight under public pressure as Joker twists Gotham's cameras, headlines, and old trauma into a campaign designed to make the city doubt the symbol it depends on."
                },
                {
                  id: "batman-dark-knight-dynasty-wayne-legacy-elseworld",
                  title: "Batman: Dark Knight Dynasty Explained - The Wayne Legacy Across Centuries",
                  category: "Elseworlds",
                  date: "July 21, 2026",
                  excerpt: "Batman: Dark Knight Dynasty stretches the Batman idea across the Wayne family line, turning Gotham's familiar crusade into a generational Elseworlds story about inheritance, obsession, and the shadow one symbol can cast through history."
                },
                {
                  id: "batman-dark-knight-of-the-round-table-arthurian-elseworld",
                  title: "Batman: Dark Knight of the Round Table Explained - Gotham's Myth Recast in Arthurian Steel",
                  category: "Elseworlds",
                  date: "July 20, 2026",
                  excerpt: "Batman: Dark Knight of the Round Table turns the Dark Knight into an Arthurian legend, trading Gotham's rooftops for haunted kingdoms while keeping the familiar questions of justice, legacy, and symbols built to outlive one man."
                },
                {
                  id: "gotham-central-soft-targets-city-under-siege",
                  title: "Gotham Central: Soft Targets Explained - The GCPD Faces a Citywide Nightmare",
                  category: "Gotham Central",
                  date: "July 20, 2026",
                  excerpt: "Gotham Central: Soft Targets turns a holiday sniper crisis into one of the series' sharpest tests of police work, fear, and what it means to protect Gotham when Batman cannot be everywhere at once."
                },
                {
                  id: "batman-their-dark-designs-gotham-conspiracy",
                  title: "Batman: Their Dark Designs Explained - Gotham's Old Plans Become a New Trap",
                  category: "Story Arc Deep Dives",
                  date: "July 20, 2026",
                  excerpt: "Batman: Their Dark Designs opens James Tynion IV's run with assassins, hidden design work, and a Gotham conspiracy that tests whether Bruce Wayne can build a better city without becoming predictable to his enemies."
                },
                {
                  id: "batman-city-of-light-gotham-idealism",
                  title: "Batman: City of Light Explained - Gotham's Brightest Promise Casts a Long Shadow",
                  category: "Story Arc Deep Dives",
                  date: "July 19, 2026",
                  excerpt: "Batman: City of Light sends the Dark Knight against a polished vision of progress, turning Gotham's hunger for renewal into a mystery about power, spectacle, and the danger of trusting a city-saving dream too quickly."
                },
                {
                  id: "batman-the-ultimate-evil-gotham-trafficking-case",
                  title: "Batman: The Ultimate Evil Explained - Gotham's Darkest Case Tests the Mission",
                  category: "Story Arc Deep Dives",
                  date: "July 19, 2026",
                  excerpt: "Batman: The Ultimate Evil pushes Bruce Wayne into one of Gotham's bleakest investigations, turning the crusade away from theatrical crime and toward the human cost of exploitation, corruption, and institutional neglect."
                },
                {
                  id: "batman-dark-patterns-early-case-files",
                  title: "Batman: Dark Patterns Explained - Early Gotham Turns Detection Into Pressure",
                  category: "Story Arc Deep Dives",
                  date: "July 19, 2026",
                  excerpt: "Batman: Dark Patterns returns to a younger Dark Knight, using compact mystery cases to show how early Gotham tests Batman's detective work, restraint, and ability to read violence before it spreads."
                },
                {
                  id: "batman-nocturna-gotham-night-thief",
                  title: "Batman: Nocturna Explained - Gotham's Night Thief Turns Romance Into Atmosphere",
                  category: "Character Spotlights",
                  date: "July 18, 2026",
                  excerpt: "Nocturna brings a strange, moonlit glamour to Batman comics, turning Gotham crime into a story about performance, longing, custody battles, and the danger of mistaking mystery for salvation."
                },
                {
                  id: "batman-long-shadows-dick-grayson-gotham",
                  title: "Batman: Long Shadows Explained — Dick Grayson Steps Into Gotham's Darkest Role",
                  category: "Story Arc Deep Dives",
                  date: "July 18, 2026",
                  excerpt: "Batman: Long Shadows follows Dick Grayson's uneasy first steps as Gotham's new Batman, turning Bruce Wayne's absence into a test of identity, trust, and whether the cowl can change without losing its purpose."
                },
                {
                  id: "batgirl-year-one-barbara-gordon-origin",
                  title: "Batgirl: Year One Explained — Barbara Gordon Finds Her Own Signal",
                  category: "Origins & Early Years",
                  date: "July 18, 2026",
                  excerpt: "Batgirl: Year One turns Barbara Gordon's first nights in costume into a bright, stubborn Gotham origin story about competence, independence, and the moment a side character decides she belongs in the myth."
                },
                {
                  id: "batman-knightsend-bruce-wayne-reclaims-batman",
                  title: "Batman: KnightsEnd Explained — Bruce Wayne Reclaims Batman From the Armor",
                  category: "Story Arc Deep Dives",
                  date: "July 17, 2026",
                  excerpt: "Batman: KnightsEnd closes Jean-Paul Valley's time under the cowl by forcing Bruce Wayne to take Batman back without becoming the brutal symbol Gotham has just learned to fear."
                },
                {
                  id: "batman-gotham-adventures-animated-continuity",
                  title: "Batman: Gotham Adventures Explained — Animated Continuity Finds Its Comic Rhythm",
                  category: "Animated Gotham",
                  date: "July 17, 2026",
                  excerpt: "Batman: Gotham Adventures keeps the animated Dark Knight moving between television episodes, turning clean continuity, sharp detective plots, and a wider Gotham cast into one of the most inviting Bat-books on the shelf."
                },
                {
                  id: "batman-knightquest-jean-paul-valley-broken-cowl",
                  title: "Batman: Knightquest Explained — Jean-Paul Valley Breaks the Cowl From Inside",
                  category: "Story Arc Deep Dives",
                  date: "July 17, 2026",
                  excerpt: "Batman: Knightquest follows Jean-Paul Valley after Knightfall, turning his armored Batman into a warning about what happens when Gotham's symbol is driven by programming, escalation, and fear instead of Bruce Wayne's restraint."
                },
                {
                  id: "batman-four-of-a-kind-gotham-rogues-origins",
                  title: "Batman: Four of a Kind Explained — Gotham's Rogues Get Their First Masks",
                  category: "Villain Spotlights",
                  date: "July 16, 2026",
                  excerpt: "Batman: Four of a Kind revisits the early origins of Poison Ivy, Scarecrow, Riddler, and Man-Bat, showing how Gotham's rogues gallery starts to look less like random crime and more like a city teaching damage to wear a theme."
                },
                {
                  id: "batman-huntress-cry-for-blood-helena-bertinelli",
                  title: "Batman/Huntress: Cry for Blood Explained - Helena Bertinelli Questions Gotham's Justice",
                  category: "Character Spotlights",
                  date: "July 16, 2026",
                  excerpt: "Batman/Huntress: Cry for Blood puts Helena Bertinelli under investigation, using family history, faith, and Gotham's mob violence to test whether Huntress is chasing justice, revenge, or both."
                },
                {
                  id: "batman-tower-of-babel-contingency-plans",
                  title: "JLA: Tower of Babel Explained — Batman's Contingency Plans Turn Against the League",
                  category: "Story Arc Deep Dives",
                  date: "July 16, 2026",
                  excerpt: "JLA: Tower of Babel turns Batman's private files into a Justice League crisis, exposing how Bruce Wayne's need to prepare for every threat can become its own betrayal when trust breaks under pressure."
                },
                {
                  id: "batman-road-to-no-mans-land-gotham-abandoned",
                  title: "Batman: Road to No Man's Land Explained — Gotham Gets Abandoned Before the Fall",
                  category: "Gotham Events",
                  date: "July 15, 2026",
                  excerpt: "Batman: Road to No Man's Land turns Gotham's earthquake aftermath into a political disaster, showing how bureaucracy, fear, and exhaustion push the city toward abandonment before the bridges ever come down."
                },
                {
                  id: "catwoman-when-in-rome-selina-kyle-mystery",
                  title: "Catwoman: When in Rome Explained — Selina Kyle Chases Gotham's Secrets Abroad",
                  category: "Gotham Rogues",
                  date: "July 15, 2026",
                  excerpt: "Catwoman: When in Rome sends Selina Kyle out of Gotham but keeps the city's secrets close, turning a glamorous Italian caper into a sharp mystery about identity, loyalty, and the long shadow of the Falcone family."
                },
                {
                  id: "batman-nightwalker-bruce-wayne-gotham-before-cowl",
                  title: "Batman: Nightwalker Explained — Bruce Wayne Faces Gotham Before the Cowl",
                  category: "Origins & Early Years",
                  date: "July 15, 2026",
                  excerpt: "Batman: Nightwalker catches Bruce Wayne before he becomes Batman, using a hostage crisis, Arkham secrets, and Gotham's class divide to show the detective instincts forming beneath the billionaire heir."
                },
                {
                  id: "detective-comics-face-of-death-dollmaker-gotham",
                  title: "Detective Comics: The Face of Death Explained — Dollmaker Carves Into New 52 Gotham",
                  category: "Detective Case Files",
                  date: "July 14, 2026",
                  excerpt: "Detective Comics: The Face of Death throws Batman into a grisly early New 52 case against the Dollmaker, using Gotham's body-horror edge to test how much fear the Dark Knight can absorb without becoming numb."
                },
                {
                  id: "flashpoint-batman-knight-of-vengeance-thomas-wayne",
                  title: "Flashpoint: Batman - Knight of Vengeance Explained — Thomas Wayne's Gotham Without Mercy",
                  category: "Elseworlds & Alternate Gothams",
                  date: "July 14, 2026",
                  excerpt: "Flashpoint: Batman - Knight of Vengeance turns Thomas Wayne into a brutal Dark Knight, rebuilding Gotham around grief, revenge, and the horrifying question of what Batman becomes without Bruce's restraint."
                },
                {
                  id: "gotham-central-half-a-life-renee-montoya",
                  title: "Gotham Central: Half a Life Explained — Renee Montoya Faces Gotham Without a Mask",
                  category: "Detective Case Files",
                  date: "July 14, 2026",
                  excerpt: "Gotham Central: Half a Life puts Renee Montoya at the center of a brutal procedural about exposure, corruption, and survival, showing how dangerous Gotham can be when the case follows a good cop home."
                },
                {
                  id: "batman-private-casebook-dini-nguyen-gotham",
                  title: "Batman: Private Casebook Explained — Paul Dini Opens Gotham's Smaller Files",
                  category: "Detective Case Files",
                  date: "July 13, 2026",
                  excerpt: "Batman: Private Casebook gathers Paul Dini and Dustin Nguyen's sharp detective-era stories, using Zatanna, Scarface, the Riddler, and Gotham's everyday predators to show how strong Batman can be in compact case files."
                },
                {
                  id: "batman-city-of-madness-gotham-below",
                  title: "Batman: City of Madness Explained — Gotham Splits Open Under the Mask",
                  category: "Story Arc Deep Dives",
                  date: "July 12, 2026",
                  excerpt: "Batman: City of Madness turns Gotham into a cosmic horror mirror, sending Batman toward a monstrous city beneath the city where every familiar face comes back warped and hungry."
                },
                {
                  id: "batman-dark-knight-dark-city-riddler-gotham-ritual",
                  title: "Batman: Dark Knight, Dark City Explained — Riddler Turns Gotham Into a Ritual",
                  category: "Story Arc Deep Dives",
                  date: "July 12, 2026",
                  excerpt: "Batman: Dark Knight, Dark City pushes the Riddler into occult nightmare territory, turning Gotham itself into a puzzle box where every clue drags Batman closer to a centuries-old ritual."
                },
                {
                  id: "batman-eye-of-the-beholder-two-face-origin",
                  title: "Batman: Eye of the Beholder Explained — Two-Face's Coin Lands in Gotham",
                  category: "Villain Spotlights",
                  date: "July 12, 2026",
                  excerpt: "Batman: Eye of the Beholder revisits Harvey Dent's fall with courtroom pressure, mob violence, and the coin-flip logic that turns Gotham's brightest district attorney into Two-Face."
                },
                {
                  id: "batman-the-world-global-gotham",
                  title: "Batman: The World Explained — The Dark Knight Crosses Borders",
                  category: "Essential Reading Guides",
                  date: "July 11, 2026",
                  excerpt: "Batman: The World turns the Bat-Signal into an international idea, letting creators from around the globe show how Bruce Wayne's mission changes shape when Gotham's shadow falls across different cities."
                },
                {
                  id: "batman-shadow-war-assassins-gotham-grief",
                  title: "Batman: Shadow War Explained — Assassins Turn Gotham's Grief Into a Global Hunt",
                  category: "Story Arc Deep Dives",
                  date: "July 11, 2026",
                  excerpt: "Batman: Shadow War uses Ra's al Ghul's apparent murder to throw Batman, Robin, Deathstroke, and the League of Shadows into a revenge spiral where every faction mistakes grief for justice."
                },
                {
                  id: "batman-arkham-knight-genesis-jason-todd",
                  title: "Batman: Arkham Knight Genesis Explained — Jason Todd Becomes Gotham's Ghost",
                  category: "Story Arc Deep Dives",
                  date: "July 11, 2026",
                  excerpt: "Batman: Arkham Knight Genesis follows Jason Todd from broken Robin to armored avenger, turning the Arkham Knight into Gotham's most personal accusation against Batman."
                },
                {
                  id: "batman-the-adventures-continue-animated-gotham",
                  title: "Batman: The Adventures Continue Explained — Animated Gotham Gets a Second Case",
                  category: "Story Arc Deep Dives",
                  date: "July 10, 2026",
                  excerpt: "Batman: The Adventures Continue reopens the animated universe's Gotham, using familiar voices, new villains, and old wounds to show why that streamlined Dark Knight still has room for fresh case files."
                },
                {
                  id: "batman-black-and-white-gotham-contrast",
                  title: "Batman: Black and White Explained — Gotham in Sharp Contrast",
                  category: "Essential Reading Guides",
                  date: "July 10, 2026",
                  excerpt: "Batman: Black and White strips the Dark Knight down to shadow, silence, and short-form storytelling, proving Gotham can feel enormous even when each tale has only a few pages to strike."
                },
                {
                  id: "batman-full-circle-reaper-robin-legacy",
                  title: "Batman: Full Circle Explained — The Reaper Returns to Test Robin's Legacy",
                  category: "Story Arc Deep Dives",
                  date: "July 10, 2026",
                  excerpt: "Batman: Full Circle revisits the shadow of the Reaper, turning a sequel to Year Two into a story about legacy, revenge, and whether Gotham's symbols heal old wounds or keep cutting them open."
                },
                {
                  id: "batman-gordon-of-gotham-jim-gordon-origin",
                  title: "Batman: Gordon of Gotham Explained — Jim Gordon Before the Bat-Signal",
                  category: "Character Spotlights",
                  date: "July 9, 2026",
                  excerpt: "Batman: Gordon of Gotham follows Jim Gordon before his most famous partnership, using Chicago corruption, family pressure, and hard compromises to show why Gotham's future commissioner was already built for impossible cities."
                },
                {
                  id: "batman-one-dark-knight-blackout-gotham",
                  title: "Batman: One Dark Knight Explained — A Blackout Turns Gotham Into a Gauntlet",
                  category: "Story Arc Deep Dives",
                  date: "July 9, 2026",
                  excerpt: "Batman: One Dark Knight throws Gotham into a citywide blackout, turning one dangerous prisoner transfer into a brutal survival march where Batman has to cross a broken city block by block."
                },
                {
                  id: "batman-dead-to-rights-deadshot-gotham",
                  title: "Batman: Dead to Rights Explained — Deadshot Takes Aim at Gotham",
                  category: "Villain Spotlights",
                  date: "July 9, 2026",
                  excerpt: "Batman: Dead to Rights puts Floyd Lawton under the microscope, turning Deadshot from a perfect-aim gimmick into a fatalistic Gotham mirror for Bruce Wayne's discipline, damage, and refusal to waste a second chance."
                },
                {
                  id: "batman-fortunate-son-rock-and-roll-rebellion",
                  title: "Batman: Fortunate Son Explained — Rock and Roll Rebellion Hits Gotham",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 8, 2026",
                  excerpt: "Batman: Fortunate Son turns rock music into Gotham's latest moral panic, using Bruce Wayne's suspicion, Dick Grayson's curiosity, and an Elvis-shaped mystery to make the Dark Knight wrestle with rebellion he cannot simply punch."
                },
                {
                  id: "batman-streets-of-gotham-city-case-files",
                  title: "Batman: Streets of Gotham Explained — Case Files From the City Batman Can't Leave Alone",
                  category: "Story Arc Deep Dives",
                  date: "July 8, 2026",
                  excerpt: "Batman: Streets of Gotham shifts the spotlight from grand crossover machinery to the city-level crimes, rogues, and civilians that make Gotham feel lived-in, proving the Bat-mission is strongest when it listens to the streets."
                },
                {
                  id: "batman-digital-justice-future-gotham-virus",
                  title: "Batman: Digital Justice Explained — Future Gotham Catches a Computer Virus",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 8, 2026",
                  excerpt: "Batman: Digital Justice throws the Bat-myth into a neon future where Gotham's corruption lives in networks, police systems, and a Joker virus, turning legacy into a cyberpunk question about who inherits the signal."
                },
                {
                  id: "batman-blades-cavalier-gotham-showmanship",
                  title: "Batman: Blades Explained — The Cavalier Cuts Through Gotham's Stage Lights",
                  category: "Story Arc Deep Dives",
                  date: "July 7, 2026",
                  excerpt: "Batman: Blades sends the Dark Knight against the Cavalier, a charming new swashbuckler whose theatrical crimes, public appeal, and hidden violence turn Gotham's hunger for heroes into a sharp little tragedy."
                },
                {
                  id: "batman-gothic-grant-morrison-mr-whisper",
                  title: "Batman: Gothic Explained — Mr. Whisper Haunts Gotham's Old Sins",
                  category: "Story Arc Deep Dives",
                  date: "July 7, 2026",
                  excerpt: "Batman: Gothic sends the Dark Knight into Grant Morrison's first Gotham nightmare, where Mr. Whisper, a childhood memory, and the city's buried crimes turn a mobster panic into a supernatural reckoning."
                },
                {
                  id: "batman-child-of-dreams-manga-gotham-mirrors",
                  title: "Batman: Child of Dreams Explained — Gotham Looks Into a Manga Mirror",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 7, 2026",
                  excerpt: "Batman: Child of Dreams sends the Dark Knight through a manga-inflected Gotham where copycat monsters, celebrity culture, and Bruce Wayne's own legend turn familiar villains into reflections of what the city wants Batman to be."
                },
                {
                  id: "batman-transference-hugo-strange-therapy",
                  title: "Batman: Transference Explained — Hugo Strange Puts Bruce Wayne on the Couch",
                  category: "Story Arc Deep Dives",
                  date: "July 6, 2026",
                  excerpt: "Batman: Transference turns Hugo Strange's return into a psychological ambush, using therapy, identity games, and Gotham's faith in the Bat to ask whether Bruce Wayne can survive being studied by someone who wants to become him."
                },
                {
                  id: "batman-gotham-shall-be-judged-azrael-judgment",
                  title: "Batman: Gotham Shall Be Judged Explained — Azrael Puts the City on Trial",
                  category: "Story Arc Deep Dives",
                  date: "July 6, 2026",
                  excerpt: "Batman: Gotham Shall Be Judged turns Gotham into a religious battlefield, using Azrael, the Crusader, and the Bat-family to ask what happens when justice becomes prophecy, punishment, and public spectacle."
                },
                {
                  id: "batman-turning-points-gordon-batman-alliance",
                  title: "Batman: Turning Points Explained — The Case Files That Built the Bat-Signal",
                  category: "Story Arc Deep Dives",
                  date: "July 5, 2026",
                  excerpt: "Batman: Turning Points uses five snapshots from Gotham's early years to show how Batman and Jim Gordon slowly turned suspicion, failure, and hard-won trust into the alliance that keeps the Bat-Signal burning."
                },
                {
                  id: "batman-dark-allegiances-1930s-elseworld",
                  title: "Batman: Dark Allegiances Explained — The Dark Knight Enters the 1930s",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 5, 2026",
                  excerpt: "Batman: Dark Allegiances drops Bruce Wayne into a late-1930s Elseworld, using crooked politics, racist secret societies, and media panic to turn the Batman myth into a period-piece argument about fear and power."
                },
                {
                  id: "batman-gotham-noir-jim-gordon-murder-mystery",
                  title: "Batman: Gotham Noir Explained — Jim Gordon Takes the Case",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 5, 2026",
                  excerpt: "Batman: Gotham Noir strips the myth down to hard-boiled crime, recasting Jim Gordon as a compromised private eye while Bruce Wayne becomes the shadow hanging over a murder mystery soaked in guilt, money, and old Gotham sins."
                },
                {
                  id: "batman-vengeance-of-bane-origin-santa-prisca",
                  title: "Batman: Vengeance of Bane Explained — Santa Prisca Builds the Bat-Breaker",
                  category: "Villain Spotlights",
                  date: "July 4, 2026",
                  excerpt: "Batman: Vengeance of Bane turns Bane from a looming musclebound threat into Gotham's most disciplined nightmare, tracing how Santa Prisca, prison brutality, and Venom shaped the strategist who would eventually break the Bat."
                },
                {
                  id: "batman-haunted-gotham-elseworlds-witchcraft",
                  title: "Batman: Haunted Gotham Explained — Witchcraft Turns the City Against the Bat",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 4, 2026",
                  excerpt: "Batman: Haunted Gotham reimagines Bruce Wayne's crusade as a supernatural war, turning familiar Gotham landmarks, family secrets, and occult horror into an Elseworlds nightmare where the city itself feels cursed."
                },
                {
                  id: "batman-penguin-triumphant-oswald-cobblepot-gotham-power",
                  title: "Batman: Penguin Triumphant Explained — Oswald Cobblepot Grabs Gotham's Respect",
                  category: "Villain Spotlights",
                  date: "July 3, 2026",
                  excerpt: "Batman: Penguin Triumphant turns Oswald Cobblepot into more than an umbrella gimmick, using crime, humiliation, and social hunger to show how Gotham's most underestimated gentleman villain converts ridicule into power."
                },
                {
                  id: "batman-scottish-connection-highland-mystery",
                  title: "Batman: Scottish Connection Explained — Bruce Wayne’s Highland Mystery",
                  category: "Story Arc Deep Dives",
                  date: "July 3, 2026",
                  excerpt: "Batman: Scottish Connection sends Bruce Wayne into a foggy family mystery far from Gotham, using castles, clan history, and old violence to show how the Dark Knight’s legacy can haunt him even outside the city."
                },
                {
                  id: "batman-bride-of-the-demon-ras-talia-gotham",
                  title: "Batman: Bride of the Demon Explained — Ra’s al Ghul Tests Love and Legacy",
                  category: "Character Spotlights",
                  date: "July 3, 2026",
                  excerpt: "Batman: Bride of the Demon pulls Bruce Wayne back into Ra’s al Ghul’s orbit, using Talia, a dangerous marriage plot, and the Demon’s Head’s endless schemes to test whether love can survive inside a legacy built on control."
                },
                {
                  id: "batman-jokers-asylum-rogues-therapy",
                  title: "Batman: Joker's Asylum Explained — Gotham's Rogues Take the Couch",
                  category: "Villain Spotlights",
                  date: "July 2, 2026",
                  excerpt: "Batman: Joker's Asylum turns Gotham's villains into case studies narrated by the Clown Prince of Crime, framing the rogues gallery as a cracked therapy session where every confession reveals why Batman's city keeps breeding monsters."
                },
                {
                  id: "batman-blue-grey-and-bat-civil-war-elseworld",
                  title: "Batman: The Blue, the Grey, and the Bat Explained — Civil War Shadows Over Gotham",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 2, 2026",
                  excerpt: "Batman: The Blue, the Grey, and the Bat recasts Bruce Wayne as a Civil War-era avenger, moving Gotham's familiar grief, disguise, and justice into a divided America where the mask becomes both battlefield myth and moral burden."
                },
                {
                  id: "batman-odyssey-neal-adams-fever-dream",
                  title: "Batman: Odyssey Explained — Neal Adams Sends Gotham Into a Fever Dream",
                  category: "Story Arc Deep Dives",
                  date: "July 2, 2026",
                  excerpt: "Batman: Odyssey turns the Dark Knight into a wild, argumentative adventure across impossible threats, using Neal Adams's kinetic imagination to make Gotham feel less like a map and more like Bruce Wayne's feverish inner weather."
                },
                {
                  id: "batman-blink-gotham-witness-detective",
                  title: "Batman: Blink Explained — The Witness Who Sees Through Gotham",
                  category: "Story Arc Deep Dives",
                  date: "July 1, 2026",
                  excerpt: "Batman: Blink turns a blind Gothamite with an impossible gift into one of the Dark Knight's strangest witnesses, using a murder case to test how Batman listens when evidence arrives through another person's pain."
                },
                {
                  id: "batman-castle-of-the-bat-frankenstein-elseworld",
                  title: "Batman: Castle of the Bat Explained — Frankenstein Shadows Over Gotham",
                  category: "Elseworlds & Alternate Gotham",
                  date: "July 1, 2026",
                  excerpt: "Batman: Castle of the Bat recasts Bruce Wayne as a grief-struck scientist in a Frankenstein-shaped Gotham, turning the Dark Knight myth into a candlelit horror story about creation, guilt, and monsters made in the name of justice."
                },
                {
                  id: "batman-troika-bruce-wayne-reclaims-cowl",
                  title: "Batman: Troika Explained — Bruce Wayne Reclaims the Cowl",
                  category: "Story Arc Deep Dives",
                  date: "June 30, 2026",
                  excerpt: "Batman: Troika brings Bruce Wayne back after Knightfall, KnightsEnd, and Prodigal, turning a Russian weapons conspiracy into the moment Gotham sees whether its original Dark Knight can reclaim the cowl without forgetting what the symbol nearly became."
                },
                {
                  id: "batman-kings-of-fear-scarecrow-gotham",
                  title: "Batman: Kings of Fear Explained — Scarecrow Puts the Mission on Trial",
                  category: "Villain Spotlights",
                  date: "June 30, 2026",
                  excerpt: "Batman: Kings of Fear traps the Dark Knight inside Scarecrow's cruelest question: whether Gotham is safer because Batman exists, or more terrified because his war taught the city to live in costume-shaped fear."
                },
                {
                  id: "batman-ego-darwyn-cooke-inner-war",
                  title: "Batman: Ego Explained — Bruce Wayne Argues With the Bat",
                  category: "Character Spotlights",
                  date: "June 30, 2026",
                  excerpt: "Batman: Ego turns a single night in Gotham into a brutal conversation between Bruce Wayne and the symbol he created, using Darwyn Cooke’s sharp noir style to ask whether Batman is a mission, a monster, or both."
                },
                {
                  id: "batman-the-button-watchmen-mystery",
                  title: "Batman: The Button Explained — Gotham Touches the Watchmen Mystery",
                  category: "Story Arc Deep Dives",
                  date: "June 29, 2026",
                  excerpt: "Batman: The Button turns a tiny bloodstained smiley face into a huge DC mystery, pairing the Dark Knight with the Flash as Gotham's detective work collides with time, grief, and the shadow of Watchmen."
                },
                {
                  id: "batman-second-chances-jason-todd-new-robin",
                  title: "Batman: Second Chances Explained — Jason Todd Steps Into the Shadow",
                  category: "Character Spotlights",
                  date: "June 29, 2026",
                  excerpt: "Batman: Second Chances gives Jason Todd his post-Crisis arrival, reframing Robin as a harder-edged Gotham rescue story where Bruce Wayne has to decide whether another wounded kid needs discipline, trust, or the chance he never got."
                },
                {
                  id: "batman-hush-returns-tommy-elliot-gotham-bandages",
                  title: "Batman: Hush Returns Explained — Tommy Elliot Pulls at Gotham's Bandages",
                  category: "Story Arc Deep Dives",
                  date: "June 29, 2026",
                  excerpt: "Batman: Hush Returns brings Tommy Elliot back into Gotham's bloodstream, turning old grudges, criminal alliances, and Bruce Wayne's exposed identity wounds into a messy sequel about what revenge does after the first grand reveal is over."
                },
                {
                  id: "batman-dark-detective-englehart-rogers-return",
                  title: "Batman: Dark Detective Explained — Strange Love Returns to Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 28, 2026",
                  excerpt: "Batman: Dark Detective reunites Steve Englehart and Marshall Rogers for a sequel steeped in obsession, political theater, and old wounds, bringing Bruce Wayne, Silver St. Cloud, the Joker, Two-Face, and a very unstable Gotham back under one operatic spotlight."
                },
                {
                  id: "batman-joker-war-gotham-punchline",
                  title: "Batman: Joker War Explained — Gotham Becomes Joker’s Punchline",
                  category: "Story Arc Deep Dives",
                  date: "June 28, 2026",
                  excerpt: "Batman: Joker War turns Bruce Wayne’s fortune, city, and symbol into weapons for the Clown Prince of Crime, forcing the Dark Knight to fight a Gotham where Joker has bought the stage, written the joke, and made everyone else part of the punchline."
                },
                {
                  id: "batman-mad-love-harley-quinn-joker-gotham",
                  title: "Batman: Mad Love Explained — Harley Quinn’s Smile Becomes Gotham’s Trap",
                  category: "Villain Spotlights",
                  date: "June 28, 2026",
                  excerpt: "Batman: Mad Love turns Harley Quinn’s origin into one of Gotham’s sharpest tragedies, showing how Dr. Harleen Quinzel’s compassion, ambition, and need to be seen are twisted by the Joker into a weapon aimed at Batman and herself."
                },
                {
                  id: "batman-in-darkest-knight-green-lantern-elseworld",
                  title: "Batman: In Darkest Knight Explained — Bruce Wayne Gets the Ring",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 27, 2026",
                  excerpt: "Batman: In Darkest Knight rewrites Bruce Wayne’s origin by giving him a Green Lantern ring instead of a bat-shaped mission, turning Gotham’s grief-driven vigilante into a cosmic protector who still cannot escape the shadow of Crime Alley."
                },
                {
                  id: "batman-dark-knight-returns-old-soldier-gotham",
                  title: "Batman: The Dark Knight Returns Explained — Gotham's Old Soldier Comes Back",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 27, 2026",
                  excerpt: "Batman: The Dark Knight Returns drags Bruce Wayne out of retirement and into a decaying future Gotham, turning the Dark Knight into an older, angrier legend who has to decide whether his war is duty, addiction, or the only language the city still understands."
                },
                {
                  id: "batman-gotham-after-midnight-madness-city",
                  title: "Batman: Gotham After Midnight Explained — Madness Walks the City After Dark",
                  category: "Story Arc Deep Dives",
                  date: "June 27, 2026",
                  excerpt: "Batman: Gotham After Midnight turns the city’s witching hour into a parade of grotesque suspects, theatrical murders, and monstrous masks, using Steve Niles and Kelley Jones’s horror-noir mood to ask what Gotham becomes when the sane world goes to sleep."
                },
                {
                  id: "batman-tenses-gotham-future-present",
                  title: "Batman: Tenses Explained — Gotham's Future Haunts Its Present",
                  category: "Story Arc Deep Dives",
                  date: "June 26, 2026",
                  excerpt: "Batman: Tenses uses a grim Gotham mystery to split Bruce Wayne's mission across past, present, and possible future, turning the Dark Knight's war on crime into a question about whether he can ever outrun the city that made him."
                },
                {
                  id: "batman-gotham-county-line-suburban-horror",
                  title: "Batman: Gotham County Line Explained — Suburban Horror Beyond the City Limits",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 26, 2026",
                  excerpt: "Batman: Gotham County Line drags the Dark Knight past Gotham’s familiar alleys into a haunted suburban borderland, turning a missing-person case into a ghost story about grief, denial, and what waits outside the city’s rules."
                },
                {
                  id: "batman-dead-reckoning-gotham-conspiracy",
                  title: "Batman: Dead Reckoning Explained — Gotham’s Past Becomes a Murder Map",
                  category: "Story Arc Deep Dives",
                  date: "June 26, 2026",
                  excerpt: "Batman: Dead Reckoning sends the Dark Knight through a conspiracy of old crimes, buried alliances, and familiar faces, turning Gotham’s history into a murder map where every clue asks who gets to escape the past."
                },
                {
                  id: "batman-mud-pack-clayface-gotham-monsters",
                  title: "Batman: The Mud Pack Explained — Clayface’s Monsters Share One Gotham Mask",
                  category: "Villain Spotlights",
                  date: "June 25, 2026",
                  excerpt: "Batman: The Mud Pack gathers Gotham’s Clayfaces into one ugly conspiracy, turning shapeshifting horror into a story about stolen identities, broken bodies, and the monsters who cannot stop wearing other people’s lives."
                },
                {
                  id: "batman-war-on-crime-paul-dini-alex-ross-gotham",
                  title: "Batman: War on Crime Explained — Gotham Through Alex Ross’s Painted Shadows",
                  category: "Story Arc Deep Dives",
                  date: "June 25, 2026",
                  excerpt: "Batman: War on Crime strips the Dark Knight down to one painted, street-level moral test, asking whether Bruce Wayne can fight Gotham’s poverty, fear, and violence without pretending every wound has a simple costumed answer."
                },
                {
                  id: "batman-whatever-happened-caped-crusader-ending",
                  title: "Batman: Whatever Happened to the Caped Crusader? Explained — The Dark Knight Gets a Funeral",
                  category: "Story Arc Deep Dives",
                  date: "June 24, 2026",
                  excerpt: "Whatever Happened to the Caped Crusader? gives Batman an impossible funeral where friends and enemies each tell a different ending, turning the Dark Knight’s death into a meditation on why Gotham’s legend can never really stay buried."
                },
                {
                  id: "batman-victim-syndicate-gotham-collateral-damage",
                  title: "Batman: The Victim Syndicate Explained — Gotham’s Collateral Damage Fights Back",
                  category: "Story Arc Deep Dives",
                  date: "June 24, 2026",
                  excerpt: "Batman: The Victim Syndicate turns Gotham’s background casualties into the main threat, forcing Batman and his allies to face the people hurt not by one villain’s plan, but by years of battles fought in the city’s streets."
                },
                {
                  id: "batman-widening-gyre-trust-betrayal",
                  title: "Batman: The Widening Gyre Explained — Trust Becomes Gotham’s Trap",
                  category: "Story Arc Deep Dives",
                  date: "June 24, 2026",
                  excerpt: "Batman: The Widening Gyre pulls Bruce Wayne toward romance, nostalgia, and a new masked ally, then turns that comfort into a Gotham trap about trust, denial, and the dangers of wanting the mission to finally get easier."
                },
                {
                  id: "batman-cacophony-joker-noise-gotham",
                  title: "Batman: Cacophony Explained — Joker’s Noise Cuts Through Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 23, 2026",
                  excerpt: "Batman: Cacophony throws the Dark Knight into a loud, nasty collision with Joker, Onomatopoeia, and Gotham’s appetite for theatrical violence, turning a grim crime story into a question of whether Batman can hear the real threat through all the noise."
                },
                {
                  id: "batman-i-am-gotham-gotham-girl-heroism",
                  title: "Batman: I Am Gotham Explained — Gotham Girl and the Cost of Hope",
                  category: "Story Arc Deep Dives",
                  date: "June 23, 2026",
                  excerpt: "Batman: I Am Gotham opens Tom King’s run by introducing Gotham and Gotham Girl, two super-powered protectors whose bright promise forces Bruce Wayne to ask what kind of hero his city really needs."
                },
                {
                  id: "batman-city-of-bane-bane-gotham-throne",
                  title: "Batman: City of Bane Explained — Bane Rules Gotham From the Throne",
                  category: "Story Arc Deep Dives",
                  date: "June 23, 2026",
                  excerpt: "Batman: City of Bane turns Gotham into Bane’s captured kingdom, forcing Bruce Wayne to recover from defeat while his enemies, allies, and city are rearranged under a villain who finally understands Batman as both symbol and system."
                },
                {
                  id: "batman-anarky-lonnie-machin-gotham-order",
                  title: "Batman: Anarky Explained — Lonnie Machin Challenges Gotham’s Order",
                  category: "Villain Spotlights",
                  date: "June 22, 2026",
                  excerpt: "Batman: Anarky introduces Lonnie Machin as a teenage revolutionary who sees Gotham’s corruption as a system to dismantle, forcing the Dark Knight to face an enemy whose danger comes from ideas as much as explosives."
                },
                {
                  id: "batman-absolution-jennifer-blake-redemption",
                  title: "Batman: Absolution Explained — Gotham’s Dark Knight Confronts Redemption",
                  category: "Story Arc Deep Dives",
                  date: "June 22, 2026",
                  excerpt: "Batman: Absolution sends Bruce Wayne after Jennifer Blake, a terrorist tied to a devastating Gotham bombing, and turns the chase into a thorny question about guilt, faith, and whether the Dark Knight can recognize redemption when vengeance is easier."
                },
                {
                  id: "batman-devils-advocate-joker-on-trial",
                  title: "Batman: The Devil’s Advocate Explained — Joker on Trial in Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 22, 2026",
                  excerpt: "Batman: The Devil’s Advocate puts the Joker on death row for a crime he may not have committed, forcing Batman to defend truth itself even when Gotham is ready to let its worst monster die."
                },
                {
                  id: "batman-prodigal-dick-grayson-guards-gotham",
                  title: "Batman: Prodigal Explained — Dick Grayson Guards Gotham Before the Cowl",
                  category: "Story Arc Deep Dives",
                  date: "June 21, 2026",
                  excerpt: "Batman: Prodigal puts Dick Grayson in the cape years before his later Batman era, turning Gotham into a test of trust, inheritance, and whether Bruce Wayne’s mission can survive when someone warmer carries the symbol."
                },
                {
                  id: "arkham-asylum-living-hell-gotham-madhouse",
                  title: "Arkham Asylum: Living Hell Explained — Gotham’s Madhouse From the Inside",
                  category: "Gotham Institutions",
                  date: "June 21, 2026",
                  excerpt: "Arkham Asylum: Living Hell turns Gotham’s most infamous institution into the main character, following Warren White’s descent through bureaucracy, monsters, and supernatural punishment inside a hospital that behaves like a trap."
                },
                {
                  id: "batman-time-and-the-batman-bruce-wayne-legacy",
                  title: "Batman: Time and the Batman Explained — Bruce Wayne’s Return Reframes the Mission",
                  category: "Story Arc Deep Dives",
                  date: "June 21, 2026",
                  excerpt: "Batman: Time and the Batman uses Bruce Wayne’s return from temporal exile to examine what Batman means to Gotham, the Bat-family, and the future he keeps shaping even when he is missing from the cave."
                },
                {
                  id: "batman-contagion-gotham-plague-crisis",
                  title: "Batman: Contagion Explained — Gotham’s Plague Turns Fear Into a Citywide Crisis",
                  category: "Story Arc Deep Dives",
                  date: "June 20, 2026",
                  excerpt: "Batman: Contagion transforms Gotham into a quarantine nightmare, forcing Batman, the Bat-family, and the city’s institutions to confront a plague that spreads panic as quickly as infection."
                },
                {
                  id: "batman-failsafe-zur-en-arrh-contingency",
                  title: "Batman: Failsafe Explained — Zur-En-Arrh Turns Batman’s Plans Against Him",
                  category: "Story Arc Deep Dives",
                  date: "June 20, 2026",
                  excerpt: "Batman: Failsafe weaponizes Bruce Wayne’s own contingency thinking against him, unleashing a machine built by Zur-En-Arrh to stop Batman if he ever crosses the line and turning Gotham into the test chamber for his worst-case logic."
                },
                {
                  id: "batman-last-knight-on-earth-final-gotham",
                  title: "Batman: Last Knight on Earth Explained — Bruce Wayne’s Final Road Through Gotham’s Ruins",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 20, 2026",
                  excerpt: "Batman: Last Knight on Earth sends Bruce Wayne across a broken future with Joker’s head in a jar, turning the Dark Knight’s last case into a surreal road trip about legacy, denial, and whether Gotham’s symbol can survive the end of the world."
                },
                {
                  id: "batman-dark-knight-iii-master-race-future-legacy",
                  title: "Batman: The Dark Knight III — The Master Race Explained — Legacy War in Future Gotham",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 19, 2026",
                  excerpt: "Batman: The Dark Knight III — The Master Race pulls Frank Miller’s future Gotham back into a mythic superhero war, testing whether Bruce Wayne’s legend can survive age, legacy, and an army determined to turn symbols into weapons."
                },
                {
                  id: "batman-bloodstorm-vampire-batman-joker",
                  title: "Batman: Bloodstorm Explained — Vampire Batman Battles the Joker’s Chaos",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 19, 2026",
                  excerpt: "Batman: Bloodstorm continues the Red Rain vampire saga by turning Bruce Wayne’s war on crime into a fight against hunger, temptation, and a Joker-led Gotham where becoming the monster may be the only way to stop one."
                },
                {
                  id: "batman-crimson-mist-vampire-tragedy",
                  title: "Batman: Crimson Mist Explained — Vampire Batman Crosses the Final Line",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 19, 2026",
                  excerpt: "Batman: Crimson Mist closes the Red Rain vampire trilogy by pushing Bruce Wayne past restraint, turning Gotham's undead Dark Knight into the monster his allies have to confront before the symbol devours the mission."
                },
                {
                  id: "batman-ten-nights-of-the-beast-kgbeast-cold-war",
                  title: "Batman: Ten Nights of the Beast Explained — KGBeast Makes Gotham a Cold War Target",
                  category: "Story Arc Deep Dives",
                  date: "June 18, 2026",
                  excerpt: "Ten Nights of the Beast drops KGBeast into Gotham as a relentless Soviet assassin, turning Batman's detective war into a Cold War siege where ideology, duty, and collateral damage collide in the city's shadows."
                },
                {
                  id: "batman-red-rain-vampire-elseworld",
                  title: "Batman & Dracula: Red Rain Explained — Gotham Turns the Bat Literal",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 18, 2026",
                  excerpt: "Batman & Dracula: Red Rain turns Gotham's bat imagery into a vampire tragedy, pushing Bruce Wayne into supernatural war and asking what happens when the Dark Knight becomes the monster criminals always feared."
                },
                {
                  id: "batman-dark-knight-strikes-again-future-rebellion",
                  title: "Batman: The Dark Knight Strikes Again Explained — Gotham’s Legend Goes Loud",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 18, 2026",
                  excerpt: "The Dark Knight Strikes Again turns Frank Miller's future Batman into a chaotic rebellion against a managed world, trading noir restraint for satire, spectacle, and a Gotham legend too loud to stay buried."
                },
                {
                  id: "batman-the-man-who-falls-bruce-wayne-origin",
                  title: "Batman: The Man Who Falls Explained — Bruce Wayne’s Origin Between Panels",
                  category: "Story Arc Deep Dives",
                  date: "June 17, 2026",
                  excerpt: "Batman: The Man Who Falls compresses Bruce Wayne's path from traumatized child to Dark Knight into a spare origin study, showing how grief, training, and one crashing bat transform a life into a symbol."
                },
                {
                  id: "batman-death-and-the-maidens-ras-al-ghul-legacy",
                  title: "Batman: Death and the Maidens Explained — Ra’s al Ghul’s Legacy Turns Fatal",
                  category: "Story Arc Deep Dives",
                  date: "June 17, 2026",
                  excerpt: "Batman: Death and the Maidens drags Bruce Wayne into Ra’s al Ghul’s bloodline wars, using Nyssa, Talia, and a dying Demon’s Head to turn immortality into a family curse Gotham cannot ignore."
                },
                {
                  id: "batman-night-of-the-monster-men-bat-family-kaiju-crisis",
                  title: "Batman: Night of the Monster Men Explained — Gotham's Kaiju Crisis Tests the Bat-Family",
                  category: "Story Arc Deep Dives",
                  date: "June 17, 2026",
                  excerpt: "Night of the Monster Men turns Gotham into a storm-lashed disaster zone, using Hugo Strange's giant creatures to test whether Batman's growing team can protect a city when the case becomes a full-scale emergency."
                },
                {
                  id: "batman-year-three-dick-grayson-robin-origin",
                  title: "Batman: Year Three Explained — Dick Grayson's Origin Refracts Through Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 16, 2026",
                  excerpt: "Batman: Year Three revisits Dick Grayson’s beginning while Bruce Wayne is at one of his loneliest points, turning Robin’s origin into a story about memory, grief, and why Gotham needs more than one shadow."
                },
                {
                  id: "robin-year-one-dick-grayson-gotham-lessons",
                  title: "Robin: Year One Explained — Dick Grayson Learns Gotham's Hard Lessons",
                  category: "Character Spotlights",
                  date: "June 16, 2026",
                  excerpt: "Robin: Year One turns Dick Grayson’s first year beside Batman into a bruising coming-of-age story, showing how Gotham tests the bright half of the Dynamic Duo before he fully understands the cost of the cape."
                },
                {
                  id: "batman-dark-prince-charming-joker-family-game",
                  title: "Batman: Dark Prince Charming Explained — Joker’s Cruel Family Game",
                  category: "Story Arc Deep Dives",
                  date: "June 16, 2026",
                  excerpt: "Batman: Dark Prince Charming turns the Dark Knight's war with the Joker into a ruthless custody nightmare, using a kidnapped child, Harley Quinn, and Gotham's broken families to test how far Batman can go without becoming cruel."
                },
                {
                  id: "batman-creature-of-the-night-bruce-wainwright",
                  title: "Batman: Creature of the Night Explained — When the Bat Becomes a Wish",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 15, 2026",
                  excerpt: "Batman: Creature of the Night turns Batman into a childhood prayer answered too literally, following Bruce Wainwright through grief, obsession, and the frightening cost of getting the avenger he thinks he needs."
                },
                {
                  id: "batman-thrillkiller-batgirl-robin-noir",
                  title: "Batman: Thrillkiller Explained — Batgirl and Robin in a Noir Gotham",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 15, 2026",
                  excerpt: "Batman: Thrillkiller rewires Gotham into a stylish 1960s noir where Barbara Gordon and Dick Grayson carry the vigilante spark, turning Batman's myth into a story about youth, rebellion, and a city already drunk on spectacle."
                },
                {
                  id: "batman-holy-terror-theocracy-elseworld",
                  title: "Batman: Holy Terror Explained — Gotham Under a Theocratic Shadow",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 14, 2026",
                  excerpt: "Batman: Holy Terror imagines Bruce Wayne rebelling against a church-ruled Gotham, turning the origin of the Dark Knight into an Elseworlds story about forbidden truth, state power, and faith twisted into control."
                },
                {
                  id: "batman-the-chalice-grail-quest",
                  title: "Batman: The Chalice Explained — Gotham's Grail Quest Tests the Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 14, 2026",
                  excerpt: "Batman: The Chalice sends the Dark Knight into a mythic chase for the Holy Grail, turning Gotham's detective logic against knights, secret orders, and the uncomfortable question of what Bruce Wayne would do with a miracle."
                },
                {
                  id: "batman-birth-of-the-demon-ras-al-ghul-origin",
                  title: "Batman: Birth of the Demon Explained — Ra’s al Ghul’s Immortal Origin",
                  category: "Villain Spotlights",
                  date: "June 14, 2026",
                  excerpt: "Batman: Birth of the Demon pulls Ra’s al Ghul out of the shadows and into tragedy, revealing how a brilliant physician became an immortal eco-terrorist whose love, loss, and rage still haunt Batman’s world."
                },
                {
                  id: "batman-europa-joker-virus-road-trip",
                  title: "Batman: Europa Explained — Batman and Joker’s Poisoned Road Trip",
                  category: "Story Arc Deep Dives",
                  date: "June 13, 2026",
                  excerpt: "Batman: Europa forces the Dark Knight and the Joker into a diseased alliance across Europe, turning their toxic bond into a race against time where survival depends on the one partner neither man can trust."
                },
                {
                  id: "batman-nine-lives-gotham-noir",
                  title: "Batman: Nine Lives Explained — Gotham as a Hard-Boiled Crime Scene",
                  category: "Elseworlds & Alternate Gotham",
                  date: "June 13, 2026",
                  excerpt: "Batman: Nine Lives recasts Gotham as a 1940s noir puzzle, turning the Dark Knight's world into a murder mystery where familiar rogues, allies, and femme fatales orbit one dead Selina Kyle and a city full of secrets."
                },
                {
                  id: "batman-the-knight-bruce-wayne-training",
                  title: "Batman: The Knight Explained — Bruce Wayne Before the Bat",
                  category: "Story Arc Deep Dives",
                  date: "June 13, 2026",
                  excerpt: "Batman: The Knight follows Bruce Wayne through the bruising years before Gotham gets its Dark Knight, turning his training journey into a story about grief, discipline, failure, and the dangerous people who help forge the Bat."
                },
                {
                  id: "batman-jekyll-and-hyde-two-face-split-soul",
                  title: "Batman: Jekyll & Hyde Explained — Two-Face and Gotham's Split Soul",
                  category: "Villain Spotlights",
                  date: "June 12, 2026",
                  excerpt: "Batman: Jekyll & Hyde drags Two-Face back to the horror roots of a divided self, using Gotham's medicine, mob violence, and Harvey Dent's broken morality to ask whether the monster is a curse, a choice, or both."
                },
                {
                  id: "batman-penguin-pain-and-prejudice-oswald-origin",
                  title: "Batman: Penguin — Pain and Prejudice Explained — Oswald Cobblepot's Bitter Origin",
                  category: "Villain Spotlights",
                  date: "June 12, 2026",
                  excerpt: "Penguin: Pain and Prejudice sharpens Oswald Cobblepot into one of Gotham's most resentful survivors, turning ridicule, class hunger, and wounded pride into the crooked engine behind the Iceberg Lounge's gentleman monster."
                },
                {
                  id: "batman-snow-mr-freeze-origin",
                  title: "Batman: Snow Explained — Mr. Freeze's Tragic Origin Gets Rebuilt",
                  category: "Story Arc Deep Dives",
                  date: "June 12, 2026",
                  excerpt: "Batman: Snow retells Mr. Freeze's early collision with the Dark Knight by framing Victor Fries as both monster and grieving husband, giving Gotham one of its coldest villains a sharper human ache beneath the ice."
                },
                {
                  id: "batman-three-jokers-identity-mystery",
                  title: "Batman: Three Jokers Explained — Gotham's Oldest Nightmare Splits in Three",
                  category: "Story Arc Deep Dives",
                  date: "June 11, 2026",
                  excerpt: "Batman: Three Jokers turns the Clown Prince of Crime into a mystery about trauma, repetition, and whether Gotham's worst wound is one monster, three masks, or the endless story Batman and Joker keep choosing to continue."
                },
                {
                  id: "batman-the-wrath-anti-batman-gotham",
                  title: "Batman: The Wrath Explained — Gotham's Anti-Batman in Uniform",
                  category: "Villain Spotlights",
                  date: "June 11, 2026",
                  excerpt: "Batman: The Wrath turns Bruce Wayne's origin inside out, introducing a police-hating vigilante whose childhood tragedy creates a lethal mirror image of the Dark Knight and forces Gotham to ask what separates justice from revenge."
                },
                {
                  id: "batman-son-of-the-demon-talia-ras-future",
                  title: "Batman: Son of the Demon Explained — Talia, Ra’s al Ghul, and a Dangerous Future",
                  category: "Story Arc Deep Dives",
                  date: "June 11, 2026",
                  excerpt: "Son of the Demon pushes Batman into uneasy alliance with Ra’s al Ghul and Talia, blending romance, succession, and global stakes into a story that later echoes through Damian Wayne and the Dark Knight’s complicated family legacy."
                },
                {
                  id: "batman-year-two-reaper-gotham-vigilante",
                  title: "Batman: Year Two Explained — The Reaper Tests Gotham's New Vigilante",
                  category: "Story Arc Deep Dives",
                  date: "June 10, 2026",
                  excerpt: "Batman: Year Two pits Bruce Wayne against the Reaper, an older and deadlier Gotham vigilante, forcing the young Dark Knight to confront guns, vengeance, Joe Chill, and the line that separates Batman from the monsters he hunts."
                },
                {
                  id: "batman-and-the-mad-monk-early-gotham",
                  title: "Batman and the Mad Monk Explained — Gothic Horror in the Dark Knight's Early Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 10, 2026",
                  excerpt: "Batman and the Mad Monk sends an early Bruce Wayne into occult-flavored crime, vampire theatrics, and Gotham's old-world nightmares, showing how the Dark Knight learns to fight enemies who turn fear into faith."
                },
                {
                  id: "batman-terror-hugo-strange-scarecrow",
                  title: "Batman: Terror Explained — Hugo Strange and Scarecrow Break the Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 9, 2026",
                  excerpt: "Batman: Terror returns Hugo Strange to the early Dark Knight's world and pairs him with Scarecrow, turning fear, obsession, and psychological warfare into a brutal test of Batman's identity before Gotham fully understands its new protector."
                },
                {
                  id: "batman-caped-crusader-post-crisis-gotham",
                  title: "Batman: The Caped Crusader Explained — Post-Crisis Gotham Finds Its Voice",
                  category: "Reading Guides",
                  date: "June 9, 2026",
                  excerpt: "Batman: The Caped Crusader collects the Dark Knight's early post-Crisis solo stories, showing Gotham sharpen into a moodier, more psychological city where crime, obsession, and Bruce Wayne's discipline begin to define the modern Batman rhythm."
                },
                {
                  id: "batman-going-sane-joker-without-batman",
                  title: "Batman: Going Sane Explained — The Joker Without Batman",
                  category: "Story Arc Deep Dives",
                  date: "June 9, 2026",
                  excerpt: "Going Sane imagines the Joker after he believes Batman is dead, turning the Clown Prince of Crime into Joseph Kerr and asking whether Gotham's greatest monster exists only because the Dark Knight gives him shape."
                },
                {
                  id: "batman-damned-supernatural-gotham-descent",
                  title: "Batman: Damned Explained — A Supernatural Descent Through Gotham",
                  category: "Story Arc Deep Dives",
                  date: "June 8, 2026",
                  excerpt: "Batman: Damned drags the Dark Knight through a feverish Gotham after the Joker's apparent death, using John Constantine, occult symbolism, and Bruce Wayne's buried guilt to turn a murder mystery into a haunted judgment of the Batman myth."
                },
                {
                  id: "batman-the-last-arkham-jeremiah-arkham",
                  title: "Batman: The Last Arkham Explained — Jeremiah Arkham Opens the Asylum",
                  category: "Story Arc Deep Dives",
                  date: "June 8, 2026",
                  excerpt: "The Last Arkham launches Shadow of the Bat by turning Arkham Asylum itself into the case, introducing Jeremiah Arkham and trapping Batman inside a rebuilt institution where madness, control, and Gotham's old sins blur together."
                },
                {
                  id: "batman-the-detective-global-mystery",
                  title: "Batman: The Detective Explained — Bruce Wayne Takes the Mission Global",
                  category: "Story Arc Deep Dives",
                  date: "June 7, 2026",
                  excerpt: "Batman: The Detective sends Bruce Wayne out of Gotham and into a European manhunt, using Equilibrium, old allies, and a trail of victims to test what Batman means when the symbol is forced to operate on unfamiliar ground."
                },
                {
                  id: "batman-war-of-jokes-and-riddles-gotham-gang-war",
                  title: "Batman: The War of Jokes and Riddles Explained — Gotham's Villains Choose Sides",
                  category: "Story Arc Deep Dives",
                  date: "June 7, 2026",
                  excerpt: "The War of Jokes and Riddles turns Batman's early Gotham into a battlefield between Joker and Riddler, revealing how the Dark Knight's code is tested when every villain in the city is forced to pick a side."
                },
                {
                  id: "batman-earth-one-flawed-dark-knight",
                  title: "Batman: Earth One Explained — A Rawer Gotham for a Flawed Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 6, 2026",
                  excerpt: "Batman: Earth One strips Gotham back to a harsher, messier origin where Bruce Wayne is not yet a legend, Alfred is not just a butler, and the city’s corruption forces the Dark Knight to earn every piece of the myth."
                },
                {
                  id: "batman-resurrection-of-ras-al-ghul-damian-legacy",
                  title: "Batman: The Resurrection of Ra’s al Ghul Explained — Legacy, Bloodlines, and Damian Wayne",
                  category: "Story Arc Deep Dives",
                  date: "June 6, 2026",
                  excerpt: "The Resurrection of Ra’s al Ghul turns Batman’s family into the battlefield, using Damian Wayne, Talia, and the League of Assassins to ask whether legacy is inherited, chosen, or escaped."
                },
                {
                  id: "batman-and-the-monster-men-hugo-strange",
                  title: "Batman and the Monster Men Explained — Hugo Strange Tests the Early Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 6, 2026",
                  excerpt: "Batman and the Monster Men pulls Hugo Strange, mad science, and creature horror into Batman's early career, showing Gotham's underworld growing stranger before the Dark Knight fully understands the city he has entered."
                },
                {
                  id: "batman-year-100-future-gotham",
                  title: "Batman: Year 100 Explained — Paul Pope’s Fugitive Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 5, 2026",
                  excerpt: "Batman: Year 100 throws the legend into a surveillance-state future where Gotham’s most wanted man may be Bruce Wayne, a successor, or something stranger — proving the Bat can survive even when history tries to erase him."
                },
                {
                  id: "batman-sword-of-azrael-jean-paul-valley",
                  title: "Batman: Sword of Azrael Explained — The Angel Who Would Become Batman",
                  category: "Story Arc Deep Dives",
                  date: "June 5, 2026",
                  excerpt: "Sword of Azrael introduces Jean-Paul Valley, the Order of St. Dumas, and a lethal religious programming that turns Batman's mission into a question of faith, identity, and whether a symbol can survive the wrong believer."
                },
                {
                  id: "batman-the-doom-that-came-to-gotham-lovecraftian-elseworld",
                  title: "Batman: The Doom That Came to Gotham Explained — A Lovecraftian Elseworld for the Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 5, 2026",
                  excerpt: "The Doom That Came to Gotham drops Bruce Wayne into a 1920s nightmare of occult bloodlines, buried monsters, and doomed inheritances, turning Batman's war on crime into a fight against cosmic horror."
                },
                {
                  id: "batman-shaman-myth-beneath-mask",
                  title: "Batman: Shaman Explained — The Myth Beneath the Mask",
                  category: "Story Arc Deep Dives",
                  date: "June 4, 2026",
                  excerpt: "Batman: Shaman follows Bruce Wayne from a near-death lesson in Alaska back to Gotham's first masked nights, showing how ritual, fear, and belief become part of the Dark Knight's foundation."
                },
                {
                  id: "batman-blind-justice-bruce-wayne-identity",
                  title: "Batman: Blind Justice Explained — Bruce Wayne, Identity, and the Man Behind the Mask",
                  category: "Story Arc Deep Dives",
                  date: "June 4, 2026",
                  excerpt: "Blind Justice pulls Bruce Wayne into a conspiracy of corporate power, military technology, and identity, asking what happens when Batman's mission and Wayne Enterprises' machinery collide."
                },
                {
                  id: "batman-the-black-casebook-strange-files",
                  title: "Batman: The Black Casebook Explained — The Strange Files That Rewired the Dark Knight",
                  category: "Reading Guides",
                  date: "June 4, 2026",
                  excerpt: "The Black Casebook collects Batman's strangest Silver Age cases and reveals why Grant Morrison treated forgotten aliens, hallucinations, and impossible mysteries as fuel for the modern Dark Knight."
                },
                {
                  id: "batman-night-cries-gotham-silence",
                  title: "Batman: Night Cries Explained — Gotham's Silence Is the Villain",
                  category: "Story Arc Deep Dives",
                  date: "June 3, 2026",
                  excerpt: "Night Cries turns Batman and Jim Gordon toward child abuse, making Gotham's most frightening enemy the silence that lets trauma hide in ordinary homes."
                },
                {
                  id: "batman-city-of-crime-gotham-body-horror",
                  title: "Batman: City of Crime Explained — Gotham's Body Horror Conspiracy",
                  category: "Story Arc Deep Dives",
                  date: "June 3, 2026",
                  excerpt: "City of Crime turns Gotham into a nightmare of missing children, corrupted families, and body-horror secrets, showing Batman trapped in a case where the city itself seems diseased."
                },
                {
                  id: "batman-gotham-by-gaslight-victorian-elseworlds",
                  title: "Batman: Gotham by Gaslight Explained — The Victorian Elseworld That Recast the Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "June 3, 2026",
                  excerpt: "Gotham by Gaslight moves Batman into a fogbound Victorian Gotham, proving the Dark Knight can survive outside continuity when the city, the mystery, and the myth still feel unmistakably his."
                },
                {
                  id: "batman-dark-knight-dark-city-riddler-ritual",
                  title: "Batman: Dark Knight, Dark City Explained — The Riddler Turns Gotham Into a Ritual",
                  category: "Story Arc Deep Dives",
                  date: "June 2, 2026",
                  excerpt: "Dark Knight, Dark City turns Gotham itself into an occult puzzle, using the Riddler's clues to push Batman through a ritual rooted in the city's oldest and ugliest secrets."
                },
                {
                  id: "batman-the-imposter-gotham-questions-mask",
                  title: "Batman: The Imposter Explained — Gotham Questions the Mask",
                  category: "Story Arc Deep Dives",
                  date: "June 2, 2026",
                  excerpt: "Batman: The Imposter strips the Dark Knight back to bruised realism, forcing Bruce Wayne to confront a copycat killer, a skeptical Gotham, and the possibility that his symbol can be weaponized against the city he is trying to save."
                },
                {
                  id: "batman-lovers-and-madmen-joker-origin",
                  title: "Batman: Lovers and Madmen Explained — A Joker Origin Built on Obsession",
                  category: "Story Arc Deep Dives",
                  date: "June 2, 2026",
                  excerpt: "Lovers and Madmen imagines one possible first collision between Batman and the Joker, turning Gotham's new vigilante myth into the spark that helps create a criminal obsessed with chaos, performance, and the Dark Knight himself."
                },
                {
                  id: "batman-gothic-mr-whisper-haunted-gotham",
                  title: "Batman: Gothic Explained — Mr. Whisper and Gotham’s Old Sins",
                  category: "Story Arc Deep Dives",
                  date: "June 1, 2026",
                  excerpt: "Batman: Gothic sends the Dark Knight into a haunted investigation where Mr. Whisper, forgotten schooldays, and Gotham's buried evil make the city feel cursed long before the next crime scene."
                },
                {
                  id: "batman-strange-apparitions-modern-gothic",
                  title: "Batman: Strange Apparitions Explained — The Dark Knight’s Modern Gothic Turn",
                  category: "Story Arc Deep Dives",
                  date: "June 1, 2026",
                  excerpt: "Strange Apparitions reshapes Bronze Age Batman with gothic romance, sharper rogues, and the sense that Gotham’s old monsters are learning how to haunt the modern Dark Knight."
                },
                {
                  id: "batman-venom-addiction-strength",
                  title: "Batman: Venom Explained — The Addiction Behind the Strength",
                  category: "Story Arc Deep Dives",
                  date: "June 1, 2026",
                  excerpt: "Batman: Venom turns Bruce Wayne's obsession with being strong enough into the enemy, showing how the Dark Knight can lose himself when willpower becomes hunger."
                },
                {
                  id: "batman-prey-hugo-strange-early-dark-knight",
                  title: "Batman: Prey Explained — Hugo Strange Hunts the Early Dark Knight",
                  category: "Story Arc Deep Dives",
                  date: "May 31, 2026",
                  excerpt: "Prey throws an early Batman against Hugo Strange, the GCPD, and a city that has not yet decided whether the Dark Knight is a savior, a symptom, or the next monster Gotham needs to cage."
                },
                {
                  id: "batman-broken-city-brian-azzarello-gotham-noir",
                  title: "Batman: Broken City Explained — Gotham Noir at Its Meanest",
                  category: "Story Arc Deep Dives",
                  date: "May 31, 2026",
                  excerpt: "Broken City sends Batman through a hardboiled Gotham murder case where every clue feels dirty, every ally is compromised, and Bruce Wayne's oldest wound keeps bleeding into the present."
                },
                {
                  id: "batman-lonely-place-of-dying-tim-drake-robin",
                  title: "Batman: A Lonely Place of Dying Explained — Tim Drake Proves Batman Needs Robin",
                  category: "Story Arc Deep Dives",
                  date: "May 31, 2026",
                  excerpt: "A Lonely Place of Dying introduces Tim Drake by turning Batman's grief after Jason Todd into a detective story about why the Dark Knight works better when Robin brings him back from the edge."
                },
                {
                  id: "batman-superheavy-jim-gordon-batman",
                  title: "Batman: Superheavy Explained — Jim Gordon Becomes Batman",
                  category: "Story Arc Deep Dives",
                  date: "May 30, 2026",
                  excerpt: "Superheavy puts Jim Gordon inside a mechanized Batman suit and asks whether Gotham can survive a corporate, state-sanctioned Dark Knight while Bruce Wayne tries to live without the cowl."
                },
                {
                  id: "batman-death-of-the-family-joker-bat-family",
                  title: "Batman: Death of the Family Explained — Joker Attacks the Bat-Family",
                  category: "Story Arc Deep Dives",
                  date: "May 30, 2026",
                  excerpt: "Death of the Family turns the Joker's return into a brutal test of Batman's closest bonds, asking whether the Bat-family makes Bruce stronger or gives his enemy sharper knives."
                },
                {
                  id: "batman-endgame-joker-last-laugh",
                  title: "Batman: Endgame Explained — The Joker Turns Gotham Into His Final Joke",
                  category: "Story Arc Deep Dives",
                  date: "May 30, 2026",
                  excerpt: "Endgame turns Batman and Joker's rivalry into a citywide apocalypse, forcing Gotham to face a clown prince who no longer wants attention — only an ending."
                },
                {
                  id: "batman-white-knight-joker-gotham-rewritten",
                  title: "Batman: White Knight Explained — When the Joker Tries to Save Gotham",
                  category: "Story Arc Deep Dives",
                  date: "May 29, 2026",
                  excerpt: "Batman: White Knight flips Gotham's usual argument inside out, asking what happens when a cured Joker weaponizes reform, public trust, and Batman's own excesses against him."
                },
                {
                  id: "batman-zero-year-gotham-origin",
                  title: "Batman: Zero Year Explained — Scott Snyder Rebuilds Gotham’s Origin",
                  category: "Story Arc Deep Dives",
                  date: "May 29, 2026",
                  excerpt: "Zero Year retells Batman's earliest war for Gotham as a bright, brutal survival epic, trading alleyway noir for hurricanes, riddles, and a city learning what the Bat can mean."
                },
                {
                  id: "batman-haunted-knight-halloween-gotham",
                  title: "Batman: Haunted Knight Explained — Halloween in Gotham Before The Long Halloween",
                  category: "Story Arc Deep Dives",
                  date: "May 29, 2026",
                  excerpt: "Haunted Knight collects three Halloween mysteries that turn Gotham into a seasonal mirror for Batman's fears, villains, and unfinished grief before The Long Halloween expands the idea."
                },
                {
                  id: "batman-return-of-bruce-wayne-time-lost",
                  title: "Batman: The Return of Bruce Wayne Explained — Bruce Wayne Fights His Way Back Through Time",
                  category: "Story Arc Deep Dives",
                  date: "May 28, 2026",
                  excerpt: "The Return of Bruce Wayne turns Batman's comeback into a time-spanning detective story, proving that Bruce Wayne survives by carrying Gotham's mission through every era he touches."
                },
                {
                  id: "batman-and-son-damian-wayne-arrives",
                  title: "Batman and Son Explained — Damian Wayne Arrives in Gotham",
                  category: "Story Arc Deep Dives",
                  date: "May 28, 2026",
                  excerpt: "Batman and Son drops Damian Wayne into Bruce's life as a challenge, a son, and a warning that Batman's mission can no longer be separated from family."
                },
                {
                  id: "batman-battle-for-the-cowl-gotham-without-bruce",
                  title: "Batman: Battle for the Cowl Explained — Gotham Without Bruce Wayne",
                  category: "Story Arc Deep Dives",
                  date: "May 28, 2026",
                  excerpt: "Battle for the Cowl turns Bruce Wayne's absence into a succession crisis, forcing Gotham's allies to decide whether Batman is a person, a weapon, or a responsibility."
                },
                {
                  id: "batman-reborn-dick-grayson-damian-wayne",
                  title: "Batman Reborn Explained — Dick Grayson, Damian Wayne, and a Brighter Gotham",
                  category: "Story Arc Deep Dives",
                  date: "May 27, 2026",
                  excerpt: "Batman Reborn gives Gotham a new Dynamic Duo, turning Dick Grayson and Damian Wayne into a Batman and Robin partnership built on trust, friction, and renewal."
                },
                {
                  id: "batman-eternal-gotham-war-on-every-front",
                  title: "Batman Eternal Explained — Gotham’s War on Every Front",
                  category: "Story Arc Deep Dives",
                  date: "May 27, 2026",
                  excerpt: "Batman Eternal turns Gotham into a year-long pressure cooker, attacking Batman through the GCPD, the underworld, Arkham, and the allies who keep his city standing."
                },
                {
                  id: "batman-heart-of-hush-thomas-elliot",
                  title: "Batman: Heart of Hush Explained — Thomas Elliot Cuts Too Close",
                  category: "Story Arc Deep Dives",
                  date: "May 26, 2026",
                  excerpt: "Heart of Hush turns Thomas Elliot's revenge into a surgical attack on Bruce Wayne's life, Catwoman's heart, and Batman's ability to protect the people closest to him."
                },
                {
                  id: "batman-face-the-face-harvey-dent",
                  title: "Batman: Face the Face Explained — Gotham Tests Harvey Dent Again",
                  category: "Story Arc Deep Dives",
                  date: "May 26, 2026",
                  excerpt: "Face the Face returns Bruce Wayne, Tim Drake, and Harvey Dent to Gotham after Infinite Crisis, asking whether Batman can rebuild trust without repeating old mistakes."
                },
                {
                  id: "batman-bruce-wayne-murderer-fugitive",
                  title: "Batman: Bruce Wayne Murderer? and Fugitive Explained — When the Mask Becomes the Alibi",
                  category: "Story Arc Deep Dives",
                  date: "May 26, 2026",
                  excerpt: "Bruce Wayne: Murderer? and Fugitive put Batman's secret identity on trial, forcing Gotham's hero to ask whether Bruce is a disguise, a liability, or the part of him worth saving."
                },
                {
                  id: "batman-officer-down-gordon-shot",
                  title: "Batman: Officer Down Explained — When Gotham Loses Jim Gordon",
                  category: "Story Arc Deep Dives",
                  date: "May 25, 2026",
                  excerpt: "Officer Down turns a sniper's bullet into a citywide crisis, forcing Batman, the GCPD, and Gotham's vigilantes to face what Jim Gordon really holds together."
                },
                {
                  id: "batman-legacy-ras-al-ghul-clench",
                  title: "Batman: Legacy Explained — Ra's al Ghul Turns Gotham's Plague Into a Weapon",
                  category: "Story Arc Deep Dives",
                  date: "May 25, 2026",
                  excerpt: "Legacy brings the Clench back with Ra's al Ghul behind the threat, expanding Contagion's public-health nightmare into a global Batman-family race against extinction."
                },
                {
                  id: "batman-contagion-gotham-plague",
                  title: "Batman: Contagion Explained — Gotham Fights a Plague It Cannot Punch",
                  category: "Story Arc Deep Dives",
                  date: "May 24, 2026",
                  excerpt: "Contagion turns Gotham into an outbreak zone, forcing Batman, Robin, Nightwing, Oracle, and the city’s doctors to fight panic, plague, and institutional collapse at once."
                },
                {
                  id: "batman-cataclysm-earthquake-gotham",
                  title: "Batman: Cataclysm Explained — The Earthquake That Broke Gotham",
                  category: "Story Arc Deep Dives",
                  date: "May 23, 2026",
                  excerpt: "Cataclysm turns Gotham's ground against itself, setting up No Man's Land by asking what Batman can protect when the city physically falls apart."
                },
                {
                  id: "batman-war-games-gotham-chaos",
                  title: "Batman: War Games Explained — When Gotham Becomes the Battlefield",
                  category: "Story Arc Deep Dives",
                  date: "May 22, 2026",
                  excerpt: "War Games turns a failed contingency plan into a citywide gang war, testing Batman’s control, Spoiler’s courage, and the cost of treating Gotham like a chessboard."
                },
                {
                  id: "batman-gates-of-gotham-family-history",
                  title: "Batman: Gates of Gotham Explained — The City’s Founding Families Strike Back",
                  category: "Story Arc Deep Dives",
                  date: "May 21, 2026",
                  excerpt: "Scott Snyder, Kyle Higgins, and Trevor McCarthy turn Gotham’s bridges into a mystery about the city’s oldest families, buried sins, and why Batman can never patrol only the present."
                },
                {
                  id: "batman-the-cult-deacon-blackfire",
                  title: "Batman: The Cult Explained — Gotham Breaks the Bat Without Bane",
                  category: "Story Arc Deep Dives",
                  date: "May 20, 2026",
                  excerpt: "Jim Starlin and Bernie Wrightson trap Batman under Gotham with Deacon Blackfire, turning the city’s forgotten people into a brutal test of Bruce Wayne’s faith in his mission."
                },
                {
                  id: "batman-the-man-who-laughs-joker-first-case",
                  title: "Batman: The Man Who Laughs Explained — The Joker's First Case Still Cuts Deep",
                  category: "Story Arc Deep Dives",
                  date: "May 19, 2026",
                  excerpt: "Ed Brubaker and Doug Mahnke revisit Batman's first true Joker case as a grim bridge between Year One realism and Gotham's coming age of theatrical monsters."
                },
                {
                  id: "batman-under-the-red-hood-explained",
                  title: "Batman: Under the Red Hood Explained — Jason Todd Comes Home Angry",
                  category: "Story Arc Deep Dives",
                  date: "May 18, 2026",
                  excerpt: "Under the Red Hood turns Jason Todd's resurrection into Batman's ugliest family argument: what justice means when grief comes back wearing a mask."
                },
                {
                  id: "batman-death-in-the-family-jason-todd",
                  title: "Batman: A Death in the Family Explained — Jason Todd and Batman's Breaking Point",
                  category: "Story Arc Deep Dives",
                  date: "May 17, 2026",
                  excerpt: "Jason Todd's death became one of Batman's defining wounds, turning a brutal Joker story into a lasting argument about grief, responsibility, and the limits of the mission."
                },
                {
                  id: "batman-rip-black-glove-bruce-wayne",
                  title: "Batman R.I.P. Explained — Bruce Wayne vs. the Black Glove",
                  category: "Story Arc Deep Dives",
                  date: "May 16, 2026",
                  excerpt: "Grant Morrison turns Batman's mind into the battlefield, asking whether Bruce Wayne can survive when the Black Glove attacks the story he tells about himself."
                },
                {
                  id: "batman-killing-joke-joker-argument",
                  title: "Batman: The Killing Joke Explained — The Joker's Cruelest Argument",
                  category: "Story Arc Deep Dives",
                  date: "May 15, 2026",
                  excerpt: "Alan Moore and Brian Bolland's controversial one-shot endures because it frames the Joker as an argument Batman has to reject, not just a villain he has to stop."
                },
                {
                  id: "batman-dark-victory-robin-legacy",
                  title: "Batman: Dark Victory Explained — Gotham Learns to Live With Robin",
                  category: "Story Arc Deep Dives",
                  date: "May 14, 2026",
                  excerpt: "Dark Victory closes the Long Halloween era by turning Gotham's grief, gang war, and serial mystery into the moment Batman finally accepts a partner."
                },
                {
                  id: "batman-incorporated-bruce-wayne-global",
                  title: "Batman Incorporated Explained — Bruce Wayne Goes Global",
                  category: "Story Arc Deep Dives",
                  date: "May 13, 2026",
                  excerpt: "Bruce Wayne turns Batman into a worldwide mission, testing whether the symbol can grow beyond Gotham without losing its soul."
                },
                {
                  id: "black-mirror-dick-grayson-gotham-horror",
                  title: "Batman: The Black Mirror Explained — Dick Grayson and Gotham Horror",
                  category: "Story Arc Deep Dives",
                  date: "May 12, 2026",
                  excerpt: "Scott Snyder, Jock, and Francesco Francavilla turn Dick Grayson’s Batman era into a cold Gotham nightmare about legacy, identity, and the city staring back."
                },
                {
                  id: "gotham-central-street-level-gotham",
                  title: "Gotham Central Explained — Batman From the Street Level",
                  category: "Reading Guides",
                  date: "May 11, 2026",
                  excerpt: "Gotham Central turns the city’s cops into the leads, showing what Batman’s world looks like to the people cleaning up after legends."
                },
                {
                  id: "batman-ego-bruce-wayne-inner-monster",
                  title: "Batman: Ego Explained — The Monster Bruce Wayne Carries",
                  category: "Story Arc Deep Dives",
                  date: "May 10, 2026",
                  excerpt: "Darwyn Cooke turns Batman's war inward, showing Bruce Wayne arguing with the brutal symbol he created to survive Gotham."
                },
                {
                  id: "arkham-asylum-serious-house-guide",
                  title: "Arkham Asylum Explained: Gotham’s Most Dangerous Idea",
                  category: "Story Arc Deep Dives",
                  date: "May 8, 2026",
                  excerpt: "Arkham is more than a prison for Batman villains. It is Gotham’s haunted mirror, where cure, punishment, and obsession blur."
                },
                {
                  id: "hush-reading-guide-batman-villains",
                  title: "Batman: Hush Reading Guide — Why It Became a Modern Gateway Story",
                  category: "Reading Guides",
                  date: "May 7, 2026",
                  excerpt: "Jeph Loeb and Jim Lee built a glossy tour of Gotham's rogues, allies, and mysteries. Here's why Hush remains an easy on-ramp."
                },
                {
                  id: "no-mans-land-gotham-survival-guide",
                  title: "No Man's Land Explained: Gotham After the Earthquake",
                  category: "Story Arc Deep Dives",
                  date: "May 7, 2026",
                  excerpt: "When Gotham is cut off from the country, Batman has to rebuild more than streets. He has to rebuild the idea of Gotham itself."
                },
                {
                  id: "poison-ivy-pamela-isley-guide",
                  title: "A Beginner's Guide to Poison Ivy: Pamela Isley Beyond the Villain Label",
                  category: "Character Spotlights",
                  date: "May 7, 2026",
                  excerpt: "Poison Ivy works best when she is frightening, sympathetic, brilliant, and impossible to reduce to a simple monster."
                },
                {
                  id: "court-of-owls-modern-gotham",
                  title: "Why The Court of Owls Works So Well",
                  category: "Story Arc Deep Dives",
                  date: "May 1, 2026",
                  excerpt: "The Court of Owls attacks Batman's greatest certainty: that he truly knows Gotham City. Here's why the arc still feels so sharp."
                },
                {
                  id: "catwoman-selina-kyle-guide",
                  title: "A Beginner's Guide to Catwoman: Selina Kyle in Batman Comics",
                  category: "Character Spotlights",
                  date: "May 1, 2026",
                  excerpt: "Thief, ally, rival, romantic partner, antihero — Selina Kyle survives because she refuses to be only one thing."
                },
                {
                  id: "batman-vs-detective-comics-explained",
                  title: "Batman vs. Detective Comics: What Is the Difference?",
                  category: "Reading Guides",
                  date: "May 1, 2026",
                  excerpt: "Two core Batman titles, two slightly different lenses on Gotham. Here's how to choose where to read next."
                },
                {
                  id: "where-to-start-reading-batman",
                  title: "Where to Start Reading Batman: A Complete Guide for New Readers",
                  category: "Reading Guides",
                  date: "April 29, 2026",
                  excerpt: "Overwhelmed by 80+ years of Batman comics? Here's the definitive guide to starting your journey through Gotham City."
                },
                {
                  id: "joker-greatest-comic-stories",
                  title: "The Joker's 10 Greatest Comic Book Stories",
                  category: "Character Spotlights",
                  date: "April 29, 2026",
                  excerpt: "From his first appearance in 1940 to modern classics, we rank the Clown Prince of Crime's most essential comic book stories."
                },
                {
                  id: "batman-long-halloween-explained",
                  title: "Batman: The Long Halloween Explained — The Story That Defined Modern Gotham",
                  category: "Story Arc Deep Dives",
                  date: "April 29, 2026",
                  excerpt: "How Jeph Loeb and Tim Sale created the definitive Batman mystery and influenced Christopher Nolan's Dark Knight trilogy."
                },
                {
                  id: "why-year-one-still-matters",
                  title: "Why Batman: Year One Still Matters",
                  category: "Story Arc Deep Dives",
                  date: "April 30, 2026",
                  excerpt: "Frank Miller and David Mazzucchelli stripped Batman back to street-level essentials — and gave Gotham its modern foundation."
                },
                {
                  id: "robin-guide-dick-jason-tim-damian",
                  title: "A Beginner's Guide to Robin: Dick, Jason, Tim, and Damian",
                  category: "Character Spotlights",
                  date: "April 30, 2026",
                  excerpt: "Robin is not just Batman's sidekick. Each Robin changes what Batman means, from hope and grief to legacy and family."
                },
                {
                  id: "knightfall-reading-order-bane",
                  title: "Knightfall Reading Order: How Bane Broke the Bat",
                  category: "Reading Guides",
                  date: "April 30, 2026",
                  excerpt: "A clean path through one of the biggest Batman events of the 1990s, from Bane's rise to Bruce Wayne's return."
                },
                {
                  id: "batman-no-kill-rule-gotham-code",
                  title: "Batman's No-Kill Rule Explained — Why the Code Defines the Dark Knight",
                  category: "Character Spotlights",
                  date: "July 26, 2026",
                  excerpt: "Batman has had every reason and opportunity to kill. He never does. Exploring where the rule comes from, how it has been tested, and why it still matters."
                },
                {
                  id: "ras-al-ghul-batman-most-dangerous-adversary",
                  title: "Ra's al Ghul Explained — Batman's Most Dangerous Adversary",
                  category: "Character Spotlights",
                  date: "July 28, 2026",
                  excerpt: "Ra's al Ghul has been planning longer than any other Batman villain — centuries longer, if the Lazarus Pits are real. He is also the only one who has ever looked at Bruce Wayne and called him worthy."
                }
              ].map(function(post) {
                return (
                  <div
                    key={post.id}
                    style={{
                      background: C.surface,
                      border: "1px solid " + C.border,
                      borderRadius: 14,
                      padding: 18,
                      cursor: "pointer",
                      transition: "all .15s",
                    }}
                    onClick={function() {
                      window.location.href = '/blog/' + post.id + '.html';
                    }}
                    onMouseEnter={function(e) {
                      e.currentTarget.style.borderColor = C.gold;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={function(e) {
                      e.currentTarget.style.borderColor = C.border;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{
                        fontSize: 10,
                        color: C.gold,
                        fontFamily: "monospace",
                        letterSpacing: 1,
                        background: "rgba(212,160,23,.1)",
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}>
                        {post.category.toUpperCase()}
                      </span>
                      <span style={{ fontSize: 11, color: C.dim }}>{post.date}</span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8, lineHeight: 1.4 }}>
                      {post.title}
                    </div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                      {post.excerpt}
                    </div>
                    <div style={{ marginTop: 12, fontSize: 12, color: C.gold, fontWeight: 600 }}>
                      Read more →
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "Account" && (
          <div style={{ padding: "0 16px" }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>
                {currentUser ? currentUser.name : "Account"}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                {currentUser 
                  ? "Track your achievements, collection progress, and Gotham Vault journey."
                  : "Sign in to track your achievements and collection progress."}
              </div>
            </div>

            {!currentUser ? (
              <div style={{
                background: C.surface,
                border: "1px solid " + C.border,
                borderRadius: 14,
                padding: 40,
                textAlign: "center",
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🦇</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                  Join the Gotham Vault
                </div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                  Sign in to unlock achievements and track your collection.
                </div>
                <button
                  onClick={function() { setShowAuthModal(true); setAuthMode('login'); }}
                  style={{
                    background: "linear-gradient(135deg," + C.gold + ",#e8a000)",
                    border: "none",
                    borderRadius: 999,
                    padding: "12px 24px",
                    color: "#090909",
                    fontSize: 14,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Sign In / Register
                </button>
              </div>
            ) : (
              <>
                {/* Stats Overview */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 12,
                  marginBottom: 24,
                }}>
                  {[
                    { label: "Collection", value: collection.length, icon: "📚" },
                    { label: "Wishlist", value: wishlist.length, icon: "🔖" },
                    { label: "Achievements", value: (function() {
                      var unlocked = Object.keys(earnedAchievements || {}).filter(function(k) { return earnedAchievements[k]; }).length;
                      return unlocked + "/26";
                    })(), icon: "🏆" },
                    { label: "Member Since", value: new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), icon: "📅" },
                  ].map(function(stat) {
                    return (
                      <div key={stat.label} style={{
                        background: C.surface,
                        border: "1px solid " + C.border,
                        borderRadius: 12,
                        padding: 16,
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 4 }}>{stat.icon}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: C.gold }}>{stat.value}</div>
                        <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>{stat.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Achievements Section */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                    🏆 Achievements
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {[
                      { id: "first_login", name: "Welcome to Gotham", desc: "Signed into Gotham Vault for the first time", icon: "🦇", unlocked: !!earnedAchievements.first_login },
                      { id: "first_collection", name: "Collector", desc: "Added your first comic to your collection", icon: "📚", unlocked: !!earnedAchievements.first_collection },
                      { id: "first_wishlist", name: "Wishful Thinker", desc: "Added your first comic to your wishlist", icon: "🔖", unlocked: !!earnedAchievements.first_wishlist },
                      { id: "wishlist_to_collection", name: "From Want List to Vault", desc: "Moved a wishlisted comic into your collection", icon: "🎁", unlocked: !!earnedAchievements.wishlist_to_collection },
                      { id: "ten_collection", name: "Growing Collection", desc: "Have 10+ comics in your collection", icon: "📖", unlocked: !!earnedAchievements.ten_collection },
                      { id: "quarter_vault", name: "Quarter Vault", desc: "Have 25+ comics in your collection", icon: "🗄️", unlocked: !!earnedAchievements.quarter_vault },
                      { id: "fifty_collection", name: "Serious Collector", desc: "Have 50+ comics in your collection", icon: "🗃️", unlocked: !!earnedAchievements.fifty_collection },
                      { id: "hundred_collection", name: "Vault Keeper", desc: "Have 100+ comics in your collection", icon: "🏛️", unlocked: !!earnedAchievements.hundred_collection },
                      { id: "five_hundred_collection", name: "Legend of Gotham", desc: "Have 500+ comics in your collection", icon: "👑", unlocked: !!earnedAchievements.five_hundred_collection },
                      { id: "arc_explorer", name: "Arc Explorer", desc: "Viewed 5+ different story arcs", icon: "🎯", unlocked: !!earnedAchievements.arc_explorer },
                      { id: "arc_master", name: "Arc Master", desc: "Viewed 20+ different story arcs", icon: "🗺️", unlocked: !!earnedAchievements.arc_master },
                      { id: "character_buff", name: "Character Buff", desc: "Viewed 10+ different character pages", icon: "🎭", unlocked: !!earnedAchievements.character_buff },
                      { id: "character_scholar", name: "Gotham Scholar", desc: "Viewed 50+ different character pages", icon: "🎓", unlocked: !!earnedAchievements.character_scholar },
                      { id: "early_bird", name: "Early Bird", desc: "Used Gotham Vault between 5-9 AM", icon: "🌅", unlocked: !!earnedAchievements.early_bird },
                      { id: "night_owl", name: "Night Owl", desc: "Used Gotham Vault between 10 PM-4 AM", icon: "🌙", unlocked: !!earnedAchievements.night_owl },
                      { id: "power_user", name: "Power User", desc: "Added 25+ comics in a single day", icon: "⚡", unlocked: !!earnedAchievements.power_user },
                      { id: "dedicated_fan", name: "Dedicated Fan", desc: "Visited Gotham Vault 7 days in a row", icon: "📅", unlocked: !!earnedAchievements.dedicated_fan },
                      { id: "bat_family", name: "Bat-Family", desc: "Collected comics featuring 5+ different Bat-family members", icon: "🦇", unlocked: !!earnedAchievements.bat_family },
                      { id: "rogues_gallery", name: "Rogues Gallery", desc: "Collected comics featuring 10+ different villains", icon: "🃏", unlocked: !!earnedAchievements.rogues_gallery },
                      { id: "golden_age", name: "Golden Age", desc: "Collected a comic from the 1940s", icon: "📜", unlocked: !!earnedAchievements.golden_age },
                      { id: "modern_era", name: "Modern Era", desc: "Collected a comic from 2011 or later", icon: "📱", unlocked: !!earnedAchievements.modern_era },
                      { id: "completionist", name: "Completionist", desc: "Collected an entire story arc", icon: "✅", unlocked: !!earnedAchievements.completionist },
                      { id: "detective", name: "World's Greatest Detective", desc: "Used the search feature 50+ times", icon: "🔍", unlocked: !!earnedAchievements.detective },
                      { id: "first_blood", name: "First Blood", desc: "Added Year One to your collection", icon: "🩸", unlocked: !!earnedAchievements.first_blood },
                      { id: "joker_fav", name: "Why So Serious?", desc: "Added 5+ Joker comics to your collection", icon: "😈", unlocked: !!earnedAchievements.joker_fav },
                      { id: "dark_knight", name: "The Dark Knight", desc: "Added 20+ Batman comics to your collection", icon: "🦇", unlocked: !!earnedAchievements.dark_knight },
                    ].map(function(achievement) {
                      return (
                        <div key={achievement.id} style={{
                          background: achievement.unlocked ? "rgba(212,160,23,.1)" : C.surface,
                          border: "1px solid " + (achievement.unlocked ? C.gold : C.border),
                          borderRadius: 12,
                          padding: 14,
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          opacity: achievement.unlocked ? 1 : 0.6,
                        }}>
                          <div style={{ fontSize: 28 }}>{achievement.icon}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: achievement.unlocked ? C.gold : C.text }}>
                              {achievement.name}
                              {achievement.unlocked && <span style={{ marginLeft: 8 }}>✓</span>}
                            </div>
                            <div style={{ fontSize: 12, color: C.muted }}>{achievement.desc}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                    ⚡ Quick Actions
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <button
                      onClick={function() { setTab("Collection"); }}
                      style={{
                        background: C.surface,
                        border: "1px solid " + C.border,
                        borderRadius: 8,
                        padding: "10px 16px",
                        color: C.text,
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      📚 View Collection
                    </button>
                    <button
                      onClick={openBrowseAll}
                      style={{
                        background: C.surface,
                        border: "1px solid " + C.border,
                        borderRadius: 8,
                        padding: "10px 16px",
                        color: C.text,
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      🔍 Browse Comics
                    </button>
                    <button
                      onClick={function() { 
                        setCurrentUser(null);
                        setAuthToken(null);
                        setAccountDataLoaded(false);
                        storageRemove('gv_auth_token');
                      }}
                      style={{
                        background: "rgba(220,53,69,.1)",
                        border: "1px solid rgba(220,53,69,.3)",
                        borderRadius: 8,
                        padding: "10px 16px",
                        color: "#ff6b6b",
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      🚪 Sign Out
                    </button>
                  </div>
                </div>

                {/* Bottom buffer for mobile scrolling */}
                <div style={{ height: 40 }} />
              </>
            )}
            {renderBugReportCard("Account")}
          </div>
        )}

        {tab === "Admin" && <AdminAnalyticsPage />}

      {selected && (
        <DetailSheet
          item={selected}
          type={selType}
          onClose={function () {
            setSelected(null);
            // Go back in history if we pushed a state for this modal
            if (typeof window !== 'undefined' && window.history.state && window.history.state.modal === 'comic') {
              window.history.back();
            }
          }}
          wishlist={wishlist}
          onWishlist={toggleWishlist}
          collection={collection}
          onOwn={toggleCollection}
          onSelectComic={function (c) {
            setSelected(c);
            setSelType("comic");
          }}
          onArcClick={function (arcName) {
            // Close the detail sheet, switch to Story Arcs tab,
            // and search for the arc by name
            setSelected(null);
            setSelectedArc(null);
            setArcIssues([]);
            setTab("Story Arcs");
            setArcSearch(arcName);
            clearTimeout(arcDebounceRef.current);
            searchStoryArcs(arcName);
          }}
        />
      )}

      {/* About Modal */}
      {showAbout && (
        <div
          onClick={function () {
            setShowAbout(false);
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={function (e) {
              e.stopPropagation();
            }}
            style={{
              width: "100%",
              maxWidth: 400,
              background: "linear-gradient(180deg,#161630," + C.bg + ")",
              border: "1px solid " + C.borderGold,
              borderRadius: 20,
              padding: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div style={{ color: C.gold }}>
                <BatLogo size={28} />
              </div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: "1px",
                }}
              >
                ABOUT GOTHAM VAULT
              </div>
            </div>
            <div
              style={{
                fontSize: 13,
                color: C.text,
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              This is an unofficial fan project. Comic details are based on real
              DC Comics publications but may contain estimated dates,
              paraphrased descriptions, and placeholder cover images. All
              characters and stories are property of DC Comics.
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                marginBottom: 20,
                padding: "12px 16px",
                background: "rgba(255,255,255,0.03)",
                borderRadius: 10,
                border: "1px solid " + C.border,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.gold }}>Comics:</strong>{" "}
                {issues.length.toLocaleString()} issues
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.gold }}>Data:</strong> Curated fan
                database
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.gold }}>©</strong> DC Comics. Used
                under fair use.
              </div>
              <div style={{ color: C.gold, fontSize: 10 }}>
                ✦ New issues added regularly ✦
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: C.muted,
                marginBottom: 20,
                textAlign: "center",
                padding: "10px",
                background: "rgba(255,214,50,0.05)",
                borderRadius: 8,
                border: "1px dashed " + C.borderGold,
              }}
            >
              <strong style={{ color: C.gold }}>📱 Install App:</strong>
              <br />
              <span style={{ fontSize: 11 }}>
                iPhone: Share → Add to Home Screen
                <br />
                Android: Menu → Add to Home Screen
              </span>
            </div>
            <button
              onClick={function () {
                setShowAbout(false);
              }}
              style={{
                width: "100%",
                padding: 12,
                background: "linear-gradient(135deg," + C.gold + ",#e8a000)",
                border: "none",
                borderRadius: 10,
                color: "#000",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Account encouragement modal */}
      {accountPrompt && !authToken && !currentUser && (
        <div
          onClick={function () { setAccountPrompt(null); }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.82)",
            zIndex: 99,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={function (e) { e.stopPropagation(); }}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "linear-gradient(180deg,#181834," + C.bg + ")",
              border: "1px solid " + C.borderGold,
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 24px 80px rgba(0,0,0,.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ color: C.gold }}><BatLogo size={30} /></div>
              <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "1px" }}>
                SAVE YOUR BAT-ARCHIVE
              </div>
            </div>
            <div style={{ color: C.text, fontSize: 15, lineHeight: 1.5, marginBottom: 10 }}>
              Nice — that comic was {accountPrompt === "collection" ? "added to your collection" : "saved to your wishlist"}.
            </div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.55, marginBottom: 20 }}>
              Create a free Gotham Vault account to keep your wishlist and collection backed up across devices.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                onClick={function () {
                  setAccountPrompt(null);
                  setAuthMode('signup');
                  setShowAuthModal(true);
                }}
                style={{
                  flex: "1 1 170px",
                  background: C.gold,
                  color: "#0a0a14",
                  border: "none",
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Create Free Account
              </button>
              <button
                onClick={function () { setAccountPrompt(null); }}
                style={{
                  flex: "1 1 120px",
                  background: "rgba(255,255,255,0.06)",
                  color: C.text,
                  border: "1px solid " + C.border,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div
          onClick={function () { setShowAuthModal(false); }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={function (e) { e.stopPropagation(); }}
            style={{
              width: "100%",
              maxWidth: 400,
              background: "linear-gradient(180deg,#161630," + C.bg + ")",
              border: "1px solid " + C.borderGold,
              borderRadius: 20,
              padding: 24,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div style={{ color: C.gold }}><BatLogo size={28} /></div>
              <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "1px" }}>
                {authMode === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
              </div>
            </div>
            
            <AuthForm 
              mode={authMode} 
              onSuccess={function(token, user) {
                setAuthToken(token);
                setCurrentUser(user);
                if (user && user.data) {
                  var serverWishlist = Array.isArray(user.data.wishlist) ? user.data.wishlist : [];
                  var serverCollection = Array.isArray(user.data.collection) ? user.data.collection : [];
                  var mergedWishlist = Array.from(new Set(serverWishlist.concat(wishlist)));
                  var mergedCollection = Array.from(new Set(serverCollection.concat(collection)));
                  var mergedAchievements = Object.assign({}, user.data.achievements || {}, earnedAchievements || {});
                  setWishlist(mergedWishlist);
                  setCollection(mergedCollection);
                  setEarnedAchievements(mergedAchievements);
                  try {
                    storageSet("gv_wishlist", JSON.stringify(mergedWishlist));
                    storageSet("gv_collection", JSON.stringify(mergedCollection));
                    storageSet("gv_achievements", JSON.stringify(mergedAchievements));
                  } catch (e) {}
                }
                setAccountDataLoaded(true);
                setShowAuthModal(false);
              }}
              onSwitchMode={function() { setAuthMode(authMode === 'login' ? 'signup' : 'login'); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
